import { normalizePhoneNumber } from '@benchmark/phone';
import { PATIENT_SUMMARY_EXTENSION_URL, GP_ROLE_TYPE_EXTENSION, GP_ROLE_TYPES, CODE_SYSTEM as CODE_SYSTEM$1, getSpecialtyDisplay as getSpecialtyDisplay$1, CLINICAL_IMPRESSION_TYPES, STANDARD_QUESTIONNAIRES, getObservationCoding as getObservationCoding$1, LEGACY_OBSERVATION_LOINC_CODES, QUESTIONNAIRE_URLS as QUESTIONNAIRE_URLS$1, ENCOUNTER_DRAFT_FORM_STATE_EXTENSION_URL, getQuestionnaireItems, LIKERT_FREQUENCY_OPTIONS, VALUE_SET, ENCOUNTER_SCRATCH_EXTENSION_URL, ENCOUNTER_SECTION_SCRATCH_EXTENSION_URL, PATIENT_TAG_MAPPINGS, getQuestionnaireDefinition, CLINICAL_IMPRESSION_CODE_MAPPINGS, buildCommunicationTypeCategory, ICD10_CM_SYSTEM, PROFILE_URLS, getEncounterTypeDisplay, SERVICE_CATEGORY_OPTIONS, SERVICE_SPECIALTY_TREATMENT_CONDITION_OPTIONS, SERVICE_SPECIALTY_TREATMENT_MODALITY_OPTIONS, SERVICE_ELIGIBILITY_AGE_GROUP_OPTIONS, SERVICE_ELIGIBILITY_GENDER_OPTIONS, SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS, SERVICE_CHARACTERISTIC_ACCESS_OPTIONS, SERVICE_CHARACTERISTIC_SCHEDULING_OPTIONS, SERVICE_PROVISION_OPTIONS, FLAG_CATEGORY_SYSTEM, getFlagCategoryDisplay, getFlagCodingByCode, GOAL_CATEGORY_SYSTEM, getGoalCategoryDisplay, isValidGASScore, SURVEY_OBSERVATION_CATEGORY, GAS_ASSESSMENT_CODE, GAS_CODE_SYSTEM, getGASScoreDescription, getGASScoreDisplay, HEALTHCARE_SERVICE_CODESYSTEMS, SERVICE_TYPE_OPTIONS, getCoverageAreaLocationName, SERVICE_CHARACTERISTIC_PARTNERSHIP_OPTIONS, isCoverageAreaStateCode, SUPPORTED_COVERAGE_AREA_STATE_CODES, getCoverageAreaStateDisplay, INDIVIDUAL_PRONOUNS_EXTENSION_URL, MIRAH_INSTITUTE_ID_SYSTEM, MIRAH_PATIENT_ID_SYSTEM, OBSERVATION_TYPES as OBSERVATION_TYPES$1, getOrganizationTypeSystem, getOrganizationTypeDisplay, ORGANIZATION_TIER_SYSTEM, COCM_CONSENT_EXTENSION_URL, PATIENT_ENGAGEMENT_STATUS_OPTIONS, PATIENT_ENGAGEMENT_STATUS_SYSTEM, LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM, LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS, LANGUAGES, PRACTITIONER_SPECIALTY_OPTIONS, getReferralModeLabel, getAllCarePlanActivityOptions, CARE_PLAN_ACTIVITY_SYSTEM, resolveActivityCode, getCarePlanActivityDisplay, TRIAGE_ACTIVITY_CODES, TERMINATING_ENGAGEMENT_STATUSES, getTaskTypeConfig, CUSTOM_TASK_TYPE, isV2CarePlanActivityTaskCode, resolveActivityDisplay, getEstimatedDurationForTaskType } from './constants.mjs';
import { formatHumanName, deepEquals } from '@medplum/core';
import { kebabCase } from 'change-case';
import { toPatientId, toOrganizationId, toPractitionerId } from './types.mjs';
import { localCalendarDateTodayISO, toE164US as toE164US$1 } from './utils.mjs';

/**
 * Field-level FHIR CodeableConcept, Identifier, and Period builders. Pure, no IO.
 * @agentic @benchmark/core
 */ /**
 * Creates FHIR CodeableConcept element.
 * @param system - Code system URL
 * @param code - Code value
 * @param display - Display name for the code
 * @param text - Optional text (defaults to display)
 * @agentic @benchmark/core
 */ const createCodeableConcept = (system, code, display, text)=>({
        coding: [
            {
                system,
                code,
                display
            }
        ],
        text: text ?? display
    });
/**
 * Creates FHIR Identifier element.
 * @agentic @benchmark/core
 */ const createIdentifier = (system, value, use)=>({
        use,
        system,
        value
    });
/**
 * Creates FHIR Period element.
 * @agentic @benchmark/core
 */ const createPeriod = (start, end)=>({
        start,
        ...end && {
            end
        }
    });

/**
 * Creates FHIR HumanName element.
 * @agentic @benchmark/core
 */ const createHumanName = (given, family, use)=>({
        use,
        family,
        given,
        text: `${given.join(" ")} ${family}`
    });
/**
 * Creates FHIR ContactPoint element.
 * @agentic @benchmark/core
 */ const createContactPoint = (system, value, use)=>({
        system,
        value,
        ...use && {
            use
        }
    });
/**
 * Creates FHIR Address element.
 * @agentic @benchmark/core
 */ const createAddress = (line, city, state, postalCode, country, use)=>({
        use,
        line,
        city,
        state,
        postalCode,
        country,
        text: `${line.join(", ")}, ${city}, ${state} ${postalCode}${country ? `, ${country}` : ""}`
    });
/**
 * Normalizes phone number to E.164 US format.
 * @agentic @benchmark/phone
 */ function toE164US(phone) {
    if (!phone) {
        return "";
    }
    // Remove common formatting characters
    const base = phone.replace(/[\s\-().]/g, "");
    // Normalize to E.164 US format
    return normalizePhoneNumber(base, "US");
}

/**
 * FHIR Helper Functions
 *
 * Pure functions for constructing common FHIR data structures.
 * These are shared utilities used by builders across apps.
 */ /**
 * Create a FHIR Reference with required display value
 *
 * @param resourceType - The FHIR resource type
 * @param id - The resource ID (can be a real ID or urn:uuid for bundles)
 * @param display - Human-readable display name (required)
 * @returns Reference with display
 */ const buildReference = (resourceType, id, display)=>{
    if (!display || display.trim().length === 0) {
        throw new Error(`${resourceType} reference requires display name. ID: ${id}`);
    }
    // Support both real IDs and urn:uuid references for transaction bundles
    const reference = id.startsWith("urn:uuid:") ? id : `${resourceType}/${id}`;
    return {
        reference,
        display: display.trim()
    };
};

/**
 * Generic reference creator that requires display value
 * Re-exported from @benchmark/core/builders for backward compatibility.
 * Supports both real IDs and urn:uuid references for transaction bundles.
 */ const buildReferenceWithDisplay = (resourceType, id, display)=>buildReference(resourceType, id, display);
/**
 * Build Patient reference with display (typed helper using branded PatientId)
 */ const buildPatientReference = (patientId, display)=>buildReference("Patient", patientId, display);
/**
 * Builds Practitioner reference with display.
 * @agentic @benchmark/core
 */ const buildPractitionerReference = (practitionerId, display)=>buildReference("Practitioner", practitionerId, display);
/**
 * Builds Organization reference with display.
 * @agentic @benchmark/core
 */ const buildOrganizationReference = (organizationId, display)=>buildReference("Organization", organizationId, display);
/**
 * Builds HealthcareService reference with display.
 * @agentic @benchmark/core
 */ const buildHealthcareServiceReference = (serviceId, display)=>buildReference("HealthcareService", serviceId, display);
/**
 * Builds PractitionerRole reference with display.
 * @agentic @benchmark/core
 */ const buildPractitionerRoleReference = (practitionerRoleId, display)=>buildReference("PractitionerRole", practitionerRoleId, display);
/**
 * Builds Location reference with display.
 * @agentic @benchmark/core
 */ const buildLocationReference = (locationId, display)=>buildReference("Location", locationId, display);

/**
 * Check if address has meaningful data
 * Internal helper function
 */ const hasAddressData = (address)=>Boolean(address.text || address.line && address.line.length > 0 && address.line[0] || address.city || address.state || address.postalCode || address.country);
/**
 * Get display name for a patient.
 * When a preferred name (FHIR nickname) exists, formats as "Legal Name (Preferred Name)".
 */ const getPatientDisplayName = (patient)=>{
    const officialName = patient.name?.find((n)=>n.use === "official") || patient.name?.[0];
    if (!officialName) {
        return `Patient ${patient.id}`;
    }
    const parts = [];
    if (officialName.given?.length) {
        parts.push(...officialName.given);
    }
    if (officialName.family) {
        parts.push(officialName.family);
    }
    if (parts.length === 0) {
        return `Patient ${patient.id}`;
    }
    const legalName = parts.join(" ");
    const nickname = patient.name?.find((n)=>n.use === "nickname" && n !== officialName);
    const preferredName = nickname?.given?.[0];
    if (preferredName) {
        return `${legalName} (${preferredName})`;
    }
    return legalName;
};
/**
 * Returns true when a gp-role-type code represents the Benchmark primary advocate.
 * Supports both current and legacy value codes.
 */ const isBenchmarkPrimaryAdvocateRoleCode = (valueCode)=>valueCode === GP_ROLE_TYPES.BENCHMARK_PRIMARY_ADVOCATE || valueCode === GP_ROLE_TYPES.BENCHMARK_LCSW;
/**
 * Get Benchmark primary advocate reference from patient's generalPractitioner array.
 * Function name preserved for backward compatibility.
 */ const getPatientBenchmarkLCSW = (patient)=>{
    return patient.generalPractitioner?.find((ref)=>ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION && isBenchmarkPrimaryAdvocateRoleCode(ext.valueCode))) ?? null;
};
/**
 * Get MRN (Medical Record Number) from patient identifiers
 * Returns null if no MRN identifier exists
 */ const getPatientMRN = (patient)=>{
    if (!patient.identifier || patient.identifier.length === 0) {
        return null;
    }
    const mrnIdentifier = patient.identifier.find((identifier)=>identifier.type?.coding?.some((coding)=>coding.code === "MR"));
    if (mrnIdentifier?.value) {
        return {
            system: mrnIdentifier.system || "",
            value: mrnIdentifier.value
        };
    }
    return null;
};
/**
 * Get active phone number from patient telecom array
 * Returns undefined if no active phone exists
 */ const getActivePatientPhone = (patient)=>patient.telecom?.find((telecom)=>telecom.system === "phone" && !telecom.period?.end)?.value;
/**
 * Get active address from patient address array
 * Filters out addresses with 'old' use or ended periods
 * Returns undefined if no active address exists or address has no data
 */ const getActivePatientAddress = (patient)=>{
    const address = patient.address?.find((candidate)=>candidate.use !== "old" && (!candidate.period || !candidate.period.end));
    return address && hasAddressData(address) ? address : undefined;
};
/**
 * Get active telecom entries from patient telecom array
 * Filters out telecom entries with ended periods
 */ const getActivePatientTelecom = (patient)=>patient.telecom?.filter((telecom)=>!telecom.period?.end) || [];
/**
 * Extracts patient summary from Patient resource extension.
 * @agentic @benchmark/core
 */ const getPatientSummary = (patient)=>{
    const summaryExtension = patient.extension?.find((ext)=>ext.url === PATIENT_SUMMARY_EXTENSION_URL);
    if (!summaryExtension?.extension) {
        return undefined;
    }
    const textExt = summaryExtension.extension.find((ext)=>ext.url === "text");
    const lastUpdatedExt = summaryExtension.extension.find((ext)=>ext.url === "lastUpdated");
    const authorExt = summaryExtension.extension.find((ext)=>ext.url === "author");
    const generatedByExt = summaryExtension.extension.find((ext)=>ext.url === "generatedBy");
    const modelUsedExt = summaryExtension.extension.find((ext)=>ext.url === "modelUsed");
    const promptVersionExt = summaryExtension.extension.find((ext)=>ext.url === "promptVersion");
    const text = textExt?.valueString;
    const lastUpdated = lastUpdatedExt?.valueDateTime;
    const authorReference = authorExt?.valueReference?.reference;
    const authorDisplay = authorExt?.valueReference?.display;
    const generatedBy = generatedByExt?.valueCode ?? "manual";
    const modelUsed = modelUsedExt?.valueString;
    const promptVersion = promptVersionExt?.valueString;
    if (!text || !lastUpdated || !authorReference || !authorDisplay) {
        return undefined;
    }
    return {
        text,
        lastUpdated,
        authorReference,
        authorDisplay,
        generatedBy,
        modelUsed,
        promptVersion
    };
};
/**
 * Builds patient summary extension for FHIR Patient resource.
 * @agentic @benchmark/core
 */ const buildPatientSummaryExtension = (options)=>{
    const { text, authorReference, generatedBy, modelUsed, promptVersion } = options;
    if (!authorReference.display) {
        throw new Error("Author reference must include display name");
    }
    const extensionItems = [
        {
            url: "text",
            valueString: text
        },
        {
            url: "lastUpdated",
            valueDateTime: new Date().toISOString()
        },
        {
            url: "author",
            valueReference: authorReference
        },
        {
            url: "generatedBy",
            valueCode: generatedBy
        }
    ];
    if (modelUsed) {
        extensionItems.push({
            url: "modelUsed",
            valueString: modelUsed
        });
    }
    if (promptVersion) {
        extensionItems.push({
            url: "promptVersion",
            valueString: promptVersion
        });
    }
    return {
        url: PATIENT_SUMMARY_EXTENSION_URL,
        extension: extensionItems
    };
};
/**
 * Updates or adds patient summary extension; returns new extensions array (immutable).
 * @agentic @benchmark/core
 */ const updatePatientSummaryExtension = (existingExtensions, options)=>{
    const newSummaryExtension = buildPatientSummaryExtension(options);
    if (!existingExtensions) {
        return [
            newSummaryExtension
        ];
    }
    const otherExtensions = existingExtensions.filter((ext)=>ext.url !== PATIENT_SUMMARY_EXTENSION_URL);
    return [
        ...otherExtensions,
        newSummaryExtension
    ];
};
/**
 * Removes patient summary extension; returns new extensions array (immutable).
 * @agentic @benchmark/core
 */ const removePatientSummaryExtension = (existingExtensions)=>{
    if (!existingExtensions) {
        return undefined;
    }
    const filtered = existingExtensions.filter((ext)=>ext.url !== PATIENT_SUMMARY_EXTENSION_URL);
    return filtered.length > 0 ? filtered : undefined;
};

/**
 * Converts form data to FHIR Appointment resource. Pure mapping, no IO.
 * @param formData - Appointment form data
 * @param patient - Patient resource (for building references)
 * @param referrer - PractitionerRole resource (referrer)
 * @param existingAppointment - Existing appointment resource (for updates)
 * @returns Appointment domain model
 * @throws Error if performer is not provided
 * @agentic @benchmark/core
 */ function appointmentFromForm(formData, patient, referrer, existingAppointment) {
    if (!formData.performer) {
        throw new Error("Practitioner Role is required");
    }
    let appointment;
    if (existingAppointment) {
        appointment = {
            ...existingAppointment
        };
    } else {
        const now = new Date().toISOString();
        appointment = {
            resourceType: "Appointment",
            status: "proposed",
            participant: [],
            created: now
        };
    }
    // Update appointment fields from form data
    if (formData.start) {
        appointment.start = formData.start;
        // Default to 45-minute duration if no end time is specified due to FHIR spec/validation
        const startDate = new Date(formData.start);
        const endDate = new Date(startDate.getTime() + 45 * 60 * 1000); // Add 45 minutes
        appointment.end = endDate.toISOString();
    }
    if (formData.status) {
        appointment.status = formData.status;
    }
    if (formData.description) {
        appointment.description = formData.description;
    }
    if (formData.comment) {
        appointment.comment = formData.comment;
    }
    // Set specialty
    if (formData["appointment-specialty"]) {
        appointment.specialty = [
            {
                coding: [
                    {
                        system: CODE_SYSTEM$1.SNOMED,
                        code: formData["appointment-specialty"],
                        display: getSpecialtyDisplay$1(formData["appointment-specialty"])
                    }
                ]
            }
        ];
    }
    // Update participants based on form data
    const participants = [];
    // Add patient as participant
    participants.push({
        actor: buildReferenceWithDisplay("Patient", patient.id, getPatientDisplayName(patient)),
        status: "accepted"
    });
    // Add performer (primary practitioner) if provided
    if (formData.performer && formData.performerDisplayName) {
        participants.push({
            type: [
                {
                    coding: [
                        {
                            system: "http://terminology.hl7.org/CodeSystem/v3-ParticipationType",
                            code: "PPRF",
                            display: "primary performer"
                        }
                    ]
                }
            ],
            actor: buildReferenceWithDisplay("PractitionerRole", formData.performer, formData.performerDisplayName),
            status: "accepted",
            required: "required"
        });
    }
    // Add healthcare service if provided
    if (formData.provider) {
        if (!formData.providerDisplayName) {
            throw new Error("HealthcareService display name is required. Please ensure the service is selected from the dropdown.");
        }
        participants.push({
            type: [
                {
                    coding: [
                        {
                            system: "http://terminology.hl7.org/CodeSystem/v3-ParticipationType",
                            code: "SPRF",
                            display: "secondary performer"
                        }
                    ]
                }
            ],
            actor: buildReferenceWithDisplay("HealthcareService", formData.provider, formData.providerDisplayName),
            status: "accepted"
        });
    }
    // Add referrer as participant
    participants.push({
        type: [
            {
                coding: [
                    {
                        system: "http://terminology.hl7.org/CodeSystem/v3-ParticipationType",
                        code: "REF",
                        display: "referrer"
                    }
                ]
            }
        ],
        actor: buildReferenceWithDisplay("PractitionerRole", referrer.id, referrer.practitioner?.display || `PractitionerRole/${referrer.id}`),
        status: "accepted"
    });
    appointment.participant = participants;
    if (formData.basedOn && formData.basedOn.length > 0) {
        appointment.basedOn = formData.basedOn;
    }
    if (formData.supportingInformation && formData.supportingInformation.length > 0) {
        appointment.supportingInformation = formData.supportingInformation;
    }
    return appointment;
}

/**
 * Extracts the primary performer participant from an appointment using FHIR participation type or fallback.
 * @agentic @benchmark/core
 */ function findPrimaryPerformerParticipant(appointment, currentUserRoleId) {
    // Strategy 1: Find by FHIR participation type code "PPRF" (primary performer)
    let performerParticipant = appointment.participant?.find((p)=>{
        const typeCode = p.type?.[0]?.coding?.[0]?.code;
        return typeCode === "PPRF";
    });
    // Strategy 2: If no PPRF found, find by reference type but exclude the current user's role
    if (!performerParticipant) {
        const practitionerRoleParticipants = appointment.participant?.filter((p)=>(p.actor?.reference?.startsWith("PractitionerRole/") || p.actor?.reference?.startsWith("Practitioner/")) && // Exclude the current user's referrer role if provided
            (!currentUserRoleId || !p.actor?.reference?.endsWith(currentUserRoleId)) && // Also exclude patient participants
            !p.actor?.reference?.startsWith("Patient/"));
        // Take the first non-referrer practitioner/role participant
        performerParticipant = practitionerRoleParticipants?.[0];
    }
    return performerParticipant;
}

/**
 * Converts FHIR Appointment resource to form data. Pure mapping, no IO.
 * @param appointment - Appointment resource
 * @param options - Optional configuration for transformation
 * @returns Appointment form data
 * @agentic @benchmark/core
 */ function appointmentToForm(appointment, options = {}) {
    const { currentUserRoleId, resolvedPerformerRoleId, performerDisplayName } = options;
    const formData = {};
    // Find the primary performer participant
    const performerParticipant = findPrimaryPerformerParticipant(appointment, currentUserRoleId);
    if (performerParticipant?.actor?.reference) {
        const reference = performerParticipant.actor.reference;
        // Extract PractitionerRole ID (preferred)
        const roleMatch = reference.match(/PractitionerRole\/(.+)/);
        if (roleMatch) {
            formData.performer = roleMatch[1];
            formData.performerDisplayName = performerDisplayName || performerParticipant.actor?.display || "";
        } else {
            // Handle Practitioner reference - use resolved role ID if provided
            const practitionerMatch = reference.match(/Practitioner\/(.+)/);
            if (practitionerMatch && resolvedPerformerRoleId) {
                formData.performer = resolvedPerformerRoleId;
                formData.performerDisplayName = performerDisplayName || performerParticipant.actor?.display || "";
            }
        // If Practitioner reference but no resolved role ID, skip performer
        // (caller should resolve PractitionerRole before calling this adapter)
        }
    }
    // Find the healthcare service participant
    const healthcareServiceParticipant = appointment.participant?.find((p)=>{
        const typeCode = p.type?.[0]?.coding?.[0]?.code;
        return typeCode === "SPRF" || p.actor?.reference?.startsWith("HealthcareService/");
    });
    if (healthcareServiceParticipant?.actor?.reference) {
        const serviceMatch = healthcareServiceParticipant.actor.reference.match(/HealthcareService\/(.+)/);
        if (serviceMatch) {
            formData.provider = serviceMatch[1];
            formData.providerDisplayName = healthcareServiceParticipant.actor.display || "";
        }
    }
    // Extract appointment fields
    if (appointment.start) {
        formData.start = appointment.start;
    }
    if (appointment.status) {
        formData.status = appointment.status;
    }
    if (appointment.specialty?.[0]?.coding?.[0]?.code) {
        formData["appointment-specialty"] = appointment.specialty[0].coding[0].code;
    }
    if (appointment.description) {
        formData.description = appointment.description;
    }
    if (appointment.comment) {
        formData.comment = appointment.comment;
    }
    if (appointment.basedOn && appointment.basedOn.length > 0) {
        formData.basedOn = appointment.basedOn;
    }
    if (appointment.supportingInformation && appointment.supportingInformation.length > 0) {
        formData.supportingInformation = appointment.supportingInformation;
    }
    return formData;
}

/**
 * FHIR-related URLs used throughout the application
 */ // HL7 FHIR Base URLs
const HL7_TERMINOLOGY_BASE = "http://terminology.hl7.org";
// Organization Base URLs
const ORGANIZATION_BASE = "http://benchmarkhealth.com/fhir";
/**
 * Base URL for Benchmark Health custom FHIR extensions
 *
 * All custom StructureDefinition URLs should be derived from this base.
 * Use this constant to ensure consistency across the codebase.
 *
 * @example
 * ```ts
 * const myExtension = `${EXTENSION_BASE}/my-extension-name`;
 * // => "http://benchmarkhealth.com/fhir/StructureDefinition/my-extension-name"
 * ```
 */ const EXTENSION_BASE = `${ORGANIZATION_BASE}/StructureDefinition`;
// CodeSystem URLs
const CODE_SYSTEM = {
    V2_0131: `${HL7_TERMINOLOGY_BASE}/CodeSystem/v2-0131`,
    V3_ROLE_CODE: `${HL7_TERMINOLOGY_BASE}/CodeSystem/v3-RoleCode`};

/**
 * Current observation LOINC codes for assessment scores
 * Used for creating new Observation resources from questionnaire responses
 *
 * IMPORTANT: These codes should ONLY be used for Observation resources that contain
 * calculated total scores. For questionnaire definitions, use QUESTIONNAIRE_LOINC_CODES
 * from questionnaire.ts which contains the panel codes.
 *
 * Validation: ✅ All codes verified against official LOINC database (2025-01-08)
 * See: packages/medplum/LOINC_CODE_MAPPINGS.md for detailed mapping documentation
 */ const OBSERVATION_LOINC_CODES = {
    /** ASRS Adult ADHD Self-Report Scale Total Score - Organizational code (no official LOINC) */ ASRS_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/asrs-total-score",
    /** AUDIT-C Total Score - LOINC 75626-2 ✅ VALIDATED */ AUDIT_C_TOTAL_SCORE: "75626-2",
    /** C-SSRS Columbia Suicide Severity Rating Scale Risk Level - Organizational code (no official LOINC) */ C_SSRS_SEVERITY: "https://benchmarkhealth.org/fhir/CodeSystem/c-ssrs-severity",
    /** GAD-7 Total Score - LOINC 70274-6 ✅ VALIDATED */ GAD7_TOTAL_SCORE: "70274-6",
    /** GAD-2 Total Score - Organizational code (the legacy LOINC 89204-7 is deprecated; the adaptive short check uses an org code) */ GAD2_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/gad-2-total-score",
    /** UCLA-3 Three-Item Loneliness Scale Total Score - Organizational code (no official LOINC) */ UCLA3_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/ucla-3-total-score",
    /** WSAS Work and Social Adjustment Scale Total Score - Organizational code (no official LOINC) */ WSAS_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/wsas-total-score",
    /** OCI-4 Obsessive-Compulsive Inventory Total Score - Organizational code (no official LOINC) */ OCI_4_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/oci-4-total-score",
    /** PC-PTSD-5 Total Score - LOINC 102017-1 ✅ VALIDATED */ PC_PTSD_5_TOTAL_SCORE: "102017-1",
    /** PSC-17 Pediatric Symptom Checklist Total Score - Organizational code (no official LOINC) */ PSC_17_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/psc-17-total-score",
    /** PHQ-9 Total Score - LOINC 44261-6 ✅ VALIDATED */ PHQ9_TOTAL_SCORE: "44261-6",
    /** PHQ-9A (Adolescent) Total Score - LOINC 89204-2 ✅ VALIDATED */ PHQ9A_TOTAL_SCORE: "89204-2",
    /** PHQ-4 Total Score - LOINC 70272-0 ✅ VALIDATED */ PHQ4_TOTAL_SCORE: "70272-0",
    /** SBIRT-ED Eating Disorder Screening Score - Organizational code (no official LOINC) */ SBIRT_ED_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/sbirt-ed-score",
    /** TAPS-1 Substance Use Screening Score - Organizational code (no official LOINC) */ TAPS_1_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/taps-1-score",
    /** TAPS-2 Substance Use Assessment Score - Organizational code (no official LOINC) */ TAPS_2_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/taps-2-score",
    /** WPAI Work Productivity and Activity Impairment Score - Organizational code (no official LOINC) */ WPAI_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/wpai-shp-score",
    /** WHO-5 Well-Being Index Total Score - Organizational code (no official LOINC) */ WHO5_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/who-5-total-score",
    /** Health Leads Social Needs Screening Toolkit Score - Organizational code (no official LOINC) */ SDOH_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/sdoh-health-leads-score",
    /** Mirah Complexity composite score - Mirah-proprietary (no official LOINC) */ MIRAH_COMPLEXITY_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-complexity-score",
    /** Mirah Severity composite score - Mirah-proprietary (no official LOINC) */ MIRAH_SEVERITY_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-severity-score",
    /** NPS 0–10 likelihood score — organizational code (no official LOINC) */ NPS_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/nps-score"
};
/**
 * Current observation types for assessment scores
 * FHIR R4-compliant observation types from LOINC
 * http://hl7.org/fhir/ValueSet/observation-codes
 * These are TOTAL SCORE codes for questionnaire observations
 * This is the UNIFIED source that replaces the previous duplicated constants
 */ const OBSERVATION_TYPES = [
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/asrs-total-score",
        display: "ASRS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "asrs",
        description: "Adult ADHD Self-Report Scale",
        minValue: 0,
        maxValue: 24
    },
    {
        code: "75626-2",
        display: "AUDIT-C",
        system: "http://loinc.org",
        fieldKey: "audit-c",
        description: "Alcohol Use Disorders Identification Test - Consumption",
        minValue: 0,
        maxValue: 12
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/gad-2-total-score",
        display: "GAD-2",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "gad-2",
        description: "Generalized Anxiety Disorder 2-item short check",
        minValue: 0,
        maxValue: 6
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/ucla-3-total-score",
        display: "UCLA-3",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "ucla-3",
        description: "Three-Item Loneliness Scale (UCLA)",
        minValue: 3,
        maxValue: 9
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/wsas-total-score",
        display: "WSAS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "wsas",
        description: "Work and Social Adjustment Scale",
        minValue: 0,
        maxValue: 40
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/c-ssrs-severity",
        display: "C-SSRS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "c-ssrs",
        description: "Columbia Suicide Severity Rating Scale",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "70274-6",
        display: "GAD-7",
        system: "http://loinc.org",
        fieldKey: "gad-7",
        description: "Generalized Anxiety Disorder-7",
        minValue: 0,
        maxValue: 21
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/oci-4-total-score",
        display: "OCI-4",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "oci-4",
        description: "Obsessive-Compulsive Inventory-4",
        minValue: 0,
        maxValue: 16
    },
    {
        code: "102017-1",
        display: "PC-PTSD-5",
        system: "http://loinc.org",
        fieldKey: "pc-ptsd-5",
        description: "Primary Care PTSD Screen for DSM-5",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/psc-17-total-score",
        display: "PSC-17",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "psc-17",
        description: "Pediatric Symptom Checklist-17",
        minValue: 0,
        maxValue: 34
    },
    {
        code: "44261-6",
        display: "PHQ-9",
        system: "http://loinc.org",
        fieldKey: "phq-9",
        description: "Patient Health Questionnaire-9",
        minValue: 0,
        maxValue: 27
    },
    {
        code: "89204-2",
        display: "PHQ-9A",
        system: "http://loinc.org",
        fieldKey: "phq-9a",
        description: "Patient Health Questionnaire-9 Modified for Teens",
        minValue: 0,
        maxValue: 27
    },
    {
        code: "70272-0",
        display: "PHQ-4",
        system: "http://loinc.org",
        fieldKey: "phq-4",
        description: "Patient Health Questionnaire-4",
        minValue: 0,
        maxValue: 12
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/prapare-risk-score",
        display: "PRAPARE",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "prapare",
        description: "Protocol for Responding to and Assessing Patients' Assets, Risks, and Experiences",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/sbirt-ed-score",
        display: "SBIRT-ED",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "sbirt-ed",
        description: "SBIRT Eating Disorder Screener",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/taps-1-score",
        display: "TAPS-1",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "taps-1",
        description: "TAPS Screener (Part 1)",
        minValue: 0,
        maxValue: 16
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/taps-2-score",
        display: "TAPS-2",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "taps-2",
        description: "TAPS Assessment (Part 2)",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/wpai-impairment-score",
        display: "WPAI",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "wpai",
        description: "Work Productivity and Activity Impairment",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/who-5-total-score",
        display: "WHO-5",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "who-5",
        description: "World Health Organization Well-Being Index",
        minValue: 0,
        maxValue: 25
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/sdoh-health-leads-score",
        display: "SDOH",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "sdoh",
        description: "Social Needs Screening Toolkit (Health Leads)",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-complexity-score",
        display: "Mirah Complexity",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "mirah-complexity",
        description: "Mirah Complexity Composite Score",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-severity-score",
        display: "Mirah Severity",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "mirah-severity",
        description: "Mirah Severity Composite Score",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/nps-score",
        display: "NPS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "nps",
        description: "Net Promoter Score (0–10)",
        minValue: 0,
        maxValue: 10
    }
];
// Select options for forms
OBSERVATION_TYPES.map((type)=>({
        value: type.code,
        label: type.display
    }));
/**
 * Get the complete observation coding for a given LOINC code
 * Returns undefined if the code is not valid for observations
 */ function getObservationCoding(code) {
    const observationType = OBSERVATION_TYPES.find((type)=>type.code === code);
    return observationType ? {
        system: observationType.system,
        code: observationType.code,
        display: observationType.display
    } : undefined;
}

/**
 * FHIR extension URL marking a care-time entry as excluded from CoCM billing.
 * Absent or `valueBoolean: false` means the entry is billable (backward
 * compatible — no historical data carries the extension). See the care-time
 * non-billable toggle plan (2026-07-23).
 * @agentic @benchmark/core
 */ const CARE_TIME_NON_BILLABLE_EXTENSION_URL = `${EXTENSION_BASE}/care-time-non-billable`;
/**
 * True only when the entry carries the non-billable extension set to `true`.
 * Any other shape (missing extension, `false`, absent `valueBoolean`) is
 * treated as billable.
 * @agentic @benchmark/core
 */ const isCareTimeNonBillable = (observation)=>(observation.extension ?? []).some((ext)=>ext.url === CARE_TIME_NON_BILLABLE_EXTENSION_URL && ext.valueBoolean === true);
/**
 * Care-time observation code system (Benchmark-minted).
 * Holds the single `care-management-minutes` code (D3).
 * @agentic @benchmark/core
 */ const CARE_TIME_CODE_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/care-time-type";
/**
 * Benchmark observation category system used as a sub-classification
 * alongside the HL7 `activity` category.
 * @agentic @benchmark/core
 */ const CARE_TIME_CATEGORY_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/observation-category";
/**
 * Care-time method system encoding the source surface that produced
 * the entry (`task-completion`, `progress-update`, `call`, ...).
 * @agentic @benchmark/core
 */ const CARE_TIME_METHOD_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/care-time-method";
/**
 * UCUM system URL — used on `valueQuantity.system` for the `min` unit.
 * @agentic @benchmark/core
 */ const UCUM_SYSTEM = "http://unitsofmeasure.org";
/**
 * HL7 observation-category code used as the primary care-time category.
 * Pairs with the Benchmark `care-time` sub-category coding.
 * @agentic @benchmark/core
 */ const ACTIVITY_OBSERVATION_CATEGORY = {
    coding: [
        {
            system: "http://terminology.hl7.org/CodeSystem/observation-category",
            code: "activity",
            display: "Activity"
        }
    ]
};
/**
 * Benchmark `care-time` sub-category coding paired with the HL7
 * `activity` category on every care-time Observation.
 * @agentic @benchmark/core
 */ const CARE_TIME_OBSERVATION_CATEGORY = {
    coding: [
        {
            system: CARE_TIME_CATEGORY_SYSTEM,
            code: "care-time",
            display: "Care Time"
        }
    ]
};
/**
 * Single observation code for all care-time entries.
 * @agentic @benchmark/core
 */ const CARE_TIME_OBSERVATION_CODE_VALUE = "care-management-minutes";
/**
 * `Observation.code` payload for every care-time entry (D3 LOCKED).
 * @agentic @benchmark/core
 */ const CARE_TIME_OBSERVATION_CODE = {
    coding: [
        {
            system: CARE_TIME_CODE_SYSTEM,
            code: CARE_TIME_OBSERVATION_CODE_VALUE,
            display: "Care management minutes"
        }
    ],
    text: "Care management minutes"
};
/**
 * Source-surface method options carried on `Observation.method` (D3 LOCKED).
 * @agentic @benchmark/core
 */ const CARE_TIME_METHOD_OPTIONS = [
    {
        code: "task-completion",
        display: "Task Completion"
    },
    {
        code: "progress-update",
        display: "Progress Update"
    },
    {
        code: "action-list-quick-add",
        display: "Action List Quick Add"
    },
    {
        code: "patient-profile-quick-add",
        display: "Patient Profile Quick Add"
    },
    {
        code: "intake-call",
        display: "Intake Call"
    },
    {
        code: "intake-documentation",
        display: "Intake Documentation"
    },
    {
        code: "cocm-followup",
        display: "CoCM Follow-Up"
    },
    {
        code: "general-interaction",
        display: "General Interaction"
    },
    {
        code: "call",
        display: "Call"
    },
    {
        code: "sms",
        display: "SMS"
    }
];
/**
 * Set of all valid care-time methods for fast membership checks.
 * @agentic @benchmark/core
 */ const CARE_TIME_METHOD_CODES = new Set(CARE_TIME_METHOD_OPTIONS.map((o)=>o.code));
/**
 * Build the FHIR CodeableConcept payload for an `Observation.method` value.
 * @agentic @benchmark/core
 */ const getCareTimeMethodCoding = (method)=>{
    const option = CARE_TIME_METHOD_OPTIONS.find((o)=>o.code === method);
    const display = option?.display ?? method;
    return {
        coding: [
            {
                system: CARE_TIME_METHOD_SYSTEM,
                code: method,
                display
            }
        ],
        text: display
    };
};
/**
 * UCUM `min` quantity coding used on every care-time Observation's
 * `valueQuantity`. Helper centralizes the unit shape.
 * @agentic @benchmark/core
 */ const buildCareTimeMinutesQuantity = (value)=>({
        value,
        unit: "min",
        system: UCUM_SYSTEM,
        code: "min"
    });
/**
 * Complete roster of every assessment instrument that can enter a patient's
 * chart, mapped to the code its score Observation carries. This is the ONE
 * place that enumerates all instruments; billing eligibility is derived from it
 * as a denylist (see {@link BILLING_INELIGIBLE_ASSESSMENTS}) so a newly added
 * instrument is never silently billable — it must be classified here.
 *
 * Observations are clinical chart data first; billing eligibility is a separate
 * decision this module owns. A `code: ""` entry is a categorical instrument
 * (PGI-S, PGI-C, Safety Plan, Drug Use) that emits NO numeric score
 * Observation; every such instrument is billing-ineligible and must appear in
 * {@link BILLING_INELIGIBLE_ASSESSMENTS}.
 *
 * This list is source-agnostic: eligibility reads Observations by code, so a
 * matching score unlocks billing whether it was written by the in-app
 * clinician encounter, a fixed remote survey link, or the adaptive screener.
 *
 * Two eligibility nuances the code-based list CANNOT enforce on its own:
 *   - AUDIT-C: only a FULLY administered AUDIT-C is billing-valid (2026-09-30).
 *     A gated "Never" first item is a genuine complete 0/12 and counts; an
 *     abandoned/partial administration must NOT emit a score Observation. Both
 *     share the one AUDIT-C score code, so completeness has to be enforced
 *     where the Observation is written, not here. Follow-up tracked on the
 *     billing-parity ticket.
 *   - Legacy PHQ-2 (deprecated LOINC 55758-7) is never billing-valid. It is not
 *     an administered instrument today, so it is intentionally absent from this
 *     roster; the lock test guards that its code never appears in the eligible
 *     list.
 * @agentic @benchmark/core
 */ const ALL_ASSESSMENTS = {
    PHQ4: OBSERVATION_LOINC_CODES.PHQ4_TOTAL_SCORE,
    PHQ9: OBSERVATION_LOINC_CODES.PHQ9_TOTAL_SCORE,
    PHQ9A: OBSERVATION_LOINC_CODES.PHQ9A_TOTAL_SCORE,
    GAD7: OBSERVATION_LOINC_CODES.GAD7_TOTAL_SCORE,
    GAD2: OBSERVATION_LOINC_CODES.GAD2_TOTAL_SCORE,
    PC_PTSD_5: OBSERVATION_LOINC_CODES.PC_PTSD_5_TOTAL_SCORE,
    PSC_17: OBSERVATION_LOINC_CODES.PSC_17_TOTAL_SCORE,
    C_SSRS: OBSERVATION_LOINC_CODES.C_SSRS_SEVERITY,
    ASRS: OBSERVATION_LOINC_CODES.ASRS_TOTAL_SCORE,
    OCI_4: OBSERVATION_LOINC_CODES.OCI_4_TOTAL_SCORE,
    TAPS_1: OBSERVATION_LOINC_CODES.TAPS_1_TOTAL_SCORE,
    TAPS_2: OBSERVATION_LOINC_CODES.TAPS_2_TOTAL_SCORE,
    SBIRT_ED: OBSERVATION_LOINC_CODES.SBIRT_ED_TOTAL_SCORE,
    AUDIT_C: OBSERVATION_LOINC_CODES.AUDIT_C_TOTAL_SCORE,
    UCLA3: OBSERVATION_LOINC_CODES.UCLA3_TOTAL_SCORE,
    WSAS: OBSERVATION_LOINC_CODES.WSAS_TOTAL_SCORE,
    WPAI: OBSERVATION_LOINC_CODES.WPAI_TOTAL_SCORE,
    WHO5: OBSERVATION_LOINC_CODES.WHO5_TOTAL_SCORE,
    SDOH: OBSERVATION_LOINC_CODES.SDOH_TOTAL_SCORE,
    MIRAH_COMPLEXITY: OBSERVATION_LOINC_CODES.MIRAH_COMPLEXITY_SCORE,
    MIRAH_SEVERITY: OBSERVATION_LOINC_CODES.MIRAH_SEVERITY_SCORE,
    NPS: OBSERVATION_LOINC_CODES.NPS_SCORE,
    PGI_S: "",
    PGI_C: "",
    SAFETY_PLAN: "",
    DRUG_USE: ""
};
/**
 * Instruments that are administered but are NOT billing-valid for CoCM
 * eligibility (D18). This is the denylist over {@link ALL_ASSESSMENTS}: every
 * key here is subtracted to produce {@link BILLING_ELIGIBLE_ASSESSMENTS}. Each
 * entry names WHY it is excluded so the decision stays auditable. Confirmed
 * with clinical/compliance (2026-06-22, refined 2026-09-30).
 *
 * Note: an instrument absent from BOTH this denylist and the eligible set can
 * only happen if a new key is added to ALL_ASSESSMENTS without classifying it;
 * the exact-set lock test in `care-time.constants.test.ts` fails loudly in that
 * case rather than letting an unreviewed instrument become billable.
 * @agentic @benchmark/core
 */ const BILLING_INELIGIBLE_ASSESSMENTS = {
    PHQ4: "Ultra-brief combined screener; not a standalone severity measure.",
    GAD2: "Two-item anxiety short check; not billing-valid (opens GAD-7, which is).",
    UCLA3: "Loneliness scale; outside the billing-valid clinical set.",
    WSAS: "Everyday-functioning scale; not a clinical severity measure.",
    WPAI: "Work productivity/activity; functional, not clinical severity.",
    WHO5: "Well-being index; not a billing-valid severity measure.",
    SDOH: "Social-needs screen; not a clinical severity measure.",
    MIRAH_COMPLEXITY: "Vendor composite score; not a billing-valid instrument.",
    MIRAH_SEVERITY: "Vendor composite score; not a billing-valid instrument.",
    NPS: "Member-feedback likelihood score; not clinical.",
    PGI_S: "Categorical patient self-rating; no numeric score Observation.",
    PGI_C: "Categorical patient self-rating; no numeric score Observation.",
    SAFETY_PLAN: "Care-planning artifact; no score Observation.",
    DRUG_USE: "House-modified categorical drug screen; not billing-valid (2026-09-30) and no numeric score Observation."
};
// Eligible-by-policy = every instrument NOT on the denylist. Confirmed valid
// (2026-09-30): PHQ-9, PHQ-9A, GAD-7, PC-PTSD-5, PSC-17, C-SSRS, ASRS, OCI-4,
// TAPS-1, TAPS-2, SBIRT-ED, and AUDIT-C (full administration only). Every one
// carries a real score code; the `.filter` below is a guard, and the lock test
// asserts no eligible-by-policy instrument is categorical (`code: ""`) — a
// categorical instrument must be denied above, never silently dropped here.
const BILLING_ELIGIBLE_POLICY_KEYS = Object.keys(ALL_ASSESSMENTS).filter((key)=>!(key in BILLING_INELIGIBLE_ASSESSMENTS));
/**
 * The single billing-eligible assessment list, honored regardless of which
 * entry surface (in-app encounter, remote survey, adaptive screener) wrote the
 * score. Derived as {@link ALL_ASSESSMENTS} minus
 * {@link BILLING_INELIGIBLE_ASSESSMENTS}, then narrowed to instruments that
 * actually emit a score Observation (non-empty code). Each entry's system is
 * resolved the same way `buildObservationFromScore` stamps the score
 * Observation (`getObservationCoding(code)?.system ?? "http://loinc.org"`), so
 * the eligibility search always matches what questionnaire completion writes.
 * Within the 6-week freshness window these unlock 99492 / 99493 / 99494.
 * @agentic @benchmark/core
 */ const BILLING_ELIGIBLE_ASSESSMENTS = BILLING_ELIGIBLE_POLICY_KEYS.map((key)=>ALL_ASSESSMENTS[key]).filter((code)=>code !== "").map((code)=>({
        system: getObservationCoding(code)?.system ?? "http://loinc.org",
        code
    }));
/**
 * Codes-only view of {@link BILLING_ELIGIBLE_ASSESSMENTS} for value comparisons
 * and tests.
 * @agentic @benchmark/core
 */ BILLING_ELIGIBLE_ASSESSMENTS.map((e)=>e.code);
/**
 * System-aware comma-delimited token search expression for the eligible
 * assessments (FHIR uses comma for OR within a single search parameter, and
 * `system|code` for the system-qualified token). Supports mixed LOINC and
 * organizational systems. Used by the eligibility service and the registry hook.
 * @agentic @benchmark/core
 */ BILLING_ELIGIBLE_ASSESSMENTS.map((e)=>`${e.system}|${e.code}`).join(",");

/**
 * Build the FHIR Observation resource for a care-time entry. Pure mapping.
 *
 * If `existingObservation` is provided, the function preserves its `id`
 * and `meta` and overlays the new fields — used by the edit flow in the
 * activity-log drawer and by the intake-modal "update auto-created
 * Communication entry" path (D4 no-double-write).
 * @agentic @benchmark/core
 */ const buildCareTimeObservation = (form, existingObservation)=>{
    if (!Number.isFinite(form.minutes) || form.minutes <= 0) {
        throw new Error("Care-time minutes must be a positive number");
    }
    if (!form.patientId) {
        throw new Error("Care-time entry requires a patientId");
    }
    if (!form.performerRoleId) {
        throw new Error("Care-time entry requires a performerRoleId (performer)");
    }
    const observation = existingObservation ? {
        ...existingObservation
    } : {
        resourceType: "Observation",
        status: "final",
        code: {
            ...CARE_TIME_OBSERVATION_CODE
        }
    };
    // Preserve existing id for updates; status stays as-is unless we're
    // creating, in which case ensure status=final.
    if (!existingObservation) {
        observation.status = "final";
    }
    observation.category = [
        {
            ...ACTIVITY_OBSERVATION_CATEGORY
        },
        {
            ...CARE_TIME_OBSERVATION_CATEGORY
        }
    ];
    observation.code = {
        ...CARE_TIME_OBSERVATION_CODE
    };
    observation.method = getCareTimeMethodCoding(form.method);
    observation.subject = buildReference("Patient", form.patientId, form.patientDisplay);
    observation.performer = [
        buildReference("PractitionerRole", form.performerRoleId, form.performerDisplay)
    ];
    observation.valueQuantity = buildCareTimeMinutesQuantity(form.minutes);
    if (form.effectivePeriodStart && form.effectivePeriodEnd) {
        observation.effectivePeriod = {
            start: form.effectivePeriodStart,
            end: form.effectivePeriodEnd
        };
        delete observation.effectiveDateTime;
    } else {
        observation.effectiveDateTime = form.effectiveDate;
        delete observation.effectivePeriod;
    }
    observation.issued = new Date().toISOString();
    if (form.taskId) {
        observation.focus = [
            buildReference("Task", form.taskId, form.taskDisplay ?? "Task")
        ];
    } else if (!existingObservation) {
        delete observation.focus;
    }
    if (form.carePlanId) {
        observation.basedOn = [
            buildReference("CarePlan", form.carePlanId, form.carePlanDisplay ?? "Care Plan")
        ];
    } else if (!existingObservation) {
        delete observation.basedOn;
    }
    if (form.encounterId) {
        observation.encounter = buildReference("Encounter", form.encounterId, form.encounterDisplay ?? `Encounter ${form.encounterId}`);
    } else if (!existingObservation) {
        delete observation.encounter;
    }
    if (form.communicationId) {
        observation.derivedFrom = [
            buildReference("Communication", form.communicationId, `Communication ${form.communicationId}`)
        ];
    } else if (!existingObservation) {
        delete observation.derivedFrom;
    }
    if (form.notes && form.notes.trim().length > 0) {
        observation.note = [
            {
                text: form.notes.trim()
            }
        ];
    } else if (!existingObservation) {
        delete observation.note;
    } else {
        observation.note = undefined;
    }
    // Upsert the non-billable flag, tri-state on `form.nonBillable` and never
    // clobbering other extensions the Observation may already carry:
    //   true      → stamp the marker (excluded from CoCM billing)
    //   false     → remove the marker (explicitly billable)
    //   undefined → leave the entry's existing flag untouched, so update paths
    //               that don't manage this flag (e.g. encounter re-saves that
    //               rebuild the intake entries) can't silently strip it. An
    //               omitted flag must not be read as "make it billable".
    if (form.nonBillable !== undefined) {
        const otherExtensions = (observation.extension ?? []).filter((ext)=>ext.url !== CARE_TIME_NON_BILLABLE_EXTENSION_URL);
        if (form.nonBillable) {
            observation.extension = [
                ...otherExtensions,
                {
                    url: CARE_TIME_NON_BILLABLE_EXTENSION_URL,
                    valueBoolean: true
                }
            ];
        } else if (otherExtensions.length > 0) {
            observation.extension = otherExtensions;
        } else {
            delete observation.extension;
        }
    }
    return observation;
};

/**
 * Convert care-time form data into a FHIR Observation. Pure.
 * @agentic @benchmark/core
 */ const careTimeFromForm = (form, existingObservation)=>buildCareTimeObservation(form, existingObservation);

/**
 * Extract a "ResourceType/id" reference's id portion. Returns `undefined`
 * when the reference doesn't match the expected resource type.
 * @agentic @benchmark/core
 */ const extractRefId = (ref, resourceType)=>{
    if (!ref?.reference) return undefined;
    const prefix = `${resourceType}/`;
    if (!ref.reference.startsWith(prefix)) return undefined;
    const id = ref.reference.slice(prefix.length);
    if (!id) return undefined;
    return {
        id,
        display: ref.display
    };
};
/**
 * Convert a care-time Observation into form data. Returns `undefined`
 * for Observations that don't carry the expected care-time shape (e.g.
 * an Observation without a `valueQuantity` minute value).
 * @agentic @benchmark/core
 */ const careTimeToForm = (observation)=>{
    const minutes = observation.valueQuantity?.value;
    if (typeof minutes !== "number") return undefined;
    const methodCode = observation.method?.coding?.[0]?.code;
    if (!methodCode || !CARE_TIME_METHOD_CODES.has(methodCode)) {
        return undefined;
    }
    const method = methodCode;
    const subject = extractRefId(observation.subject, "Patient");
    if (!subject) return undefined;
    const performerRef = observation.performer?.[0];
    const performer = extractRefId(performerRef, "PractitionerRole");
    if (!performer) return undefined;
    const effectiveDate = observation.effectiveDateTime ?? observation.effectivePeriod?.start ?? new Date(0).toISOString();
    const taskRef = observation.focus?.[0];
    const task = taskRef ? extractRefId(taskRef, "Task") : undefined;
    const carePlanRef = observation.basedOn?.[0];
    const carePlan = carePlanRef ? extractRefId(carePlanRef, "CarePlan") : undefined;
    const encounter = observation.encounter ? extractRefId(observation.encounter, "Encounter") : undefined;
    const communicationRef = observation.derivedFrom?.[0];
    const communication = communicationRef ? extractRefId(communicationRef, "Communication") : undefined;
    return {
        minutes,
        method,
        effectiveDate,
        effectivePeriodStart: observation.effectivePeriod?.start,
        effectivePeriodEnd: observation.effectivePeriod?.end,
        patientId: subject.id,
        patientDisplay: subject.display ?? "Patient",
        performerRoleId: performer.id,
        performerDisplay: performer.display ?? "Practitioner",
        taskId: task?.id,
        taskDisplay: task?.display,
        carePlanId: carePlan?.id,
        carePlanDisplay: carePlan?.display,
        encounterId: encounter?.id,
        encounterDisplay: encounter?.display,
        communicationId: communication?.id,
        notes: observation.note?.[0]?.text,
        nonBillable: isCareTimeNonBillable(observation),
        existingObservationId: observation.id
    };
};

/**
 * Converts form data to domain model for builder consumption.
 * @param formData - Clinical impression form data
 * @returns Domain model ready for builder consumption
 * @agentic @benchmark/core
 */ function clinicalImpressionFromForm(formData) {
    // Build code from impression-type
    const impressionType = formData["impression-type"] || "";
    const code = {
        coding: [
            {
                system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types",
                code: impressionType,
                display: CLINICAL_IMPRESSION_TYPES[impressionType] || ""
            }
        ]
    };
    // Build value from summary
    const value = formData.summary ? {
        valueString: formData.summary
    } : undefined;
    return {
        code,
        valueType: "summary",
        value,
        date: formData.effective
    };
}

/**
 * Clinical Impression To Form Adapter
 *
 * Pure function that maps ClinicalImpression domain model to ClinicalImpressionFormData.
 * No IO operations - mechanical transformation only.
 */ /**
 * Converts FHIR ClinicalImpression resource to form data. Pure mapping, no IO.
 * @param clinicalImpression - ClinicalImpression resource
 * @returns ClinicalImpression form data
 * @agentic @benchmark/core
 */ function clinicalImpressionToForm(clinicalImpression) {
    const formData = {};
    // Convert impression type from coding
    if (clinicalImpression.code?.coding?.[0]) {
        const coding = clinicalImpression.code.coding[0];
        // Check if it's using our custom coding system
        if (coding.system === "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types") {
            formData["impression-type"] = coding.code;
        } else {
            // For backward compatibility with old SNOMED codes, try to map to closest custom field
            // or just use the code as-is
            formData["impression-type"] = coding.code;
        }
    }
    // Convert effective date
    if (clinicalImpression.date) {
        formData.effective = clinicalImpression.date;
    } else if (clinicalImpression.effectiveDateTime) {
        // Fallback for backward compatibility
        formData.effective = clinicalImpression.effectiveDateTime;
    }
    // Convert summary
    if (clinicalImpression.summary) {
        formData.summary = clinicalImpression.summary;
    }
    return formData;
}

/** Converts FHIR QuestionnaireResponse items to internal format. Flattens nested structure. @agentic @benchmark/core */ function flattenResponseItems(items) {
    const result = [];
    for (const item of items){
        // Handle nested items recursively
        if (item.item && item.item.length > 0) {
            result.push(...flattenResponseItems(item.item));
        }
        // Handle answers
        if (item.answer && item.answer.length > 0) {
            const answer = item.answer[0];
            if (answer.valueString !== undefined) {
                result.push({
                    linkId: item.linkId,
                    valueString: answer.valueString
                });
            } else if (answer.valueInteger !== undefined) {
                result.push({
                    linkId: item.linkId,
                    value: answer.valueInteger,
                    coding: answer.valueCoding ? {
                        code: answer.valueCoding.code || "",
                        display: answer.valueCoding.display || ""
                    } : undefined
                });
            }
        }
    }
    return result;
}

/**
 * Strict score-coding reverse lookup for encounter artifact ownership.
 * Display resolvers may remain code-tolerant; persistence requires the expected
 * coding system as well so an unrelated score cannot be claimed or deleted.
 */ const getQuestionnaireUrlFromScoreCoding = (coding)=>{
    if (!coding.code || !coding.system) return null;
    for (const config of Object.values(STANDARD_QUESTIONNAIRES)){
        if (!config.totalScoreLoincCode) continue;
        const expected = getObservationCoding$1(config.totalScoreLoincCode);
        if (expected && expected.code === coding.code && expected.system === coding.system) {
            return config.url;
        }
    }
    if (coding.system === "http://loinc.org" && coding.code === LEGACY_OBSERVATION_LOINC_CODES.GAD2_TOTAL_SCORE) {
        return QUESTIONNAIRE_URLS$1.GAD2;
    }
    return null;
};

/** Keys persisted in the draft form state extension (not stored in other FHIR resources). */ const DRAFT_STATE_KEYS = [
    "goal-creation",
    "followup-enroll",
    "followup-program-selection",
    "followup-include-referrals",
    "followup-service-needs",
    "followup-requires-approval",
    "followup-flags",
    "followup-task-type",
    "followup-due-date",
    "followup-description",
    "followup-assigned-to",
    "followup-assigned-to-display",
    "followup-mode",
    "followup-activity-code",
    "followup-care-plan-id",
    "_topicChecks",
    "_sectionScratch",
    // Structured AI safety-risk signal (R1). Draft-only: it lives on the scribe
    // Composition, not the encounter, so without round-tripping it here a resumed
    // draft would lose the signal and under-prompt for a genuinely at-risk patient.
    "_aiSafetyRiskPresent",
    // Care-time minutes are draft-only state: a draft must NOT create care-time
    // Observations (the monthly total counts `preliminary` status), so the
    // minutes ride along in the draft extension and become Observations only on
    // finalize. The linkage ids ride along so the finalize build can set
    // focus/basedOn.
    "care-time-call-minutes",
    "care-time-call-notes",
    "care-time-documentation-minutes",
    "care-time-documentation-notes",
    "_careTimeTaskId",
    "_careTimeTaskDisplay",
    "_careTimeCarePlanId",
    "_careTimeCarePlanDisplay",
    // Adaptive screener rail state — session id, settlements, acknowledgement,
    // and record-gate flags (_adaptiveActive / _adaptiveCanRecord) so draft
    // resume keeps C6 + D13 enforcement before AssessmentsStep remounts.
    "_adaptiveScreenerSession",
    "_adaptiveSettlements",
    "_suicideRiskAcknowledged",
    "_suicideRiskAcknowledgedAt",
    "_suicideRiskAcknowledgedBy",
    "_adaptiveActive",
    "_adaptiveCanRecord"
];
/**
 * Builds the draftFormState record from form data for passing to the bundle builder.
 * @agentic @benchmark/core
 */ function buildDraftFormStateRecord(formData) {
    const state = {};
    for (const key of DRAFT_STATE_KEYS){
        const value = formData[key];
        if (value !== undefined && value !== null && value !== "") {
            state[key] = value;
        }
    }
    return state;
}
/**
 * Extracts non-bundled form state from the draft extension on an encounter.
 * Returns an empty object if the extension is missing or malformed.
 * @agentic @benchmark/core
 */ function extractDraftFormState(encounter) {
    const ext = encounter.extension?.find((e)=>e.url === ENCOUNTER_DRAFT_FORM_STATE_EXTENSION_URL);
    if (!ext?.valueString) return {};
    try {
        const parsed = JSON.parse(ext.valueString);
        const result = {};
        for (const key of DRAFT_STATE_KEYS){
            if (key in parsed) {
                result[key] = parsed[key];
            }
        }
        return result;
    } catch  {
        return {};
    }
}

const flattenItems = (items)=>items.flatMap((item)=>item.type === "group" && item.item ? [
            item,
            ...flattenItems(item.item)
        ] : [
            item
        ]);
const answered = (answer)=>answer.value !== undefined || answer.valueString !== undefined && answer.valueString.length > 0;
/**
 * Evaluate completion against the current canonical questionnaire definition.
 * Gated-out required items are excluded when their gate parent is answered 0.
 *
 * @agentic @benchmark/core
 */ const evaluateQuestionnaireCompletion = (questionnaireUrl, answers, options = {})=>{
    const items = getQuestionnaireItems(questionnaireUrl);
    if (!items) {
        return {
            supported: false,
            reason: "unknown-questionnaire"
        };
    }
    const flattened = flattenItems(items);
    const itemByLinkId = new Map(flattened.map((item)=>[
            item.linkId,
            item
        ]));
    for (const answer of answers){
        if (!itemByLinkId.has(answer.linkId)) {
            return {
                supported: false,
                reason: "unknown-link-id"
            };
        }
        if (!answered(answer) && answer.coding !== undefined) {
            return {
                supported: false,
                reason: "unsupported-answer"
            };
        }
    }
    const valueByLinkId = new Map(answers.map((answer)=>[
            answer.linkId,
            answer.value
        ]));
    const gateForByLinkId = new Map();
    for (const item of flattened){
        if (item.gateFor) {
            gateForByLinkId.set(item.linkId, item.gateFor);
        }
    }
    const hidden = new Set();
    for (const item of flattened){
        if (!item.gateFor || valueByLinkId.get(item.linkId) !== 0) {
            continue;
        }
        const queue = [
            ...item.gateFor
        ];
        while(queue.length > 0){
            const linkId = queue.shift();
            if (hidden.has(linkId)) {
                continue;
            }
            hidden.add(linkId);
            queue.push(...gateForByLinkId.get(linkId) ?? []);
        }
    }
    const answerByLinkId = new Map(answers.filter(answered).map((answer)=>[
            answer.linkId,
            answer
        ]));
    const missingRequired = flattened.filter((item)=>item.type !== "group" && item.required !== false && !hidden.has(item.linkId) && !answerByLinkId.has(item.linkId)).map((item)=>item.linkId);
    const activeRequiredCount = flattened.filter((item)=>item.type !== "group" && item.required !== false && !hidden.has(item.linkId)).length;
    return {
        supported: true,
        complete: missingRequired.length === 0 && (activeRequiredCount > 0 || options.declaredComplete === true),
        missingRequired
    };
};

/** `meta.tag` system that marks a QR as part of an adaptive-screener sitting. */ const ADAPTIVE_SCREENER_SESSION_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/adaptive-screener-session";
/** Extension url carrying why the section was asked (F7). */ const ADAPTIVE_SCREENER_REASON_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-reason";
/** Extension url carrying whether the sitting was the patient's baseline. */ const ADAPTIVE_SCREENER_BASELINE_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-baseline";
/** Extension url carrying how the section settled — answered vs a decline kind (D8). */ const ADAPTIVE_SCREENER_SETTLEMENT_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-settlement";
/**
 * Extension url carrying the link ids the patient explicitly skipped (asked and
 * not answered — PRD §8.4). Skips are deliberately persisted as an extension,
 * never as answerless `item` entries: item presence would read as "content" to
 * cross-visit memory (flipping a later administration to follow-up against
 * D12) and would complicate every raw-item consumer, while an extension is
 * invisible to memory, completion, and scoring.
 */ const ADAPTIVE_SCREENER_SKIPPED_ITEMS_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-skipped-items";
/**
 * Extension url freezing the administration's composition inputs at the first
 * persisted write: which short-check sections were opened-in-full by memory
 * and whether the rating anchor was established. Without this, every request
 * re-derives composition from the patient's *current* prior responses, so a
 * prior sitting completing mid-administration would flip the composed section
 * list (and kind) between two saves of the same sitting. The marker makes the
 * first write's derivation authoritative for the administration's lifetime.
 */ const ADAPTIVE_SCREENER_COMPOSITION_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-composition";
/** Extension urls capturing the suicide-risk acknowledgement author + time (D13). */ const ADAPTIVE_SCREENER_ACK_AT_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-acknowledged-at";
const ADAPTIVE_SCREENER_ACK_BY_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-acknowledged-by";
/**
 * Read the adaptive-screener marker off a QR, or null when the QR was not part
 * of an adaptive-screener sitting.
 *
 * @agentic @benchmark/core
 */ /**
 * Parse the frozen-composition extension fail-soft: a corrupt or unexpected
 * payload yields `undefined` (the caller re-derives from prior responses)
 * rather than throwing on a persisted-value read.
 */ const parseComposition = (raw)=>{
    if (!raw) return undefined;
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null) return undefined;
        const candidate = parsed;
        if (!Array.isArray(candidate.openedInFullBefore) || !candidate.openedInFullBefore.every((key)=>typeof key === "string") || typeof candidate.ratingAnchorEstablished !== "boolean") {
            return undefined;
        }
        return {
            openedInFullBefore: candidate.openedInFullBefore,
            ratingAnchorEstablished: candidate.ratingAnchorEstablished
        };
    } catch  {
        return undefined;
    }
};
const readAdaptiveScreenerMarker = (qr)=>{
    const sessionTag = (qr.meta?.tag ?? []).find((tag)=>tag.system === ADAPTIVE_SCREENER_SESSION_SYSTEM);
    if (!sessionTag?.code) {
        return null;
    }
    const findExt = (url)=>(qr.extension ?? []).find((ext)=>ext.url === url);
    const skippedRaw = findExt(ADAPTIVE_SCREENER_SKIPPED_ITEMS_EXTENSION)?.valueString;
    const skippedLinkIds = skippedRaw ? skippedRaw.split(/\s+/).filter((id)=>id.length > 0) : undefined;
    const composition = parseComposition(findExt(ADAPTIVE_SCREENER_COMPOSITION_EXTENSION)?.valueString);
    return {
        sessionId: sessionTag.code,
        baseline: findExt(ADAPTIVE_SCREENER_BASELINE_EXTENSION)?.valueBoolean === true,
        reasonCode: findExt(ADAPTIVE_SCREENER_REASON_EXTENSION)?.valueCode,
        settlement: findExt(ADAPTIVE_SCREENER_SETTLEMENT_EXTENSION)?.valueCode,
        ...skippedLinkIds && skippedLinkIds.length > 0 ? {
            skippedLinkIds
        } : {},
        ...composition ? {
            composition
        } : {},
        acknowledgedAt: findExt(ADAPTIVE_SCREENER_ACK_AT_EXTENSION)?.valueDateTime,
        acknowledgedBy: findExt(ADAPTIVE_SCREENER_ACK_BY_EXTENSION)?.valueString
    };
};
/** A persisted Encounter contains contradictory adaptive-session markers. */ class AdaptiveScreenerIntegrityError extends Error {
    constructor(message){
        super(message);
        this.name = "AdaptiveScreenerIntegrityError";
    }
}
/**
 * Restore durable adaptive form state from encounter-linked QRs.
 *
 * One encounter edit may own at most one adaptive sitting. Conflicting markers
 * fail closed rather than selecting an arbitrary session and rewriting clinical
 * provenance.
 *
 * @agentic @benchmark/core
 */ const restoreAdaptiveScreenerState = (responses)=>{
    let sessionId;
    let baseline;
    let acknowledgedAt;
    let acknowledgedBy;
    const reasons = {};
    const settlements = {};
    const seenUrls = new Set();
    for (const response of responses){
        const marker = readAdaptiveScreenerMarker(response);
        if (!marker) continue;
        const url = response.questionnaire;
        if (!url) {
            throw new AdaptiveScreenerIntegrityError("An adaptive response is missing its questionnaire canonical.");
        }
        if (seenUrls.has(url)) {
            throw new AdaptiveScreenerIntegrityError("Multiple adaptive responses exist for the same questionnaire.");
        }
        seenUrls.add(url);
        if (sessionId !== undefined && sessionId !== marker.sessionId) {
            throw new AdaptiveScreenerIntegrityError("Multiple adaptive screening sessions are linked to this encounter.");
        }
        if (baseline !== undefined && baseline !== marker.baseline) {
            throw new AdaptiveScreenerIntegrityError("Adaptive screening baseline markers conflict.");
        }
        sessionId = marker.sessionId;
        baseline = marker.baseline;
        if (marker.reasonCode) {
            reasons[url] = marker.reasonCode;
        }
        if (marker.settlement) {
            settlements[url] = marker.settlement;
        }
        if (acknowledgedAt !== undefined && marker.acknowledgedAt !== undefined && acknowledgedAt !== marker.acknowledgedAt) {
            throw new AdaptiveScreenerIntegrityError("Adaptive screening acknowledgement timestamps conflict.");
        }
        if (acknowledgedBy !== undefined && marker.acknowledgedBy !== undefined && acknowledgedBy !== marker.acknowledgedBy) {
            throw new AdaptiveScreenerIntegrityError("Adaptive screening acknowledgement authors conflict.");
        }
        acknowledgedAt ??= marker.acknowledgedAt;
        acknowledgedBy ??= marker.acknowledgedBy;
    }
    if (!sessionId || baseline === undefined) {
        return null;
    }
    return {
        session: {
            id: sessionId,
            baseline,
            reasons
        },
        settlements,
        suicideRiskAcknowledged: Boolean(acknowledgedAt || acknowledgedBy),
        suicideRiskAcknowledgedAt: acknowledgedAt,
        suicideRiskAcknowledgedBy: acknowledgedBy
    };
};
new Map(LIKERT_FREQUENCY_OPTIONS.map((option)=>[
        option.coding.code,
        option.value
    ]));

/**
 * LOINC codes for questionnaire panels/forms (used for search/discovery and canonical URL construction)
 * These identify the questionnaire definition itself, NOT the scores/observations
 *
 * NOTE: These are used for:
 * 1. Searching/discovering questionnaires in FHIR servers
 * 2. Constructing canonical URLs for Questionnaire.url
 * 3. Initial questionnaire creation
 *
 * IMPORTANT: Once a questionnaire is fetched, prefer extracting LOINC codes
 * dynamically from the actual FHIR resource rather than using these static values.
 *
 * Only includes assessments with official LOINC questionnaire panel codes.
 * Questionnaires without official LOINC panel codes use organizational URLs.
 *
 * IMPORTANT: These codes should ONLY be used for Questionnaire and QuestionnaireResponse
 * resources. For total score observations, use OBSERVATION_LOINC_CODES from
 * observation.ts which contains the score codes.
 *
 * Validation: ✅ All codes verified against official LOINC database (2025-01-08)
 * See: packages/medplum/LOINC_CODE_MAPPINGS.md for detailed mapping documentation
 */ const QUESTIONNAIRE_LOINC_CODES = {
    /** ASRS Adult ADHD Self-Report Scale - LOINC 62750-5 ✅ VALIDATED */ ASRS: "62750-5",
    /** AUDIT-C Alcohol Screening Panel - LOINC 72109-2 ✅ VALIDATED */ AUDIT_C: "72109-2",
    /** C-SSRS Columbia Suicide Severity Rating Scale - LOINC 93245-9 ✅ VALIDATED */ C_SSRS: "93245-9",
    /** GAD-7 Anxiety Assessment Panel - LOINC 69737-5 ✅ VALIDATED */ GAD7: "69737-5",
    /** PC-PTSD-5 PTSD Screening Panel - LOINC 102010-6 ✅ VALIDATED */ PC_PTSD_5: "102010-6",
    /** PHQ-9 Depression Assessment Panel - LOINC 44249-1 ✅ VALIDATED */ PHQ9: "44249-1",
    /** PHQ-9A (Adolescent) Depression Assessment Panel - LOINC 89206-7 ✅ VALIDATED */ PHQ9A: "89206-7",
    /** PHQ-4 Combined Depression/Anxiety Panel - LOINC 69724-3 ✅ VALIDATED */ PHQ4: "69724-3",
    /** Health Leads Screening Panel (SDOH) - LOINC 99549-8 ✅ VALIDATED */ SDOH: "99549-8"
};
/**
 * Common questionnaire URLs used in FHIR Questionnaire.url
 * Uses official LOINC canonical URLs where available, organizational URLs otherwise
 */ const QUESTIONNAIRE_URLS = {
    ASRS: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.ASRS}`,
    C_SSRS: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.C_SSRS}`,
    GAD7: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.GAD7}`,
    OCI_4: "https://benchmarkhealth.org/fhir/Questionnaire/oci-4",
    PC_PTSD_5: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PC_PTSD_5}`,
    PSC_17: "https://benchmarkhealth.org/fhir/Questionnaire/psc-17",
    PHQ9: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PHQ9}`,
    PHQ9A: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PHQ9A}`,
    PHQ4: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PHQ4}`,
    SBIRT_ED: "https://benchmarkhealth.org/fhir/Questionnaire/sbirt-ed",
    SAFETY_PLAN: "https://benchmarkhealth.org/fhir/Questionnaire/safety-plan",
    TAPS_1: "https://benchmarkhealth.org/fhir/Questionnaire/taps-1",
    TAPS_2: "https://benchmarkhealth.org/fhir/Questionnaire/taps-2",
    WPAI: "https://benchmarkhealth.org/fhir/Questionnaire/wpai-functional",
    WHO5: "https://benchmarkhealth.org/fhir/Questionnaire/who-5",
    SDOH: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.SDOH}`,
    MIRAH_COMPLEXITY: "https://benchmarkhealth.org/fhir/Questionnaire/mirah-complexity",
    MIRAH_SEVERITY: "https://benchmarkhealth.org/fhir/Questionnaire/mirah-severity",
    NPS: "https://benchmarkhealth.org/fhir/Questionnaire/nps-member-feedback",
    DRUG_USE: "https://benchmarkhealth.org/fhir/Questionnaire/drug-use-screen",
    GAD2: "https://benchmarkhealth.org/fhir/Questionnaire/gad-2",
    AUDIT_C: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.AUDIT_C}`,
    UCLA3: "https://benchmarkhealth.org/fhir/Questionnaire/ucla-3",
    PGI_S: "https://benchmarkhealth.org/fhir/Questionnaire/pgi-s",
    PGI_C: "https://benchmarkhealth.org/fhir/Questionnaire/pgi-c",
    WSAS: "https://benchmarkhealth.org/fhir/Questionnaire/wsas"
};

/**
 * The full nine-section order (§8.2). Suicide-risk (3) and safety-plan (4) are
 * trigger-only and excluded from base composition; they are exported separately.
 */ const SECTION_SPECS = [
    {
        key: "patient-rating",
        order: 1,
        baseGating: "categorical",
        everyoneAsked: true,
        instruments: [
            "PGI_S"
        ]
    },
    {
        key: "depression",
        order: 2,
        baseGating: "always",
        everyoneAsked: true,
        instruments: [
            "PHQ9"
        ]
    },
    {
        key: "anxiety",
        order: 5,
        baseGating: "short-check",
        everyoneAsked: false,
        instruments: [
            "GAD2",
            "GAD7"
        ]
    },
    {
        key: "loneliness",
        order: 6,
        baseGating: "always",
        everyoneAsked: true,
        instruments: [
            "UCLA3"
        ]
    },
    {
        key: "alcohol",
        order: 7,
        baseGating: "short-check",
        everyoneAsked: false,
        instruments: [
            "AUDIT_C"
        ]
    },
    {
        key: "drug-use",
        order: 8,
        baseGating: "categorical",
        everyoneAsked: true,
        instruments: [
            "DRUG_USE"
        ]
    },
    {
        key: "functioning",
        order: 9,
        baseGating: "always",
        everyoneAsked: true,
        instruments: [
            "WSAS"
        ]
    }
];
/**
 * The trigger-added sections (PRD §8.4 / D9), in order, inserted live by the UI
 * directly after depression on a positive self-harm answer — never carried by
 * memory (suicide-risk is re-derived each visit, E6).
 */ const TRIGGER_ADDED_SECTIONS = [
    {
        key: "suicide-risk",
        order: 3,
        gating: "always",
        reasonCode: "triggered",
        reason: "Triggered now by the depression self-harm answer",
        instruments: [
            "C_SSRS"
        ]
    },
    {
        key: "safety-plan",
        order: 4,
        gating: "always",
        reasonCode: "triggered",
        reason: "Triggered now — always paired with suicide risk",
        instruments: [
            "SAFETY_PLAN"
        ]
    }
];
/**
 * Every instrument that can appear in an adaptive-screener sitting, in actual
 * administration order. The list is derived from the section definitions so
 * timeline/read-back surfaces cannot drift from screener composition. PGI-S
 * and PGI-C occupy the same final section and are mutually exclusive per visit.
 */ const ADAPTIVE_SCREENER_INSTRUMENT_ORDER = [
    ...SECTION_SPECS,
    ...TRIGGER_ADDED_SECTIONS
].sort((a, b)=>a.order - b.order).flatMap((section)=>section.key === "patient-rating" ? [
        "PGI_S",
        "PGI_C"
    ] : section.instruments);
/** Canonical URLs matching `ADAPTIVE_SCREENER_INSTRUMENT_ORDER`. */ const ADAPTIVE_SCREENER_QUESTIONNAIRE_ORDER = ADAPTIVE_SCREENER_INSTRUMENT_ORDER.map((instrument)=>QUESTIONNAIRE_URLS[instrument]);
new Map(ADAPTIVE_SCREENER_QUESTIONNAIRE_ORDER.map((url, index)=>[
        url,
        index
    ]));

/**
 * Whether a status is an explicit advocate/patient decision — a patient decline
 * or the advocate's not-clinically-appropriate judgement — as opposed to the
 * `answered` status (re-derived from responses) or the transient `pending`
 * default. Only these survive the rail remounting (e.g. toggling the screener
 * off and back on) and must be restored as settlements; `answered` and `pending`
 * are always derived from the live responses, never rehydrated as a settlement,
 * so a not-yet-settled section can always still be declined after a remount.
 */ const isExplicitSettlement = (status)=>status === "declined-remaining" || status === "declined-section" || status === "not-administered-clinical";

/**
 * Extension linking a saved Encounter back to the scribe Composition it was
 * prefilled from. Restoring `_scribeCompositionId` from it lets an edit/re-open
 * save re-affirm the FHIR-native `Composition.encounter` link (parity with the
 * initial draft/finalize save).
 */ const PREFILL_SOURCE_EXTENSION_URL = "http://benchmarkhealth.com/fhir/StructureDefinition/prefill-source";
const hasUnsupportedQuestionnaireAnswer = (items)=>items.some((item)=>{
        const unsupportedAnswer = (item.answer ?? []).some((answer)=>answer.valueInteger === undefined && answer.valueString === undefined);
        return unsupportedAnswer || (item.item ? hasUnsupportedQuestionnaireAnswer(item.item) : false);
    });
/**
 * Finds form field key for clinical impression by LOINC code or display text.
 *
 * The four generic keys (`substance-use-history`, `medical-history`,
 * `social-history`, `current-outpatient-treaters`) are no longer authored, but
 * their mappings and display patterns MUST stay: generics and subsections share
 * a LOINC and disambiguate on display, and the code-only fallback below would
 * otherwise resolve a historical generic impression to whichever subsection
 * happens to come first in the map — losing one value and binding the form's
 * impression id to the wrong resource.
 * @param impression - ClinicalImpression resource
 * @returns Form field key (kebab-case) or null if no match found
 * @agentic @benchmark/core
 */ function findClinicalImpressionFieldKey(impression) {
    const coding = impression.code?.coding?.[0];
    if (!coding) return null;
    const loincCode = coding.code;
    const displayText = coding.display?.toLowerCase() || "";
    // Match by key in CLINICAL_IMPRESSION_CODE_MAPPINGS: find the entry whose
    // LOINC code AND display text both match (needed because mental-status and
    // assessment-plan sub-fields share a parent LOINC code).
    if (loincCode) {
        const matchByCodeAndDisplay = Object.entries(CLINICAL_IMPRESSION_CODE_MAPPINGS).find(([, codeableConcept])=>codeableConcept.coding?.[0]?.code === loincCode && codeableConcept.coding?.[0]?.display?.toLowerCase() === displayText);
        if (matchByCodeAndDisplay) {
            const fieldKey = kebabCase(matchByCodeAndDisplay[0]);
            return fieldKey;
        }
        // Fallback: match by LOINC code only (unique codes)
        const matchByCode = Object.entries(CLINICAL_IMPRESSION_CODE_MAPPINGS).find(([, codeableConcept])=>codeableConcept.coding?.[0]?.code === loincCode);
        if (matchByCode) {
            const fieldKey = kebabCase(matchByCode[0]);
            return fieldKey;
        }
    }
    // Fallback to display text matching (for backwards compatibility with
    // old ClinicalImpressions that used the monolithic mental-status / assessment-plan codes)
    const displayPatterns = [
        {
            patterns: [
                "general note"
            ],
            fieldKey: "comment"
        },
        {
            patterns: [
                "chief complaint"
            ],
            fieldKey: "chief-complaint"
        },
        {
            patterns: [
                "hpi",
                "current symptoms"
            ],
            fieldKey: "hpi-current-symptoms"
        },
        {
            patterns: [
                "mental health history"
            ],
            fieldKey: "mental-health-history"
        },
        {
            patterns: [
                "mental status - appearance"
            ],
            fieldKey: "mental-status-appearance"
        },
        {
            patterns: [
                "mental status - behavior"
            ],
            fieldKey: "mental-status-behavior"
        },
        {
            patterns: [
                "mental status - speech"
            ],
            fieldKey: "mental-status-speech"
        },
        {
            patterns: [
                "mental status - mood"
            ],
            fieldKey: "mental-status-mood-affect"
        },
        {
            patterns: [
                "mental status - thought"
            ],
            fieldKey: "mental-status-thought-process-content"
        },
        {
            patterns: [
                "mental status - perception"
            ],
            fieldKey: "mental-status-perception"
        },
        {
            patterns: [
                "mental status - cognition"
            ],
            fieldKey: "mental-status-cognition"
        },
        {
            patterns: [
                "mental status - insight"
            ],
            fieldKey: "mental-status-insight-judgment"
        },
        {
            patterns: [
                "trauma history"
            ],
            fieldKey: "trauma-history"
        },
        {
            patterns: [
                "substance use"
            ],
            fieldKey: "substance-use-history"
        },
        {
            patterns: [
                "medical history"
            ],
            fieldKey: "medical-history"
        },
        {
            patterns: [
                "medication history",
                "history of medication use"
            ],
            fieldKey: "medication-history"
        },
        {
            patterns: [
                "social history"
            ],
            fieldKey: "social-history"
        },
        {
            patterns: [
                "family history"
            ],
            fieldKey: "family-history"
        },
        {
            patterns: [
                "assessment",
                "plan"
            ],
            fieldKey: "assessment-plan"
        },
        {
            patterns: [
                "current outpatient treaters"
            ],
            fieldKey: "current-outpatient-treaters"
        }
    ];
    for (const { patterns, fieldKey } of displayPatterns){
        if (patterns.some((pattern)=>displayText.includes(pattern))) {
            return fieldKey;
        }
    }
    return null;
}
/**
 * Converts FHIR encounter and related resources to EncounterFormData.
 * @param context - All resources needed for transformation
 * @returns EncounterFormData ready for form editing
 * @agentic @benchmark/core
 */ function encounterToForm(context) {
    const { encounter, patient, clinicalImpressions, questionnaireResponses, observations, careTimeObservations } = context;
    const formData = {};
    // Convert encounter basic data
    if (encounter.period?.start) {
        formData.period = {
            start: encounter.period.start,
            end: encounter.period.end
        };
    }
    // Convert encounter tags
    const methodTag = encounter.meta?.tag?.find((t)=>t.system === VALUE_SET.ENCOUNTER_METHOD);
    if (methodTag) {
        formData["encounter-method"] = methodTag.code;
    }
    const successTag = encounter.meta?.tag?.find((t)=>t.system === VALUE_SET.ENCOUNTER_SUCCESS);
    if (successTag) {
        formData["encounter-success"] = successTag.code;
    }
    const directionTag = encounter.meta?.tag?.find((t)=>t.system === VALUE_SET.ENCOUNTER_INOUT);
    if (directionTag) {
        formData["encounter-inout"] = directionTag.code;
    }
    const billableTag = encounter.meta?.tag?.find((t)=>t.system === VALUE_SET.ENCOUNTER_BILLABLE);
    if (billableTag) {
        formData["encounter-billable"] = billableTag.code;
    }
    const typeTag = encounter.meta?.tag?.find((t)=>t.system === VALUE_SET.ENCOUNTER_TYPE);
    if (typeTag) {
        formData["encounter-type"] = typeTag.code;
    }
    // Load scratch pad from encounter extension
    const scratchExtension = encounter.extension?.find((ext)=>ext.url === ENCOUNTER_SCRATCH_EXTENSION_URL);
    if (scratchExtension?.valueString) {
        formData["intake-scratch"] = scratchExtension.valueString;
    }
    // Restore the scribe Composition id from the prefill-source extension so an
    // edit/re-open save carries it into the bundle (keeping Composition.encounter
    // linked and self-healing), matching the initial prefill save path.
    const prefillSourceRef = encounter.extension?.find((ext)=>ext.url === PREFILL_SOURCE_EXTENSION_URL)?.valueReference?.reference;
    if (prefillSourceRef?.startsWith("Composition/")) {
        formData._scribeCompositionId = prefillSourceRef.slice("Composition/".length);
    }
    // Restore per-section scratch from its dedicated extension regardless of
    // status (it survives finalize). Older in-progress drafts that stored scratch
    // only in the draft-form-state extension are covered by the draft-state merge
    // below.
    const sectionScratchExtension = encounter.extension?.find((ext)=>ext.url === ENCOUNTER_SECTION_SCRATCH_EXTENSION_URL);
    if (sectionScratchExtension?.valueString) {
        try {
            const parsed = JSON.parse(sectionScratchExtension.valueString);
            if (parsed && typeof parsed === "object") {
                formData._sectionScratch = parsed;
            }
        } catch  {
        // Ignore malformed scratch JSON; treat as no scratch.
        }
    }
    // Note: Comment field is loaded from clinical impressions, not encounter extensions
    // This prevents conflicts between encounter extensions and clinical impressions
    // Initialize clinical impression IDs object
    formData._clinicalImpressionIds = {};
    // Map clinical impressions to form fields using LOINC codes or display text
    clinicalImpressions.forEach((impression)=>{
        if (!impression.summary || !impression.id) return;
        const fieldKey = findClinicalImpressionFieldKey(impression);
        if (fieldKey) {
            // Type-safe assignment to form data
            formData[fieldKey] = impression.summary;
            // Initialize IDs object if needed
            if (!formData._clinicalImpressionIds) {
                formData._clinicalImpressionIds = {};
            }
            formData._clinicalImpressionIds[fieldKey] = impression.id;
        }
    });
    // Note: Questionnaire observations are now derived from QuestionnaireResponse resources
    // No longer loading questionnaire scores into form data
    // Get patient engagement status from patient tags (production uses V1 system URL for all codes)
    const engagementTag = patient.meta?.tag?.find((t)=>t.system === PATIENT_TAG_MAPPINGS.engagementStatus.system);
    if (engagementTag) {
        formData["patient-engagement-status"] = engagementTag.code;
    }
    // Initialize questionnaire response data
    formData.selectedQuestionnaires = [];
    formData.questionnaireResponses = {};
    formData._questionnaireResponseIds = {};
    formData._observationIds = {};
    formData._questionnaireScoreConflictUrls = [];
    formData._questionnaireDuplicateScoreUrls = [];
    formData._readOnlyQuestionnaireCount = 0;
    const writableQuestionnaireResponses = [];
    for (const response of questionnaireResponses){
        const url = response.questionnaire;
        if (!url || !response.id || response.status === "entered-in-error" || response.status === "stopped" || hasUnsupportedQuestionnaireAnswer(response.item ?? [])) {
            formData._readOnlyQuestionnaireCount = (formData._readOnlyQuestionnaireCount ?? 0) + 1;
            continue;
        }
        const marker = readAdaptiveScreenerMarker(response);
        const internalResponses = flattenResponseItems(response.item ?? []);
        const completion = evaluateQuestionnaireCompletion(url, internalResponses, {
            declaredComplete: response.status === "completed"
        });
        if (!completion.supported) {
            formData._readOnlyQuestionnaireCount = (formData._readOnlyQuestionnaireCount ?? 0) + 1;
            continue;
        }
        const terminalNonAnswer = marker?.settlement !== undefined && isExplicitSettlement(marker.settlement);
        if (response.status === "completed" && !completion.complete && !terminalNonAnswer) {
            formData._readOnlyQuestionnaireCount = (formData._readOnlyQuestionnaireCount ?? 0) + 1;
            continue;
        }
        if (formData._questionnaireResponseIds[url]) {
            throw new Error("Multiple recorded responses exist for the same questionnaire. Review the assessment record before editing.");
        }
        writableQuestionnaireResponses.push(response);
        if (!marker) {
            formData.selectedQuestionnaires.push(url);
        }
        formData.questionnaireResponses[url] = {
            completed: completion.complete && !terminalNonAnswer,
            responses: internalResponses
        };
        formData._questionnaireResponseIds[url] = response.id;
    }
    const restoredAdaptive = restoreAdaptiveScreenerState(writableQuestionnaireResponses);
    if (restoredAdaptive) {
        formData._adaptiveScreenerSession = restoredAdaptive.session;
        formData._adaptiveSettlements = restoredAdaptive.settlements;
        formData._suicideRiskAcknowledged = restoredAdaptive.suicideRiskAcknowledged;
        formData._suicideRiskAcknowledgedAt = restoredAdaptive.suicideRiskAcknowledgedAt;
        formData._suicideRiskAcknowledgedBy = restoredAdaptive.suicideRiskAcknowledgedBy;
    }
    // Strict score ownership: code + expected system must map to a writable QR.
    // Any ambiguity blocks editing rather than choosing an arbitrary id or POSTing
    // another score.
    const scoreCandidates = new Map();
    for (const observation of observations){
        if (!observation.id || observation.status === "entered-in-error" || observation.status === "cancelled") {
            if (observation.id) {
                formData._readOnlyQuestionnaireCount = (formData._readOnlyQuestionnaireCount ?? 0) + 1;
            }
            continue;
        }
        const questionnaireUrl = (observation.code?.coding ?? []).map(getQuestionnaireUrlFromScoreCoding).find((url)=>Boolean(url));
        if (!questionnaireUrl || getQuestionnaireDefinition(questionnaireUrl)?.capabilities.encounterAdministrable !== true) {
            continue;
        }
        if (!formData._questionnaireResponseIds[questionnaireUrl]) {
            if (!formData._questionnaireScoreConflictUrls.includes(questionnaireUrl)) {
                formData._questionnaireScoreConflictUrls.push(questionnaireUrl);
            }
            continue;
        }
        const candidates = scoreCandidates.get(questionnaireUrl) ?? [];
        candidates.push(observation);
        scoreCandidates.set(questionnaireUrl, candidates);
    }
    for (const [url, candidates] of scoreCandidates){
        if (candidates.length > 1) {
            if (!formData._questionnaireScoreConflictUrls.includes(url)) {
                formData._questionnaireScoreConflictUrls.push(url);
            }
            formData._questionnaireDuplicateScoreUrls.push(url);
            formData._readOnlyQuestionnaireCount = (formData._readOnlyQuestionnaireCount ?? 0) + candidates.length + 1;
            continue;
        }
        formData._observationIds[url] = candidates[0].id;
    }
    // Map care-time observations back to form fields + ids so an edit/re-save
    // updates the same Observations (PUT) instead of creating duplicates. The
    // task/care-plan linkage is round-tripped too because the builder rebuilds
    // `focus`/`basedOn` from these fields (the existing-observation stub passed
    // on edit carries only resourceType + id).
    formData._careTimeObservationIds = {};
    (careTimeObservations ?? []).forEach((observation)=>{
        const careTime = careTimeToForm(observation);
        if (!careTime || !observation.id) return;
        if (careTime.taskId) {
            formData._careTimeTaskId = careTime.taskId;
            formData._careTimeTaskDisplay = careTime.taskDisplay;
        }
        if (careTime.carePlanId) {
            formData._careTimeCarePlanId = careTime.carePlanId;
            formData._careTimeCarePlanDisplay = careTime.carePlanDisplay;
        }
        if (careTime.method === "intake-call") {
            formData["care-time-call-minutes"] = careTime.minutes;
            if (careTime.notes) formData["care-time-call-notes"] = careTime.notes;
            formData._careTimeObservationIds["intake-call"] = observation.id;
        } else if (careTime.method === "intake-documentation") {
            formData["care-time-documentation-minutes"] = careTime.minutes;
            if (careTime.notes) {
                formData["care-time-documentation-notes"] = careTime.notes;
            }
            formData._careTimeObservationIds["intake-documentation"] = observation.id;
        }
    });
    // Restore non-bundled draft form state from extension (goals, enrollment, referrals, etc.)
    if (encounter.status === "in-progress") {
        const draftState = extractDraftFormState(encounter);
        Object.assign(formData, draftState);
    }
    // Multiple active scores for one unique writable QR qualify for local
    // quarantine. Preserve the response and adaptive bookkeeping for read-only
    // display and safety evaluation, but remove every writable resource id: no
    // score is chosen and neither the QR nor an Observation can be PUT by this
    // form. Broader score conflicts (for example an orphan score with no QR)
    // remain fail-closed and are never added to this duplicate-only set.
    const quarantinedUrls = new Set(formData._questionnaireDuplicateScoreUrls ?? []);
    for (const url of quarantinedUrls){
        delete formData._questionnaireResponseIds?.[url];
        delete formData._observationIds?.[url];
    }
    return formData;
}

/**
 * Fields pre-filled from Initial Clinical Intake to CoCM Intake.
 *
 * Targets the authored subsections, not the four retired generics: a generic
 * value on the source encounter has no field to land in on the new one, and
 * copying a monolithic narrative into one arbitrary subsection would misfile
 * it. @agentic @benchmark/core
 */ const PREFILL_FIELDS = [
    "mental-health-history",
    "trauma-history",
    "family-history",
    "medication-history",
    "existing-treaters",
    "outpatient-care-history",
    "substance-current-use",
    "substance-treatment-history",
    "chronic-conditions",
    "other-physical-symptoms",
    "living-situation",
    "social-supports",
    "occupation-education"
];
/**
 * Extracts pre-fill data from Initial Clinical Intake for CoCM intake forms.
 * @param context - Encounter form data context with clinical impressions
 * @returns Partial EncounterFormData with only pre-fillable fields
 * @agentic @benchmark/core
 */ function extractPrefillDataFromClinicalIntake(context) {
    const { clinicalImpressions } = context;
    const prefillData = {};
    // Map clinical impressions to form fields
    clinicalImpressions.forEach((impression)=>{
        if (!impression.summary) return;
        const fieldKey = findClinicalImpressionFieldKey(impression);
        if (!fieldKey) return;
        // Only include fields that should be pre-filled
        if (PREFILL_FIELDS.includes(fieldKey)) {
            // Type-safe assignment to form data
            prefillData[fieldKey] = impression.summary;
        }
    });
    return prefillData;
}

/**
 * Returns display name for practitioner from formatted name, parts, or ID fallback.
 * @agentic @benchmark/core
 */ const getPractitionerDisplayName = (practitioner)=>{
    // Try formatted name first
    const formattedName = formatHumanName(practitioner.name?.[0]);
    if (formattedName) return formattedName;
    // Try individual name parts
    const name = practitioner.name?.[0];
    if (name) {
        const parts = [];
        if (name.prefix?.length) parts.push(...name.prefix);
        if (name.given?.length) parts.push(...name.given);
        if (name.family) parts.push(name.family);
        if (parts.length > 0) return parts.join(" ");
    }
    // Fallback to ID-based display
    return `Practitioner ${practitioner.id}`;
};

/**
 * Converts form data to FHIR Communication resource. Pure mapping, no IO.
 * @param formData - Communication form data
 * @param patient - Patient resource (for building references)
 * @param practitioner - Practitioner resource (for sender)
 * @param practitionerRole - PractitionerRole resource (for sender)
 * @param existingCommunication - Existing communication resource (for updates)
 * @returns Communication domain model
 * @agentic @benchmark/core
 */ function communicationFromForm(formData, patient, practitioner, practitionerRole, existingCommunication) {
    // Start with existing communication or create new one
    const communication = existingCommunication ? {
        ...existingCommunication
    } : {
        resourceType: "Communication",
        status: "completed",
        priority: "routine"
    };
    // Always update references with fresh display names (even when updating)
    // This ensures display names are always correct and up-to-date
    if (!patient.id) {
        throw new Error("Patient ID is required for communication creation");
    }
    communication.subject = buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient));
    if (!practitionerRole.id) {
        throw new Error("PractitionerRole ID is required for communication creation");
    }
    communication.sender = buildPractitionerRoleReference(practitionerRole.id, getPractitionerDisplayName(practitioner));
    communication.recipient = [
        buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient))
    ];
    // Manual outreach is staff-logged correspondence with a patient. It carries
    // the `manual-outreach` interaction type (NOT `patient-message`, which is
    // reserved for the Dialpad conversational channel) so it stays out of the
    // channel-scoped SMS thread / unreplied / triage queries. Only set when
    // absent so an explicitly-set category on an existing resource is preserved.
    if (!communication.category || communication.category.length === 0) {
        communication.category = buildCommunicationTypeCategory("manual-outreach");
    }
    // Update fields from form data
    if (formData.sent) {
        communication.sent = formData.sent;
    }
    if (formData.status) {
        communication.status = formData.status;
    }
    if (formData.medium) {
        communication.medium = formData.medium;
    }
    if (formData.payload) {
        communication.payload = formData.payload;
    }
    return communication;
}

/**
 * Communication To Form Adapter
 *
 * Pure function that maps Communication domain model to CommunicationFormData.
 * No IO operations - mechanical transformation only.
 */ /**
 * Converts FHIR Communication resource to form data. Pure mapping, no IO.
 * @param communication - Communication resource
 * @returns Communication form data
 * @agentic @benchmark/core
 */ function communicationToForm(communication) {
    const formData = {
        status: communication.status
    };
    // Basic identification
    formData.id = communication.id;
    // Communication method
    formData.medium = communication.medium;
    // Timing
    formData.sent = communication.sent;
    // Message content
    formData.payload = communication.payload;
    // Follow-up information removed - will be handled via Tasks in the future
    return formData;
}

/**
 * Maps Composition sections to EncounterFormData. Pure transformation, no IO.
 * @agentic @benchmark/core
 */ /** Base URL for AICD pipeline custom FHIR extensions. @agentic @benchmark/core */ const AICD_EXTENSION_BASE = "https://benchmark.health/fhir/StructureDefinition";
/** Decodes common HTML entities to character equivalents. @agentic @benchmark/core */ const decodeHtmlEntities = (input)=>{
    // Common named entities
    const entities = {
        "&amp;": "&",
        "&lt;": "<",
        "&gt;": ">",
        "&quot;": '"',
        "&apos;": "'",
        "&nbsp;": " ",
        "&ndash;": "\u2013",
        "&mdash;": "\u2014",
        "&lsquo;": "\u2018",
        "&rsquo;": "\u2019",
        "&ldquo;": "\u201C",
        "&rdquo;": "\u201D",
        "&hellip;": "\u2026"
    };
    let result = input;
    // Replace named entities
    for (const [entity, char] of Object.entries(entities)){
        result = result.split(entity).join(char);
    }
    // Replace numeric entities (&#039; &#x27; etc.)
    result = result.replace(/&#(\d+);/g, (_, code)=>String.fromCharCode(parseInt(code, 10)));
    result = result.replace(/&#x([0-9a-fA-F]+);/g, (_, code)=>String.fromCharCode(parseInt(code, 16)));
    return result;
};
/** Converts HTML to plain text, preserving structure. @agentic @benchmark/core */ const htmlToPlainText = (input)=>{
    let out = input;
    // Add line breaks before block elements
    out = out.replace(/<(p|div|h[1-6]|br|hr)\s*\/?>/gi, "\n");
    out = out.replace(/<\/(p|div|h[1-6])>/gi, "\n");
    // Convert list items to bullet points
    out = out.replace(/<li[^>]*>/gi, "\n• ");
    out = out.replace(/<\/li>/gi, "");
    // Remove list containers (ul, ol) - we've already handled the items
    out = out.replace(/<\/?[uo]l[^>]*>/gi, "");
    // Remove remaining tags
    let prev;
    do {
        prev = out;
        out = out.replace(/<[^>]+>/g, "");
    }while (out !== prev)
    // Decode HTML entities
    out = decodeHtmlEntities(out);
    // Clean up whitespace: collapse multiple newlines, trim lines
    out = out.split("\n").map((line)=>line.trim()).filter((line, index, arr)=>{
        // Keep non-empty lines, and only one empty line in a row
        if (line) return true;
        return index > 0 && arr[index - 1] !== "";
    }).join("\n").trim();
    return out;
};
/** @deprecated Use htmlToPlainText instead. @agentic @benchmark/core */ const stripHtmlTags = htmlToPlainText;
/** Converts Date to ISO string without milliseconds. @agentic @benchmark/core */ const toIsoNoMs = (d)=>d.toISOString().replace(/\.\d{3}Z$/, "Z");
/** Normalizes ISO timestamp to millisecond-free format. @agentic @benchmark/core */ const normalizeIso = (value)=>{
    if (!value) return undefined;
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) return toIsoNoMs(parsed);
    // Some sources provide microseconds beyond 3 decimals (e.g., .000000+00:00)
    const trimmed = value.replace(/\.(\d{3})\d+/, ".$1");
    const parsedTrimmed = new Date(trimmed);
    if (!isNaN(parsedTrimmed.getTime())) return toIsoNoMs(parsedTrimmed);
    // Last resort: drop fractional seconds entirely
    const dropped = value.replace(/\.\d+/, "");
    const parsedDropped = new Date(dropped);
    return !isNaN(parsedDropped.getTime()) ? toIsoNoMs(parsedDropped) : value;
};
/**
 * Maps Composition sections to EncounterFormData fields.
 * @param composition - Composition resource
 * @param encounter - Optional encounter resource for period data (legacy fallback)
 * @returns Partial EncounterFormData with mapped fields
 * @agentic @benchmark/core
 */ /**
 * Extracts labeled sub-fields from a legacy merged Mental Status section.
 * Parses `<strong>Label:</strong> value` patterns from the raw HTML.
 * @agentic @benchmark/core
 */ const parseLegacyMentalStatus = (html, assignIf)=>{
    const fieldMap = [
        {
            pattern: /Appearance:\s*/i,
            key: "mental-status-appearance"
        },
        {
            pattern: /Behavior:\s*/i,
            key: "mental-status-behavior"
        },
        {
            pattern: /Speech:\s*/i,
            key: "mental-status-speech"
        },
        {
            pattern: /Mood\/Affect:\s*/i,
            key: "mental-status-mood-affect"
        },
        {
            pattern: /Thought Process\/Content:\s*/i,
            key: "mental-status-thought-process-content"
        },
        {
            pattern: /Perception:\s*/i,
            key: "mental-status-perception"
        },
        {
            pattern: /Cognition:\s*/i,
            key: "mental-status-cognition"
        },
        {
            pattern: /Insight\/Judgment:\s*/i,
            key: "mental-status-insight-judgment"
        }
    ];
    for (const { pattern, key } of fieldMap){
        const regex = new RegExp(`<strong>${pattern.source}</strong>\\s*([^<]+)`, "i");
        const match = html.match(regex);
        if (match?.[1]) {
            assignIf(key, decodeHtmlEntities(match[1].trim()));
        }
    }
};
/**
 * Fabricated "not_discussed" fallback prose emitted by earlier AICD composition
 * builds (now omit-when-empty). Lowercased for case-insensitive matching. Never
 * prefill these — see {@link assignIf}. @agentic @benchmark/core
 */ const LEGACY_FABRICATED_FALLBACKS = new Set([
    "chief complaint not documented.",
    "history of present illness not documented.",
    // Fabricated by pre-v3.6 builds for sections that are still mapped below.
    // The retired generics ("No substance use reported.", "No medical history
    // reported.", "No social history documented.", "No current outpatient
    // treaters reported.") need no entry: their section titles no longer have a
    // mapping branch at all, so nothing they contain can reach a form field.
    "no mental health history reported.",
    "no trauma history reported.",
    "no family history reported.",
    // Whole-section fallbacks. Each looked like note framing rather than a
    // clinical assertion, but every one of them maps to a rendered clinical
    // field: the MSE line lands in `mental-status-speech` (and satisfies the MSE
    // required-section check), the medications line in `medication-history`, and
    // the all-boilerplate Assessment and Plan in `assessment-plan`.
    "mental status not assessable on this telephonic encounter.",
    "no medications reported or discussed.",
    "reported conditions: none reported.\n\nsuspected conditions: none suspected.\n\nplan: no specific plan documented.",
    "no psychiatric or medical hospitalizations reported.",
    "no self-harm or suicidal-ideation history reported.",
    "no current or past substance use reported.",
    "no substance-use treatment history reported.",
    "no known chronic conditions reported.",
    "no other physical symptoms reported.",
    "no living situation documented.",
    "no social supports documented.",
    "no occupation or education documented.",
    "no existing treaters reported.",
    "no history of outpatient care reported."
]);
function compositionToEncounterFormData(composition, encounter) {
    const out = {};
    // 1. Period — try Composition.event[0].period first (TypeScript pipeline), fall back to encounter
    const compositionPeriod = composition.event?.[0]?.period;
    if (compositionPeriod?.start) {
        const startIso = normalizeIso(compositionPeriod.start);
        const endIso = normalizeIso(compositionPeriod.end);
        if (startIso) out.period = {
            start: startIso,
            ...endIso && {
                end: endIso
            }
        };
    }
    if (!out.period && encounter?.period?.start) {
        const startIso = normalizeIso(encounter.period.start);
        const endIso = normalizeIso(encounter.period.end);
        if (startIso) out.period = {
            start: startIso,
            ...endIso && {
                end: endIso
            }
        };
    }
    // 2. Call direction — from call-direction extension (set by TypeScript pipeline)
    const directionExt = composition.extension?.find((e)=>e.url === `${AICD_EXTENSION_BASE}/call-direction`);
    if (directionExt?.valueString) {
        out["encounter-inout"] = directionExt.valueString;
    }
    // 3. Call method — always "phone" for Dialpad-sourced Compositions
    out["encounter-method"] = "phone";
    // 3b. Structured safety-risk triage signal (R1). Drives the Safety Plan
    // prompt so a denial or non-safety distress in the `safety` prose no longer
    // trips it. Absent on pre-change Compositions → left undefined (no AI prompt).
    const safetyRiskExt = composition.extension?.find((e)=>e.url === `${AICD_EXTENSION_BASE}/safety-risk-present`);
    if (typeof safetyRiskExt?.valueBoolean === "boolean") {
        out._aiSafetyRiskPresent = safetyRiskExt.valueBoolean;
    }
    const assignIf = (key, value)=>{
        if (!value) return;
        const trimmed = value.trim();
        if (trimmed.length === 0) return;
        // Never prefill a fabricated clinical negative. Compositions generated
        // before the "omit-when-empty" change (or by any straggler path) may still
        // carry a `not_discussed` fallback like "Chief complaint not documented." or
        // "No self-harm or suicidal-ideation history reported." Importing those
        // would fabricate a clinical negative (a risk determination for `safety`,
        // D2) AND satisfy submit-time required-section validation. Drop them here so
        // the field reads empty — the source of truth for "was this addressed".
        if (LEGACY_FABRICATED_FALLBACKS.has(trimmed.toLowerCase())) return;
        out[key] = trimmed;
    };
    const sections = composition.section || [];
    for (const s of sections){
        const title = (s.title || s.code?.text || "").toLowerCase();
        const txt = s.text?.div ? stripHtmlTags(s.text.div).trim() : "";
        if (!txt) continue;
        // --- New-format individual sections (dash convention) ---
        if (title === "mental status - appearance") assignIf("mental-status-appearance", txt);
        else if (title === "mental status - behavior") assignIf("mental-status-behavior", txt);
        else if (title === "mental status - speech") assignIf("mental-status-speech", txt);
        else if (title === "mental status - mood/affect") assignIf("mental-status-mood-affect", txt);
        else if (title === "mental status - thought process/content") assignIf("mental-status-thought-process-content", txt);
        else if (title === "mental status - perception") assignIf("mental-status-perception", txt);
        else if (title === "mental status - cognition") assignIf("mental-status-cognition", txt);
        else if (title === "mental status - insight/judgment") assignIf("mental-status-insight-judgment", txt);
        else if (title.includes("mental status") && !title.includes("mental health")) {
            parseLegacyMentalStatus(s.text?.div ?? "", assignIf);
        } else if (title === "hospitalizations") assignIf("hospitalizations", txt);
        else if (title === "safety and risk history") assignIf("safety", txt);
        else if (title === "existing treaters") assignIf("existing-treaters", txt);
        else if (title === "history of outpatient care") assignIf("outpatient-care-history", txt);
        else if (title === "substance use - current or past use") assignIf("substance-current-use", txt);
        else if (title === "substance use - treatment history") assignIf("substance-treatment-history", txt);
        else if (title === "known chronic conditions") assignIf("chronic-conditions", txt);
        else if (title === "other physical symptoms") assignIf("other-physical-symptoms", txt);
        else if (title === "living situation") assignIf("living-situation", txt);
        else if (title === "social supports") assignIf("social-supports", txt);
        else if (title === "occupation and education") assignIf("occupation-education", txt);
        else if (title === "pediatric - home life") assignIf("peds-home-life", txt);
        else if (title === "pediatric - school and education") assignIf("peds-school-education", txt);
        else if (title === "pediatric - activities and strengths") assignIf("peds-activities-strengths", txt);
        else if (title === "pediatric - peers and social") assignIf("peds-peers-social", txt);
        else if (title === "pediatric - birth complications") assignIf("peds-birth-complications", txt);
        else if (title === "pediatric - developmental milestones") assignIf("peds-developmental-milestones", txt);
        else if (title === "pediatric - family history") assignIf("peds-family-history", txt);
        else if (title.includes("chief complaint")) assignIf("chief-complaint", txt);
        else if (title.includes("hpi") || title.includes("current symptoms")) assignIf("hpi-current-symptoms", txt);
        else if (title.includes("mental health history")) assignIf("mental-health-history", txt);
        else if (title.includes("trauma history")) assignIf("trauma-history", txt);
        else if (title.includes("medication") // covers "Current Medications", "Medication History", "Medication Use"
        ) assignIf("medication-history", txt);
        else if (title.includes("family history")) assignIf("family-history", txt);
        else if (title.includes("assessment") || title.includes("plan")) assignIf("assessment-plan", txt);
        else if (title.includes("general note") || title.includes("summary")) assignIf("comment", txt);
    }
    return out;
}

/**
 * Extracts ICD-10 code from Condition resource. Returns null if not found.
 * @param condition - FHIR Condition resource
 * @returns ICD10Code or null if not found
 * @agentic @benchmark/core
 */ function conditionToICD10Code(condition) {
    // Find ICD-10-CM coding in the condition's code
    const icd10Coding = condition.code?.coding?.find((coding)=>coding.system === ICD10_CM_SYSTEM);
    if (!icd10Coding?.code) {
        return null;
    }
    return {
        code: icd10Coding.code,
        display: icd10Coding.display || condition.code?.text || icd10Coding.code
    };
}
/**
 * Converts Condition array to ICD-10 codes for form. Filters non-ICD-10 conditions.
 * @param conditions - Array of FHIR Condition resources
 * @returns Array of ICD10Code objects for form use
 * @agentic @benchmark/core
 */ function conditionsToICD10Codes(conditions) {
    return conditions.map(conditionToICD10Code).filter((code)=>code !== null);
}
/**
 * Finds Condition resource by ICD-10 code.
 * @param conditions - Array of Condition resources
 * @param icd10Code - ICD-10 code to find
 * @returns Matching Condition or undefined
 * @agentic @benchmark/core
 */ function findConditionByICD10Code(conditions, icd10Code) {
    return conditions.find((condition)=>condition.code?.coding?.some((coding)=>coding.system === ICD10_CM_SYSTEM && coding.code === icd10Code));
}

/** Maps coverage type code to display string. @agentic @benchmark/core */ function coverageTypeToDisplay(code) {
    const specialtyMap = {
        EHCPOL: "Extended Healthcare",
        PUBLICPOL: "Public Healthcare",
        DENTPRG: "Dental Program",
        MENTPRG: "Mental Health Program"
    };
    return specialtyMap[code] || code;
}
/** Maps subscriber relationship code to display string. @agentic @benchmark/core */ function relationshipToDisplay(code) {
    switch(code){
        case "self":
            return "Self";
        case "spouse":
            return "Spouse";
        case "child":
            return "Child";
        case "other":
            return "Other";
        default:
            return code;
    }
}
/**
 * Converts form data to FHIR Coverage resource. Pure mapping, no IO.
 * @param formData - Coverage form data
 * @param patient - Patient resource (for building references)
 * @param existingCoverage - Existing coverage resource (for updates)
 * @returns Coverage domain model
 * @agentic @benchmark/core
 */ function coverageFromForm(formData, patient, existingCoverage) {
    // Start with existing coverage or create new one
    const coverage = existingCoverage ? {
        ...existingCoverage
    } : {
        resourceType: "Coverage",
        status: formData.status,
        meta: {
            profile: [
                PROFILE_URLS.Coverage
            ]
        }
    };
    // Update fields from form data
    coverage.status = formData.status;
    coverage.type = {
        coding: [
            {
                system: "http://terminology.hl7.org/CodeSystem/v3-ActCode",
                code: formData["coverage-type"],
                display: coverageTypeToDisplay(formData["coverage-type"])
            }
        ]
    };
    coverage.beneficiary = buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient));
    coverage.period = {
        start: formData["period-start"],
        ...formData["period-end"] && {
            end: formData["period-end"]
        }
    };
    // Build payor reference with display name if available
    if (formData["payor-display"]) {
        coverage.payor = [
            buildOrganizationReference(toOrganizationId(formData["payor-name"]), formData["payor-display"])
        ];
    } else {
        // Fallback to reference only if display not provided (for backward compatibility)
        coverage.payor = [
            {
                reference: `Organization/${formData["payor-name"]}`
            }
        ];
    }
    // Add InsurancePlan reference in Coverage.class if plan is selected
    // Preserve existing classes and add/update plan class
    if (formData["insurance-plan"]) {
        const existingClasses = coverage.class || [];
        const nonPlanClasses = existingClasses.filter((cls)=>cls.type?.coding?.[0]?.code !== "plan");
        const planClass = {
            type: {
                coding: [
                    {
                        system: "http://terminology.hl7.org/CodeSystem/coverage-class",
                        code: "plan",
                        display: "Plan"
                    }
                ]
            },
            value: formData["insurance-plan"],
            ...formData["insurance-plan-display"] && {
                name: formData["insurance-plan-display"]
            }
        };
        coverage.class = [
            ...nonPlanClasses,
            planClass
        ];
    }
    coverage.subscriberId = formData["subscriber-id"];
    if (formData["dependent-number"]) {
        coverage.dependent = formData["dependent-number"];
    }
    coverage.relationship = {
        coding: [
            {
                system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
                code: formData.relationship,
                display: relationshipToDisplay(formData.relationship)
            }
        ]
    };
    if (formData.order !== undefined) {
        coverage.order = formData.order;
    }
    return coverage;
}

/**
 * Maps Coverage domain model to CoverageFormData. Pure transformation, no IO.
 * @agentic @benchmark/core
 */ /**
 * Converts FHIR Coverage resource to form data. Pure mapping, no IO.
 * @param coverage - Coverage resource
 * @returns Coverage form data
 * @agentic @benchmark/core
 */ function coverageToForm(coverage) {
    // Extract payor name - prefer display name, fallback to reference ID, or empty string
    let payorName = "";
    if (coverage.payor?.[0]) {
        if (coverage.payor[0].display) {
            payorName = coverage.payor[0].display;
        } else if (coverage.payor[0].reference) {
            // Extract ID from reference (e.g., "Organization/org-123" -> "org-123")
            payorName = coverage.payor[0].reference.replace("Organization/", "");
        }
    }
    // Extract InsurancePlan ID and display name from Coverage.class[type=plan]
    let insurancePlanId = "";
    let insurancePlanDisplay = "";
    if (coverage.class && Array.isArray(coverage.class)) {
        const planClass = coverage.class.find((cls)=>cls.type?.coding?.[0]?.code === "plan");
        if (planClass?.value) {
            insurancePlanId = planClass.value;
        }
        if (planClass?.name) {
            insurancePlanDisplay = planClass.name;
        }
    }
    return {
        "coverage-type": coverage.type?.coding?.[0]?.code || "",
        status: coverage.status || "active",
        "period-start": coverage.period?.start?.split("T")[0] || "",
        "period-end": coverage.period?.end?.split("T")[0] || "",
        "payor-name": payorName,
        "insurance-plan": insurancePlanId,
        "insurance-plan-display": insurancePlanDisplay,
        "subscriber-id": coverage.subscriberId || "",
        "dependent-number": coverage.dependent || "",
        relationship: coverage.relationship?.coding?.[0]?.code || "self",
        note: ""
    };
}

/**
 * Per-type projection from the flat {@link EncounterBundleInput} (the shape
 * returned by `encounterToForm` for hydration) into the strict
 * {@link EncounterFormDataByType}[T] shape consumed by a typed form.
 *
 * Pure, no IO. Drops keys that are not part of the typed form's surface so
 * unrelated state cannot leak into a form whose shape doesn't model it.
 *
 * @agentic @benchmark/core
 */ const baseTyped = (flat, type)=>({
        "encounter-type": type,
        ...flat.period !== undefined && {
            period: flat.period
        },
        ...flat["encounter-method"] !== undefined && {
            "encounter-method": flat["encounter-method"]
        },
        ...flat["encounter-inout"] !== undefined && {
            "encounter-inout": flat["encounter-inout"]
        },
        ...flat["patient-engagement-status"] !== undefined && {
            "patient-engagement-status": flat["patient-engagement-status"]
        },
        ...flat["followup-assigned-to"] !== undefined && {
            "followup-assigned-to": flat["followup-assigned-to"]
        },
        ...flat["followup-assigned-to-display"] !== undefined && {
            "followup-assigned-to-display": flat["followup-assigned-to-display"]
        }
    });
const pickClinicalImpressions = (flat)=>{
    const fields = [
        "comment",
        "chief-complaint",
        "hpi-current-symptoms",
        "mental-health-history",
        "mental-status-appearance",
        "mental-status-behavior",
        "mental-status-speech",
        "mental-status-mood-affect",
        "mental-status-thought-process-content",
        "mental-status-perception",
        "mental-status-cognition",
        "mental-status-insight-judgment",
        "trauma-history",
        "substance-use-history",
        "medical-history",
        "medication-history",
        "social-history",
        "family-history",
        "assessment-plan",
        "current-outpatient-treaters",
        "hospitalizations",
        "safety",
        "existing-treaters",
        "outpatient-care-history",
        "substance-current-use",
        "substance-treatment-history",
        "chronic-conditions",
        "other-physical-symptoms",
        "living-situation",
        "social-supports",
        "occupation-education",
        "peds-home-life",
        "peds-school-education",
        "peds-activities-strengths",
        "peds-peers-social",
        "peds-birth-complications",
        "peds-developmental-milestones",
        "peds-family-history"
    ];
    const out = {};
    for (const field of fields){
        const value = flat[field];
        if (value !== undefined) out[field] = value;
    }
    if (flat._clinicalImpressionIds !== undefined) {
        out._clinicalImpressionIds = flat._clinicalImpressionIds;
    }
    return out;
};
const pickQuestionnaires = (flat)=>{
    const out = {};
    if (flat.selectedQuestionnaires !== undefined) {
        out.selectedQuestionnaires = flat.selectedQuestionnaires;
    }
    if (flat.questionnaireResponses !== undefined) {
        out.questionnaireResponses = flat.questionnaireResponses;
    }
    if (flat._questionnaireResponseIds !== undefined) {
        out._questionnaireResponseIds = flat._questionnaireResponseIds;
    }
    if (flat._observationIds !== undefined) {
        out._observationIds = flat._observationIds;
    }
    if (flat._questionnaireScoreConflictUrls !== undefined) {
        out._questionnaireScoreConflictUrls = flat._questionnaireScoreConflictUrls;
    }
    if (flat._questionnaireDuplicateScoreUrls !== undefined) {
        out._questionnaireDuplicateScoreUrls = flat._questionnaireDuplicateScoreUrls;
    }
    if (flat._readOnlyQuestionnaireCount !== undefined) {
        out._readOnlyQuestionnaireCount = flat._readOnlyQuestionnaireCount;
    }
    return out;
};
const pickScribe = (flat)=>{
    const out = {};
    if (flat["intake-scratch"] !== undefined) {
        out["intake-scratch"] = flat["intake-scratch"];
    }
    if (flat._topicChecks !== undefined) out._topicChecks = flat._topicChecks;
    if (flat._sectionScratch !== undefined) {
        out._sectionScratch = flat._sectionScratch;
    }
    if (flat._aiSafetyRiskPresent !== undefined) {
        out._aiSafetyRiskPresent = flat._aiSafetyRiskPresent;
    }
    if (flat._scribeCompositionId !== undefined) {
        out._scribeCompositionId = flat._scribeCompositionId;
    }
    if (flat._scribeEncounterId !== undefined) {
        out._scribeEncounterId = flat._scribeEncounterId;
    }
    if (flat._scribeInitialized !== undefined) {
        out._scribeInitialized = flat._scribeInitialized;
    }
    if (flat._scribeApplied !== undefined) out._scribeApplied = flat._scribeApplied;
    return out;
};
const pickCareTime = (flat)=>{
    const out = {};
    if (flat["care-time-call-minutes"] !== undefined) {
        out["care-time-call-minutes"] = flat["care-time-call-minutes"];
    }
    if (flat["care-time-call-notes"] !== undefined) {
        out["care-time-call-notes"] = flat["care-time-call-notes"];
    }
    if (flat["care-time-documentation-minutes"] !== undefined) {
        out["care-time-documentation-minutes"] = flat["care-time-documentation-minutes"];
    }
    if (flat["care-time-documentation-notes"] !== undefined) {
        out["care-time-documentation-notes"] = flat["care-time-documentation-notes"];
    }
    if (flat._careTimeObservationIds !== undefined) {
        out._careTimeObservationIds = flat._careTimeObservationIds;
    }
    if (flat._careTimeTaskId !== undefined) {
        out._careTimeTaskId = flat._careTimeTaskId;
    }
    if (flat._careTimeTaskDisplay !== undefined) {
        out._careTimeTaskDisplay = flat._careTimeTaskDisplay;
    }
    if (flat._careTimeCarePlanId !== undefined) {
        out._careTimeCarePlanId = flat._careTimeCarePlanId;
    }
    if (flat._careTimeCarePlanDisplay !== undefined) {
        out._careTimeCarePlanDisplay = flat._careTimeCarePlanDisplay;
    }
    return out;
};
const pickAdaptiveScreener = (flat)=>{
    const out = {};
    if (flat._adaptiveScreenerSession !== undefined) {
        out._adaptiveScreenerSession = flat._adaptiveScreenerSession;
    }
    if (flat._adaptiveSettlements !== undefined) {
        out._adaptiveSettlements = flat._adaptiveSettlements;
    }
    if (flat._suicideRiskAcknowledged !== undefined) {
        out._suicideRiskAcknowledged = flat._suicideRiskAcknowledged;
    }
    if (flat._suicideRiskAcknowledgedAt !== undefined) {
        out._suicideRiskAcknowledgedAt = flat._suicideRiskAcknowledgedAt;
    }
    if (flat._suicideRiskAcknowledgedBy !== undefined) {
        out._suicideRiskAcknowledgedBy = flat._suicideRiskAcknowledgedBy;
    }
    if (flat._adaptiveActive !== undefined) {
        out._adaptiveActive = flat._adaptiveActive;
    }
    if (flat._adaptiveCanRecord !== undefined) {
        out._adaptiveCanRecord = flat._adaptiveCanRecord;
    }
    return out;
};
const pickCompleteIntakeChain = (flat)=>{
    const out = {};
    if (flat["followup-enroll"] !== undefined) {
        out["followup-enroll"] = flat["followup-enroll"];
    }
    if (flat["followup-program-selection"] !== undefined) {
        out["followup-program-selection"] = flat["followup-program-selection"];
    }
    if (flat["followup-include-referrals"] !== undefined) {
        out["followup-include-referrals"] = flat["followup-include-referrals"];
    }
    if (flat["followup-service-needs"] !== undefined) {
        out["followup-service-needs"] = flat["followup-service-needs"];
    }
    if (flat["followup-requires-approval"] !== undefined) {
        out["followup-requires-approval"] = flat["followup-requires-approval"];
    }
    return out;
};
// Preserve only the General Note (`comment`) impression id when hydrating the
// note-only forms. Other impression ids are intentionally dropped: these forms
// never edit those fields, and a stray id paired with an empty value would make
// the builder delete an unrelated impression.
const pickCommentImpressionId = (flat)=>flat._clinicalImpressionIds?.comment !== undefined ? {
        _clinicalImpressionIds: {
            comment: flat._clinicalImpressionIds.comment
        }
    } : {};
const generalFromBundleInput = (flat)=>({
        ...baseTyped(flat, "general-interaction"),
        ...flat.comment !== undefined && {
            comment: flat.comment
        },
        ...pickCommentImpressionId(flat)
    });
const caseManagementFromBundleInput = (flat)=>({
        ...baseTyped(flat, "case-management-interaction"),
        ...flat.comment !== undefined && {
            comment: flat.comment
        },
        ...pickCommentImpressionId(flat)
    });
const followUpFromBundleInput = (flat, type)=>({
        ...baseTyped(flat, type),
        ...flat.comment !== undefined && {
            comment: flat.comment
        },
        ...pickCommentImpressionId(flat),
        ...flat["goal-progress"] !== undefined && {
            "goal-progress": flat["goal-progress"]
        },
        ...flat._goalProgressObservationIds !== undefined && {
            _goalProgressObservationIds: flat._goalProgressObservationIds
        },
        ...flat["goal-creation"] !== undefined && {
            "goal-creation": flat["goal-creation"]
        },
        ...pickQuestionnaires(flat),
        ...pickAdaptiveScreener(flat)
    });
const intakeFromBundleInput = (flat, type)=>({
        ...baseTyped(flat, type),
        ...flat["encounter-billable"] !== undefined && {
            "encounter-billable": flat["encounter-billable"]
        },
        ...flat["goal-creation"] !== undefined && {
            "goal-creation": flat["goal-creation"]
        },
        ...flat["followup-flags"] !== undefined && {
            "followup-flags": flat["followup-flags"]
        },
        ...pickClinicalImpressions(flat),
        ...pickQuestionnaires(flat),
        ...pickScribe(flat),
        ...pickCareTime(flat),
        ...pickCompleteIntakeChain(flat),
        ...pickAdaptiveScreener(flat)
    });
const dischargeFromBundleInput = (flat)=>({
        ...baseTyped(flat, "discharge-interaction")
    });
const FROM_BUNDLE_INPUT = {
    "general-interaction": generalFromBundleInput,
    "case-management-interaction": caseManagementFromBundleInput,
    "clinical-follow-up-interaction": (flat)=>followUpFromBundleInput(flat, "clinical-follow-up-interaction"),
    "cocm-follow-up-interaction": (flat)=>followUpFromBundleInput(flat, "cocm-follow-up-interaction"),
    "clinical-intake-interaction": (flat)=>intakeFromBundleInput(flat, "clinical-intake-interaction"),
    "cocm-intake-interaction": (flat)=>intakeFromBundleInput(flat, "cocm-intake-interaction"),
    "unbilled-intake-interaction": (flat)=>intakeFromBundleInput(flat, "unbilled-intake-interaction"),
    "unbilled-follow-up-interaction": (flat)=>followUpFromBundleInput(flat, "unbilled-follow-up-interaction"),
    "discharge-interaction": dischargeFromBundleInput
};
/**
 * Project a flat {@link EncounterBundleInput} into the typed shape for a
 * specific encounter type. Drops keys that are not part of the typed form's
 * surface; preserves bookkeeping (`_*Ids`, `_scribe*`) when present.
 *
 * @agentic @benchmark/core
 */ const fromBundleInput = (type, flat)=>FROM_BUNDLE_INPUT[type](flat);
const LEGACY_TYPE_CODES = new Set([
    "outreach",
    "initial",
    "check-in-evaluation",
    "follow-up"
]);
/**
 * Project a flat {@link EncounterBundleInput} into the legacy
 * {@link LegacyEncounterFormData} shape. Returns `undefined` when the flat
 * input does not carry a known v1 encounter-type code.
 *
 * @agentic @benchmark/core
 */ const legacyEncounterFromBundleInput = (flat)=>{
    const code = flat["encounter-type"];
    if (code === undefined || !LEGACY_TYPE_CODES.has(code)) {
        return undefined;
    }
    return {
        "encounter-type": code,
        ...flat.period !== undefined && {
            period: flat.period
        },
        ...flat["encounter-method"] !== undefined && {
            "encounter-method": flat["encounter-method"]
        },
        ...flat["encounter-inout"] !== undefined && {
            "encounter-inout": flat["encounter-inout"]
        },
        ...flat["patient-engagement-status"] !== undefined && {
            "patient-engagement-status": flat["patient-engagement-status"]
        },
        ...flat["followup-assigned-to"] !== undefined && {
            "followup-assigned-to": flat["followup-assigned-to"]
        },
        ...flat["followup-assigned-to-display"] !== undefined && {
            "followup-assigned-to-display": flat["followup-assigned-to-display"]
        },
        ...flat["encounter-billable"] !== undefined && {
            "encounter-billable": flat["encounter-billable"]
        },
        ...flat.comment !== undefined && {
            comment: flat.comment
        },
        ...pickClinicalImpressions(flat),
        ...pickQuestionnaires(flat)
    };
};

/**
 * Converts form data to domain model for builder consumption.
 * @param formData - Encounter form data
 * @param existingTags - Existing tags from encounter (for filtering)
 * @returns Domain model ready for builder consumption
 * @agentic @benchmark/core
 */ function encounterFromForm(formData, existingTags = []) {
    const tags = [];
    const extensions = [];
    // Filter out encounter-specific tags from existing tags
    // (we'll rebuild them from form data)
    const encounterTagSystems = new Set([
        VALUE_SET.ENCOUNTER_INOUT,
        VALUE_SET.ENCOUNTER_METHOD,
        VALUE_SET.ENCOUNTER_TYPE,
        VALUE_SET.ENCOUNTER_BILLABLE,
        VALUE_SET.ENCOUNTER_SUCCESS
    ]);
    const preservedTags = existingTags.filter((tag)=>!encounterTagSystems.has(tag.system || ""));
    tags.push(...preservedTags);
    // Build encounter-specific tags from form data
    if (formData["encounter-inout"]) {
        const inoutCode = formData["encounter-inout"];
        tags.push({
            system: VALUE_SET.ENCOUNTER_INOUT,
            code: inoutCode,
            display: inoutCode === "inbound" ? "Inbound" : inoutCode === "outbound" ? "Outbound" : "Unknown"
        });
    }
    if (formData["encounter-method"]) {
        const methodCode = formData["encounter-method"];
        tags.push({
            system: VALUE_SET.ENCOUNTER_METHOD,
            code: methodCode,
            display: methodCode === "phone" ? "Phone" : methodCode === "text" ? "Text" : methodCode === "video" ? "Video" : methodCode === "in-person" ? "In-Person" : "Unknown"
        });
    }
    if (formData["encounter-type"]) {
        const typeCode = formData["encounter-type"];
        tags.push({
            system: VALUE_SET.ENCOUNTER_TYPE,
            code: typeCode,
            display: getEncounterTypeDisplay(typeCode)
        });
    }
    // Entry-path provenance (encounter-origin). Callers set `_encounterOrigin`
    // only at create time; the system is deliberately NOT in the strip set above,
    // so an existing stamp is preserved on edits. The dedupe guard is defensive:
    // even if a caller passes the field on an edit, an already-stamped encounter
    // is never restamped.
    if (formData._encounterOrigin && !preservedTags.some((tag)=>tag.system === VALUE_SET.ENCOUNTER_ORIGIN)) {
        tags.push({
            system: VALUE_SET.ENCOUNTER_ORIGIN,
            code: formData._encounterOrigin,
            display: formData._encounterOrigin === "activity-completion" ? "Activity Completion" : "Free-Form"
        });
    }
    if (formData["encounter-billable"]) {
        const billableCode = formData["encounter-billable"];
        tags.push({
            system: VALUE_SET.ENCOUNTER_BILLABLE,
            code: billableCode,
            display: billableCode === "billable" ? "Billable" : billableCode === "non-billable" ? "Non-Billable" : "Unknown"
        });
    }
    if (formData["encounter-success"]) {
        const successCode = formData["encounter-success"];
        tags.push({
            system: VALUE_SET.ENCOUNTER_SUCCESS,
            code: successCode,
            display: successCode === "pending" ? "Pending" : successCode === "successful" ? "Successful" : successCode === "unsuccessful" ? "Unsuccessful" : "Unknown"
        });
    }
    // Build extensions from form data
    if (formData["intake-scratch"]) {
        extensions.push({
            url: ENCOUNTER_SCRATCH_EXTENSION_URL,
            valueString: formData["intake-scratch"]
        });
    }
    // Per-section scratch on its own extension so it survives finalize (the
    // draft-form-state extension is stripped when the encounter is finished).
    // Only non-empty sections are persisted.
    const sectionScratchEntries = Object.entries(formData._sectionScratch ?? {}).filter(([, value])=>typeof value === "string" && value.trim().length > 0);
    if (sectionScratchEntries.length > 0) {
        extensions.push({
            url: ENCOUNTER_SECTION_SCRATCH_EXTENSION_URL,
            valueString: JSON.stringify(Object.fromEntries(sectionScratchEntries))
        });
    }
    // Determine encounter class from method
    let encounterClass;
    if (formData["encounter-method"]) {
        const methodCode = formData["encounter-method"];
        encounterClass = methodCode === "in-person" ? {
            system: "http://terminology.hl7.org/CodeSystem/v3-ActCode",
            code: "FLD",
            display: "field"
        } : {
            system: "http://terminology.hl7.org/CodeSystem/v3-ActCode",
            code: "VR",
            display: "virtual"
        };
    }
    return {
        period: formData.period,
        tags,
        extensions,
        class: encounterClass,
        scribeCompositionId: formData._scribeCompositionId
    };
}

/**
 * Per-type adapters that project a typed encounter form's strict shape into
 * the flat {@link EncounterBundleInput} consumed by
 * `buildUpsertEncounterBundle`. These are pure functions.
 *
 * The transaction boundary (facade → action → builder → submitBundle) is
 * unchanged; this adapter is the only new code on the typed-form → bundle
 * direction. Each typed adapter copies exactly the keys its source shape
 * declares — never more, never less — so unrelated state cannot leak into
 * the FHIR Bundle.
 *
 * @agentic @benchmark/core
 */ const baseFlat = (values)=>({
        ...values.period !== undefined && {
            period: values.period
        },
        ...values["encounter-method"] !== undefined && {
            "encounter-method": values["encounter-method"]
        },
        ...values["encounter-inout"] !== undefined && {
            "encounter-inout": values["encounter-inout"]
        },
        ...values["patient-engagement-status"] !== undefined && {
            "patient-engagement-status": values["patient-engagement-status"]
        },
        ...values["followup-assigned-to"] !== undefined && {
            "followup-assigned-to": values["followup-assigned-to"]
        },
        ...values["followup-assigned-to-display"] !== undefined && {
            "followup-assigned-to-display": values["followup-assigned-to-display"]
        }
    });
// General Note (`comment`) id bookkeeping for the note-only forms (general,
// case management, follow-up). Scoped to `comment` so the builder PUTs the
// existing ClinicalImpression on edit instead of POSTing a duplicate.
const commentImpressionIdFlat = (values)=>values._clinicalImpressionIds?.comment !== undefined ? {
        _clinicalImpressionIds: {
            comment: values._clinicalImpressionIds.comment
        }
    } : {};
const generalToBundleInput = (values)=>({
        ...baseFlat(values),
        "encounter-type": values["encounter-type"],
        ...values.comment !== undefined && {
            comment: values.comment
        },
        ...commentImpressionIdFlat(values)
    });
const caseManagementToBundleInput = (values)=>({
        ...baseFlat(values),
        "encounter-type": values["encounter-type"],
        ...values.comment !== undefined && {
            comment: values.comment
        },
        ...commentImpressionIdFlat(values)
    });
const clinicalImpressionFlat = (values)=>{
    const flat = {};
    const fields = [
        "comment",
        "chief-complaint",
        "hpi-current-symptoms",
        "mental-health-history",
        "mental-status-appearance",
        "mental-status-behavior",
        "mental-status-speech",
        "mental-status-mood-affect",
        "mental-status-thought-process-content",
        "mental-status-perception",
        "mental-status-cognition",
        "mental-status-insight-judgment",
        "trauma-history",
        "substance-use-history",
        "medical-history",
        "medication-history",
        "social-history",
        "family-history",
        "assessment-plan",
        "current-outpatient-treaters",
        "hospitalizations",
        "safety",
        "existing-treaters",
        "outpatient-care-history",
        "substance-current-use",
        "substance-treatment-history",
        "chronic-conditions",
        "other-physical-symptoms",
        "living-situation",
        "social-supports",
        "occupation-education",
        "peds-home-life",
        "peds-school-education",
        "peds-activities-strengths",
        "peds-peers-social",
        "peds-birth-complications",
        "peds-developmental-milestones",
        "peds-family-history"
    ];
    for (const field of fields){
        const value = values[field];
        if (value !== undefined) {
            flat[field] = value;
        }
    }
    if (values._clinicalImpressionIds !== undefined) {
        flat._clinicalImpressionIds = values._clinicalImpressionIds;
    }
    return flat;
};
const questionnaireFlat = (values)=>({
        ...values.selectedQuestionnaires !== undefined && {
            selectedQuestionnaires: values.selectedQuestionnaires
        },
        ...values.questionnaireResponses !== undefined && {
            questionnaireResponses: values.questionnaireResponses
        },
        ...values._questionnaireResponseIds !== undefined && {
            _questionnaireResponseIds: values._questionnaireResponseIds
        },
        ...values._observationIds !== undefined && {
            _observationIds: values._observationIds
        },
        ...values._questionnaireScoreConflictUrls !== undefined && {
            _questionnaireScoreConflictUrls: values._questionnaireScoreConflictUrls
        },
        ...values._questionnaireDuplicateScoreUrls !== undefined && {
            _questionnaireDuplicateScoreUrls: values._questionnaireDuplicateScoreUrls
        },
        ...values._readOnlyQuestionnaireCount !== undefined && {
            _readOnlyQuestionnaireCount: values._readOnlyQuestionnaireCount
        }
    });
const scribeFlat = (values)=>({
        ...values["intake-scratch"] !== undefined && {
            "intake-scratch": values["intake-scratch"]
        },
        ...values._topicChecks !== undefined && {
            _topicChecks: values._topicChecks
        },
        ...values._sectionScratch !== undefined && {
            _sectionScratch: values._sectionScratch
        },
        ...values._aiSafetyRiskPresent !== undefined && {
            _aiSafetyRiskPresent: values._aiSafetyRiskPresent
        },
        ...values._scribeCompositionId !== undefined && {
            _scribeCompositionId: values._scribeCompositionId
        },
        ...values._scribeEncounterId !== undefined && {
            _scribeEncounterId: values._scribeEncounterId
        },
        ...values._scribeInitialized !== undefined && {
            _scribeInitialized: values._scribeInitialized
        },
        ...values._scribeApplied !== undefined && {
            _scribeApplied: values._scribeApplied
        }
    });
const careTimeFlat = (values)=>({
        ...values["care-time-call-minutes"] !== undefined && {
            "care-time-call-minutes": values["care-time-call-minutes"]
        },
        ...values["care-time-call-notes"] !== undefined && {
            "care-time-call-notes": values["care-time-call-notes"]
        },
        ...values["care-time-documentation-minutes"] !== undefined && {
            "care-time-documentation-minutes": values["care-time-documentation-minutes"]
        },
        ...values["care-time-documentation-notes"] !== undefined && {
            "care-time-documentation-notes": values["care-time-documentation-notes"]
        },
        ...values._careTimeObservationIds !== undefined && {
            _careTimeObservationIds: values._careTimeObservationIds
        },
        ...values._careTimeTaskId !== undefined && {
            _careTimeTaskId: values._careTimeTaskId
        },
        ...values._careTimeTaskDisplay !== undefined && {
            _careTimeTaskDisplay: values._careTimeTaskDisplay
        },
        ...values._careTimeCarePlanId !== undefined && {
            _careTimeCarePlanId: values._careTimeCarePlanId
        },
        ...values._careTimeCarePlanDisplay !== undefined && {
            _careTimeCarePlanDisplay: values._careTimeCarePlanDisplay
        }
    });
const completeIntakeChainFlat = (values)=>({
        ...values["followup-enroll"] !== undefined && {
            "followup-enroll": values["followup-enroll"]
        },
        ...values["followup-program-selection"] !== undefined && {
            "followup-program-selection": values["followup-program-selection"]
        },
        ...values["followup-include-referrals"] !== undefined && {
            "followup-include-referrals": values["followup-include-referrals"]
        },
        ...values["followup-service-needs"] !== undefined && {
            "followup-service-needs": values["followup-service-needs"]
        },
        ...values["followup-requires-approval"] !== undefined && {
            "followup-requires-approval": values["followup-requires-approval"]
        }
    });
const adaptiveScreenerFlat = (values)=>({
        ...values._adaptiveScreenerSession !== undefined && {
            _adaptiveScreenerSession: values._adaptiveScreenerSession
        },
        ...values._adaptiveSettlements !== undefined && {
            _adaptiveSettlements: values._adaptiveSettlements
        },
        ...values._suicideRiskAcknowledged !== undefined && {
            _suicideRiskAcknowledged: values._suicideRiskAcknowledged
        },
        ...values._suicideRiskAcknowledgedAt !== undefined && {
            _suicideRiskAcknowledgedAt: values._suicideRiskAcknowledgedAt
        },
        ...values._suicideRiskAcknowledgedBy !== undefined && {
            _suicideRiskAcknowledgedBy: values._suicideRiskAcknowledgedBy
        },
        ...values._adaptiveActive !== undefined && {
            _adaptiveActive: values._adaptiveActive
        },
        ...values._adaptiveCanRecord !== undefined && {
            _adaptiveCanRecord: values._adaptiveCanRecord
        }
    });
const intakeToBundleInput = (values)=>({
        ...baseFlat(values),
        "encounter-type": values["encounter-type"],
        ...values["encounter-billable"] !== undefined && {
            "encounter-billable": values["encounter-billable"]
        },
        ...values["goal-creation"] !== undefined && {
            "goal-creation": values["goal-creation"]
        },
        ...values["followup-flags"] !== undefined && {
            "followup-flags": values["followup-flags"]
        },
        ...clinicalImpressionFlat(values),
        ...questionnaireFlat(values),
        ...adaptiveScreenerFlat(values),
        ...scribeFlat(values),
        ...careTimeFlat(values),
        ...completeIntakeChainFlat(values)
    });
const followUpToBundleInput = (values)=>({
        ...baseFlat(values),
        "encounter-type": values["encounter-type"],
        ...values.comment !== undefined && {
            comment: values.comment
        },
        ...commentImpressionIdFlat(values),
        ...values["goal-progress"] !== undefined && {
            "goal-progress": values["goal-progress"]
        },
        ...values._goalProgressObservationIds !== undefined && {
            _goalProgressObservationIds: values._goalProgressObservationIds
        },
        ...values["goal-creation"] !== undefined && {
            "goal-creation": values["goal-creation"]
        },
        ...questionnaireFlat(values),
        ...adaptiveScreenerFlat(values)
    });
const dischargeToBundleInput = (values)=>({
        ...baseFlat(values),
        "encounter-type": values["encounter-type"]
    });
const legacyToBundleInput = (values)=>({
        ...baseFlat(values),
        "encounter-type": values["encounter-type"],
        ...values["encounter-billable"] !== undefined && {
            "encounter-billable": values["encounter-billable"]
        },
        ...values.comment !== undefined && {
            comment: values.comment
        },
        ...clinicalImpressionFlat(values)
    });
/**
 * Per-type adapter table. Use {@link toBundleInput} from external code; this
 * map exists for tests and for the dispatcher's narrowed paths.
 *
 * @agentic @benchmark/core
 */ const TO_BUNDLE_INPUT = {
    "general-interaction": generalToBundleInput,
    "case-management-interaction": caseManagementToBundleInput,
    "clinical-follow-up-interaction": followUpToBundleInput,
    "cocm-follow-up-interaction": followUpToBundleInput,
    "clinical-intake-interaction": intakeToBundleInput,
    "cocm-intake-interaction": intakeToBundleInput,
    "unbilled-intake-interaction": intakeToBundleInput,
    "unbilled-follow-up-interaction": followUpToBundleInput,
    "discharge-interaction": dischargeToBundleInput
};
/**
 * Project a typed encounter form's values into the flat
 * {@link EncounterBundleInput} consumed by `buildUpsertEncounterBundle`.
 *
 * The output contains exactly the keys the input shape declares, plus the
 * `encounter-type` discriminator. Bookkeeping (`_clinicalImpressionIds`,
 * `_questionnaireResponseIds`, `_observationIds`,
 * `_goalProgressObservationIds`, `_scribe*`) is preserved when present so
 * the builder's PUT/DELETE paths continue to operate identically.
 *
 * @agentic @benchmark/core
 */ const toBundleInput = (type, values)=>TO_BUNDLE_INPUT[type](values);
/**
 * Project legacy encounter form data into the flat
 * {@link EncounterBundleInput}. Kept separate from {@link toBundleInput}
 * because legacy types are not part of {@link EncounterFormDataByType}.
 *
 * @agentic @benchmark/core
 */ const legacyEncounterToBundleInput = (values)=>legacyToBundleInput(values);

/**
 * Maps extracted patient info to form data for pre-filling.
 * @agentic @benchmark/core
 */ const extractedInfoToForm = (extractedInfo, binaryId, binaryUrl)=>{
    const formData = {
        _referralBinaryId: binaryId,
        _referralBinaryUrl: binaryUrl,
        _extractionConfidence: extractedInfo.confidence
    };
    if (extractedInfo.patientName) {
        if (extractedInfo.patientName.given) {
            formData["first-name"] = extractedInfo.patientName.given;
        }
        if (extractedInfo.patientName.family) {
            formData["last-name"] = extractedInfo.patientName.family;
        }
    }
    if (extractedInfo.dateOfBirth) {
        formData.dob = extractedInfo.dateOfBirth;
    }
    if (extractedInfo.mrn) {
        formData.mrn = extractedInfo.mrn;
    }
    // The user will still need to search and select the matching
    // PractitionerRole from the PCP dropdown to link the formal FHIR reference
    if (extractedInfo.practice) {
        formData["referring-practice"] = extractedInfo.practice;
    }
    if (extractedInfo.referringPractitioner) {
        formData["referring-practitioner"] = extractedInfo.referringPractitioner;
    }
    // We create a single phone entry; user can add more in the form
    if (extractedInfo.phone) {
        formData.phoneNumbers = [
            {
                value: extractedInfo.phone,
                // Default to mobile since fax referrals often have cell numbers
                use: "mobile",
                rank: 1
            }
        ];
    }
    if (extractedInfo.email) {
        formData.email = extractedInfo.email;
    }
    if (extractedInfo.address) {
        if (extractedInfo.address.street) {
            formData.street = extractedInfo.address.street;
        }
        if (extractedInfo.address.city) {
            formData.city = extractedInfo.address.city;
        }
        if (extractedInfo.address.state) {
            formData.state = extractedInfo.address.state;
        }
        if (extractedInfo.address.zip) {
            formData.zip = extractedInfo.address.zip;
        }
    }
    if (extractedInfo.diagnosisCodes && extractedInfo.diagnosisCodes.length > 0) {
        formData["icd10-codes"] = extractedInfo.diagnosisCodes.map((dx)=>({
                code: dx.code,
                display: dx.display ?? dx.code
            }));
        formData["_extracted-diagnosis-codes"] = extractedInfo.diagnosisCodes.map((dx)=>dx.display ? `${dx.code} - ${dx.display}` : dx.code).join(", ");
    }
    if (extractedInfo.insurance) {
        if (extractedInfo.insurance.planName) {
            formData["primary-insurance-provider-display"] = extractedInfo.insurance.planName;
            formData["_extracted-insurance-provider"] = extractedInfo.insurance.planName;
        }
        if (extractedInfo.insurance.subscriberId) {
            formData["primary-subscriber-id"] = extractedInfo.insurance.subscriberId;
            formData["_extracted-subscriber-id"] = extractedInfo.insurance.subscriberId;
        }
    }
    // Default referral date to today (local calendar, not UTC)
    formData["referral-date"] = localCalendarDateTodayISO();
    return formData;
};
/**
 * Checks if form data has referral metadata.
 * @agentic @benchmark/core
 */ const hasReferralMetadata = (formData)=>{
    return Boolean(formData._referralBinaryId && formData._referralBinaryUrl);
};
/**
 * Extracts referral metadata from form data.
 * @agentic @benchmark/core
 */ const extractReferralMetadata = (formData)=>{
    if (!hasReferralMetadata(formData)) {
        return null;
    }
    return {
        binaryId: formData._referralBinaryId,
        binaryUrl: formData._referralBinaryUrl,
        confidence: formData._extractionConfidence
    };
};
/**
 * Strips referral metadata from form data. Returns clean `PatientFormData`.
 * @agentic @benchmark/core
 */ const stripReferralMetadata = (formData)=>{
    const { _referralBinaryId: _binaryId, _referralBinaryUrl: _binaryUrl, _extractionConfidence: _confidence, ...cleanData } = formData;
    return cleanData;
};

/**
 * Case-insensitive fuzzy match: checks if the freeform value is a substring
 * of any option label (or vice versa), or if individual words overlap.
 * @agentic @benchmark/core
 */ const fuzzyMatchOption = (freeform, options)=>{
    const normalized = freeform.toLowerCase().trim();
    if (!normalized) return null;
    // Pass 1: exact match on label or value
    for (const opt of options){
        const label = opt.label.toLowerCase();
        const value = opt.value.toLowerCase();
        if (label === normalized || value === normalized) return opt.value;
    }
    // Pass 2: substring containment (only when strings are long enough to avoid false positives)
    if (normalized.length >= 5) {
        for (const opt of options){
            const label = opt.label.toLowerCase();
            if (label.includes(normalized) || normalized.includes(label)) {
                return opt.value;
            }
        }
    }
    // Pass 3: word-overlap scoring (only meaningful multi-word inputs)
    const words = normalized.split(/[\s,/&-]+/).filter((w)=>w.length > 3);
    if (words.length === 0) return null;
    let bestMatch = null;
    let bestScore = 0;
    for (const opt of options){
        const label = opt.label.toLowerCase();
        const labelWords = label.split(/[\s,/&()-]+/).filter((w)=>w.length > 3);
        const matchCount = words.filter((w)=>labelWords.some((lw)=>lw === w || lw.includes(w) || w.includes(lw))).length;
        if (matchCount > bestScore && matchCount >= Math.ceil(words.length / 2)) {
            bestScore = matchCount;
            bestMatch = opt.value;
        }
    }
    return bestMatch;
};
const matchAll = (freeformValues, options)=>{
    const matched = new Set();
    for (const fv of freeformValues){
        const result = fuzzyMatchOption(fv, options);
        if (result) matched.add(result);
    }
    return [
        ...matched
    ];
};
const getDisplay = (value, options)=>options.find((o)=>o.value === value)?.label;
const ORGANIZATION_TYPE_OPTIONS = [
    {
        value: "prov",
        label: "Healthcare Provider"
    },
    {
        value: "cg",
        label: "Community Group"
    },
    {
        value: "private-practice",
        label: "Private Practice"
    }
];
/**
 * Maps Firecrawl extraction data to partial `HealthcareServiceFormData`.
 * @agentic @benchmark/core
 */ const extractedOrgToForm = (data)=>{
    const formValues = {};
    const fieldsPopulated = [];
    // Provider Organization (new org with name and type)
    if (data.organization_name) {
        const orgData = {
            isNew: true,
            name: data.organization_name
        };
        if (data.organization_type) {
            const matchedType = fuzzyMatchOption(data.organization_type, ORGANIZATION_TYPE_OPTIONS);
            if (matchedType) {
                orgData.type = matchedType;
            }
        }
        formValues.providerOrganization = orgData;
        fieldsPopulated.push("Organization");
    }
    if (data.category) {
        const matched = fuzzyMatchOption(data.category, SERVICE_CATEGORY_OPTIONS);
        if (matched) {
            formValues.category = matched;
            fieldsPopulated.push("Category");
        }
    }
    if (data.description) {
        formValues.description = data.description;
        fieldsPopulated.push("Description");
    }
    // Specialty: conditions_treated → treatmentCondition; therapy_modalities → treatmentModality
    const specialties = [];
    if (data.conditions_treated?.length) {
        const matched = matchAll(data.conditions_treated, SERVICE_SPECIALTY_TREATMENT_CONDITION_OPTIONS);
        for (const value of matched){
            specialties.push({
                dimension: "treatmentCondition",
                value,
                valueDisplay: getDisplay(value, SERVICE_SPECIALTY_TREATMENT_CONDITION_OPTIONS)
            });
        }
    }
    if (data.therapy_modalities?.length) {
        const matched = matchAll(data.therapy_modalities, SERVICE_SPECIALTY_TREATMENT_MODALITY_OPTIONS);
        for (const value of matched){
            specialties.push({
                dimension: "treatmentModality",
                value,
                valueDisplay: getDisplay(value, SERVICE_SPECIALTY_TREATMENT_MODALITY_OPTIONS)
            });
        }
    }
    if (specialties.length > 0) {
        formValues.specialty = specialties;
        fieldsPopulated.push("Specialties");
    }
    // Eligibility: ages_served → age; genders_served → gender
    const eligibility = [];
    if (data.ages_served?.length) {
        const matched = matchAll(data.ages_served, SERVICE_ELIGIBILITY_AGE_GROUP_OPTIONS);
        for (const value of matched){
            eligibility.push({
                dimension: "age",
                value,
                valueDisplay: getDisplay(value, SERVICE_ELIGIBILITY_AGE_GROUP_OPTIONS)
            });
        }
    }
    if (data.genders_served?.length) {
        const matched = matchAll(data.genders_served, SERVICE_ELIGIBILITY_GENDER_OPTIONS);
        for (const value of matched){
            eligibility.push({
                dimension: "gender",
                value,
                valueDisplay: getDisplay(value, SERVICE_ELIGIBILITY_GENDER_OPTIONS)
            });
        }
    }
    if (eligibility.length > 0) {
        formValues.eligibility = eligibility;
        fieldsPopulated.push("Eligibility");
    }
    // Care modality (visit mode)
    if (data.service_mode?.length) {
        const matched = matchAll(data.service_mode, SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS);
        if (matched.length > 0) {
            formValues.careModality = matched;
            fieldsPopulated.push("Care Modality");
        }
    }
    // Characteristics: access + scheduling
    const characteristics = [];
    if (data.access?.length) {
        const matched = matchAll(data.access, SERVICE_CHARACTERISTIC_ACCESS_OPTIONS);
        for (const value of matched){
            characteristics.push({
                dimension: "access",
                value,
                valueDisplay: getDisplay(value, SERVICE_CHARACTERISTIC_ACCESS_OPTIONS)
            });
        }
    }
    if (data.scheduling) {
        const schedulingValues = data.scheduling.split(/[,;&]+/).map((s)=>s.trim()).filter(Boolean);
        const matched = matchAll(schedulingValues, SERVICE_CHARACTERISTIC_SCHEDULING_OPTIONS);
        for (const value of matched){
            characteristics.push({
                dimension: "scheduling",
                value,
                valueDisplay: getDisplay(value, SERVICE_CHARACTERISTIC_SCHEDULING_OPTIONS)
            });
        }
    }
    if (characteristics.length > 0) {
        formValues.characteristic = characteristics;
        fieldsPopulated.push("Characteristics");
    }
    if (data.payment_models?.length) {
        const matched = matchAll(data.payment_models, SERVICE_PROVISION_OPTIONS);
        if (matched.length > 0) {
            formValues.serviceProvisionCode = matched;
            fieldsPopulated.push("Payment Models");
        }
    }
    const telecom = [];
    if (data.phone) {
        telecom.push({
            system: "phone",
            value: data.phone,
            use: "work"
        });
    }
    if (data.email) {
        telecom.push({
            system: "email",
            value: data.email,
            use: "work"
        });
    }
    if (telecom.length > 0) {
        formValues.telecom = telecom;
        fieldsPopulated.push("Contact Info");
    }
    const extractedLocations = data.locations?.map((l)=>l.address).filter((a)=>a !== null) ?? null;
    return {
        formValues,
        meta: {
            extractedOrgName: data.organization_name,
            extractedInsurance: data.insurance_accepted,
            extractedServices: data.services,
            extractedLocations: extractedLocations && extractedLocations.length > 0 ? extractedLocations : null,
            fieldsPopulated
        }
    };
};

/**
 * Converts form data to FHIR Flag resource. Pure mapping, no IO.
 * @param formData - Flag form data
 * @param patient - Patient resource (for building references)
 * @param practitioner - Practitioner resource (optional, for author)
 * @param practitionerRole - PractitionerRole resource (optional, for author)
 * @param existingFlag - Existing flag resource (for updates)
 * @returns Flag domain model
 * @agentic @benchmark/core
 */ function flagFromForm(formData, patient, practitioner, practitionerRole, existingFlag) {
    // Start with existing flag or create new one
    const flag = existingFlag ? {
        ...existingFlag
    } : {
        resourceType: "Flag"
    };
    // Subject (required)
    flag.subject = buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient));
    // Status (default to active if not provided)
    flag.status = formData.status || "active";
    // Category (required)
    if (formData.category) {
        flag.category = [
            {
                coding: [
                    {
                        system: FLAG_CATEGORY_SYSTEM,
                        code: formData.category,
                        display: getFlagCategoryDisplay(formData.category)
                    }
                ],
                text: getFlagCategoryDisplay(formData.category)
            }
        ];
    }
    // Code (required)
    if (formData.code) {
        const coding = getFlagCodingByCode(formData.code);
        flag.code = {
            coding: [
                {
                    system: coding.system,
                    code: formData.code,
                    display: coding.display
                }
            ],
            text: coding.display
        };
    }
    // Period
    if (formData.period?.start || formData.period?.end) {
        flag.period = {
            start: formData.period.start,
            end: formData.period.end || undefined
        };
    }
    // Author (optional) - prefer PractitionerRole if available
    if (practitioner) {
        const displayName = getPractitionerDisplayName(practitioner);
        if (practitionerRole) {
            flag.author = buildPractitionerRoleReference(practitionerRole.id, displayName);
        } else {
            flag.author = buildPractitionerReference(toPractitionerId(practitioner.id), displayName);
        }
    }
    return flag;
}

/**
 * Maps Flag domain model to FlagFormData. Pure transformation, no IO.
 * @agentic @benchmark/core
 */ /**
 * Map Flag domain model to FlagFormData
 *
 * This is a pure mapping function that converts FHIR Flag resource
 * to form data structure. No business logic or IO operations.
 *
 * @param flag - Flag resource
 * @returns Flag form data
 */ function flagToForm(flag) {
    const formData = {
        category: flag.category?.[0]?.coding?.[0]?.code || flag.category?.[0]?.text || "",
        code: flag.code?.coding?.[0]?.code || flag.code?.text || "",
        period: flag.period ? {
            start: flag.period.start,
            end: flag.period.end
        } : undefined
    };
    // Only include status if it exists
    if (flag.status) {
        formData.status = flag.status;
    }
    return formData;
}

/**
 * Converts GoalFormData into a FHIR Goal resource, handling category, priority, achievement status, and references.
 * @agentic @benchmark/core
 */ function goalFromForm(formData, patient, practitionerRole, carePlan, existingGoal) {
    // Start with existing goal or create new one
    const goal = existingGoal ? {
        ...existingGoal
    } : {
        resourceType: "Goal"
    };
    // Subject (required) - Patient reference WITH display name
    goal.subject = buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient));
    // Lifecycle status (required)
    goal.lifecycleStatus = formData.lifecycleStatus;
    // Category (required) - with proper CodeableConcept structure
    if (formData.category) {
        goal.category = [
            {
                coding: [
                    {
                        system: GOAL_CATEGORY_SYSTEM,
                        code: formData.category,
                        display: getGoalCategoryDisplay(formData.category)
                    }
                ],
                text: getGoalCategoryDisplay(formData.category)
            }
        ];
    }
    // Description (required)
    if (formData.description) {
        goal.description = {
            text: formData.description
        };
    }
    // Notes (optional) - stored as Annotation in Goal.note
    if (formData.notes && formData.notes.trim()) {
        goal.note = [
            {
                text: formData.notes.trim()
            }
        ];
    } else {
        // Clear notes if empty or not provided
        goal.note = undefined;
    }
    // Achievement status (optional)
    // Default to "in-progress" for active goals if not specified
    if (formData.achievementStatus) {
        goal.achievementStatus = {
            coding: [
                {
                    code: formData.achievementStatus,
                    display: formData.achievementStatus
                }
            ]
        };
    } else if (goal.lifecycleStatus === "active" && !existingGoal) {
        // Set default achievement status for newly created active goals
        goal.achievementStatus = {
            coding: [
                {
                    code: "in-progress",
                    display: "In Progress"
                }
            ]
        };
    }
    // Start date (optional)
    if (formData.startDate) {
        goal.startDate = formData.startDate;
    }
    // Status date (optional)
    if (formData.statusDate) {
        goal.statusDate = formData.statusDate;
    }
    // Priority (optional) - FHIR R4 Goal.priority
    if (formData.priority) {
        goal.priority = {
            coding: [
                {
                    code: formData.priority,
                    display: formData.priority === "high-priority" ? "High Priority" : formData.priority === "medium-priority" ? "Medium Priority" : "Low Priority"
                }
            ],
            text: formData.priority === "high-priority" ? "High Priority" : formData.priority === "medium-priority" ? "Medium Priority" : "Low Priority"
        };
    }
    // Expressed by (optional) - PractitionerRole reference WITH display name
    if (practitionerRole) {
        // Use practitioner display from role if available, otherwise fallback
        const displayName = practitionerRole.practitioner?.display || `PractitionerRole/${practitionerRole.id}`;
        goal.expressedBy = buildPractitionerRoleReference(practitionerRole.id, displayName);
    }
    // Addresses (optional) - CarePlan reference WITH display name
    // Note: FHIR Goal.addresses accepts various resource types, but we're using CarePlan
    if (carePlan) {
        // Use meaningful display name for CoCM care plan
        const carePlanDisplay = carePlan.title || (carePlan.category?.[0]?.coding?.[0]?.code === "cocm" ? "Collaborative Care Plan" : carePlan.category?.[0]?.coding?.[0]?.display || `CarePlan/${carePlan.id}`);
        const carePlanRef = buildReferenceWithDisplay("CarePlan", carePlan.id, carePlanDisplay);
        // Type assertion needed because Goal.addresses has a union type
        goal.addresses = [
            carePlanRef
        ];
    }
    return goal;
}

/**
 * Converts FHIR Goal resource to GoalFormData.
 * @param goal - Goal FHIR resource
 * @returns GoalFormData
 * @agentic @benchmark/core
 */ function goalToForm(goal) {
    // Extract category from first coding
    let category;
    if (goal.category?.[0]?.coding?.[0]) {
        const coding = goal.category[0].coding[0];
        if (coding.system === GOAL_CATEGORY_SYSTEM && coding.code) {
            category = coding.code;
        }
    }
    // Extract description text
    const description = goal.description?.text;
    // Extract notes from first annotation (if any)
    // Always include notes field, even if empty, to ensure form state is consistent
    const notes = goal.note?.[0]?.text || "";
    // Extract lifecycle status (handle "rejected" which is valid in FHIR but not in our type)
    const lifecycleStatus = (goal.lifecycleStatus === "rejected" ? "cancelled" : goal.lifecycleStatus) || "proposed";
    // Extract achievement status
    let achievementStatus;
    if (goal.achievementStatus?.coding?.[0]?.code) {
        achievementStatus = goal.achievementStatus.coding[0].code;
    }
    // Extract dates
    const startDate = goal.startDate;
    const statusDate = goal.statusDate;
    return {
        ...category && {
            category
        },
        ...description && {
            description
        },
        lifecycleStatus,
        ...achievementStatus && {
            achievementStatus
        },
        ...startDate && {
            startDate
        },
        ...statusDate && {
            statusDate
        },
        notes
    };
}

/**
 * Converts form data to Observation FHIR resource (BH-GoalProgress).
 * @param formData - Goal progress form data
 * @param goal - Goal resource (for focus reference)
 * @param patient - Patient resource (for subject reference)
 * @param practitionerRole - PractitionerRole resource (for performer)
 * @param existingObservation - Existing observation resource (for updates)
 * @returns Observation FHIR resource
 * @agentic @benchmark/core
 */ function goalProgressFromForm(formData, goal, patient, practitionerRole, existingObservation) {
    // Validate GAS score
    if (!isValidGASScore(formData.gasScore)) {
        throw new Error(`Invalid GAS score: ${formData.gasScore}. Must be between -2 and +2.`);
    }
    // Start with existing observation or create new one
    const observation = existingObservation ? {
        ...existingObservation
    } : {
        resourceType: "Observation"
    };
    // Status (default to final for goal progress)
    observation.status = "final";
    // Category - Survey (assessment)
    observation.category = [
        SURVEY_OBSERVATION_CATEGORY
    ];
    // Code - GAS assessment (what we're measuring)
    observation.code = {
        coding: [
            {
                system: GAS_ASSESSMENT_CODE.system,
                code: GAS_ASSESSMENT_CODE.code,
                display: GAS_ASSESSMENT_CODE.display
            }
        ],
        text: GAS_ASSESSMENT_CODE.display
    };
    // Value - GAS score as integer
    observation.valueInteger = formData.gasScore;
    // Interpretation - What the score means
    observation.interpretation = [
        {
            coding: [
                {
                    system: GAS_CODE_SYSTEM,
                    code: String(formData.gasScore),
                    display: getGASScoreDisplay(formData.gasScore)
                }
            ],
            text: getGASScoreDescription(formData.gasScore)
        }
    ];
    // Subject (required) - Patient reference WITH display name
    observation.subject = buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient));
    // Focus (required) - Goal reference WITH display name (goal description)
    const goalDisplay = goal.description?.text || `Goal/${goal.id}`;
    observation.focus = [
        buildReferenceWithDisplay("Goal", goal.id, goalDisplay)
    ];
    // Effective date time (required)
    observation.effectiveDateTime = formData.effectiveDateTime;
    // Issued timestamp (when observation was recorded)
    observation.issued = new Date().toISOString();
    // Performer (optional) - PractitionerRole reference WITH display name
    if (practitionerRole) {
        // Use practitioner display from role if available, otherwise fallback
        const displayName = practitionerRole.practitioner?.display || `PractitionerRole/${practitionerRole.id}`;
        observation.performer = [
            buildPractitionerRoleReference(practitionerRole.id, displayName)
        ];
    }
    // Note (optional)
    if (formData.note) {
        observation.note = [
            {
                text: formData.note
            }
        ];
    }
    return observation;
}

/**
 * Maps Observation FHIR resource (BH-GoalProgress) to GoalProgressFormData. Pure, no IO.
 * @agentic @benchmark/core
 */ /** Extracts goal ID from focus reference. @agentic @benchmark/core */ function extractGoalIdFromReference(reference) {
    if (!reference) {
        return undefined;
    }
    // Handle "Goal/goal-123" format
    if (reference.startsWith("Goal/")) {
        return reference.replace("Goal/", "");
    }
    // Handle "goal-123" format (assume it's a goal ID)
    return reference;
}
/**
 * Converts Observation FHIR resource (BH-GoalProgress) to GoalProgressFormData.
 * @param observation - Observation FHIR resource
 * @returns GoalProgressFormData
 * @agentic @benchmark/core
 */ function goalProgressToForm(observation) {
    // Extract goal ID from focus reference
    const goalId = extractGoalIdFromReference(observation.focus?.[0]?.reference);
    // Extract GAS score from valueInteger
    const gasScore = observation.valueInteger;
    // Extract effective date time
    const effectiveDateTime = observation.effectiveDateTime;
    // Extract note from first note entry
    const note = observation.note?.[0]?.text;
    // goalId, effectiveDateTime, and gasScore are required
    if (!goalId) {
        throw new Error("Goal progress observation must have a focus reference to a Goal");
    }
    if (!effectiveDateTime) {
        throw new Error("Goal progress observation must have an effectiveDateTime");
    }
    if (gasScore === undefined) {
        throw new Error("Goal progress observation must have a GAS score (valueInteger)");
    }
    return {
        goalId,
        effectiveDateTime,
        gasScore,
        ...note && {
            note
        }
    };
}

/** Looks up display name for a code from options array. @agentic @benchmark/core */ function getDisplayForCode$1(options, code) {
    if (!code) {
        return "";
    }
    return options.find((opt)=>opt.value === code)?.label || code;
}
/** Dimension code to display name mapping. @agentic @benchmark/core */ const DIMENSION_DISPLAYS = {
    // Specialty dimensions
    treatmentModality: "Treatment Modality",
    treatmentCondition: "Treatment Condition",
    // Eligibility dimensions
    age: "Age Group",
    gender: "Gender",
    veteranStatus: "Veteran Status",
    studentStatus: "Student Status",
    pregnancyStatus: "Pregnancy Status",
    legalStatus: "Legal Status",
    incomeBand: "Income Band",
    // Program dimensions
    payerNetwork: "Payer Network",
    stateProgram: "State Program",
    insurancePlan: "Insurance Plan",
    // Characteristic dimensions
    access: "Access",
    availability: "Availability",
    scheduling: "Scheduling",
    waitTime: "Wait Time",
    visitMode: "Visit Mode",
    partnership: "Partnership"
};
/** Gets display name for dimension. @agentic @benchmark/core */ function getDimensionDisplay(dimension) {
    return DIMENSION_DISPLAYS[dimension] || dimension;
}
/** Creates dimension + value coding for eligibility. @agentic @benchmark/core */ function createEligibilityDimensionValue(dimension, value, valueDisplay) {
    // Skip if dimension or value is empty/invalid
    if (!dimension || !value) {
        return null;
    }
    // Map dimension to dimension-specific CodeSystem
    const dimensionSystemMap = {
        age: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_AGE_GROUP,
        gender: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_GENDER,
        veteranStatus: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_VETERAN_STATUS,
        studentStatus: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_STUDENT_STATUS,
        pregnancyStatus: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_PREGNANCY_STATUS,
        legalStatus: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_LEGAL_STATUS,
        incomeBand: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_INCOME_BAND
    };
    const valueSystem = dimensionSystemMap[dimension];
    if (!valueSystem) {
        // Return null instead of throwing to handle incomplete form data gracefully
        return null;
    }
    return {
        coding: [
            {
                system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_DIMENSION,
                code: dimension,
                display: getDimensionDisplay(dimension)
            },
            {
                system: valueSystem,
                code: value,
                display: valueDisplay || value
            }
        ],
        text: valueDisplay || value
    };
}
/** Creates dimension + value coding for specialty. @agentic @benchmark/core */ function createSpecialtyDimensionValue(dimension, value, valueDisplay) {
    // Skip if dimension or value is empty/invalid
    if (!dimension || !value) {
        return null;
    }
    // Map dimension to dimension-specific CodeSystem
    const dimensionSystemMap = {
        treatmentModality: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY_TREATMENT_MODALITY,
        treatmentCondition: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY_TREATMENT_CONDITION
    };
    const valueSystem = dimensionSystemMap[dimension];
    if (!valueSystem) {
        // Return null instead of throwing to handle incomplete form data gracefully
        return null;
    }
    return {
        coding: [
            {
                system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY_DIMENSION,
                code: dimension,
                display: getDimensionDisplay(dimension)
            },
            {
                system: valueSystem,
                code: value,
                display: valueDisplay || value
            }
        ],
        text: valueDisplay || value
    };
}
/** Creates dimension + value coding for program. @agentic @benchmark/core */ function createProgramDimensionValue(dimension, value, valueDisplay) {
    // Skip if dimension or value is empty/invalid
    if (!dimension || !value) {
        return null;
    }
    // Map dimension to dimension-specific CodeSystem
    const dimensionSystemMap = {
        payerNetwork: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_PAYER_NETWORK,
        stateProgram: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_STATE,
        insurancePlan: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_INSURANCE_PLAN
    };
    const valueSystem = dimensionSystemMap[dimension];
    if (!valueSystem) {
        // Return null instead of throwing to handle incomplete form data gracefully
        return null;
    }
    return {
        coding: [
            {
                system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_DIMENSION,
                code: dimension,
                display: getDimensionDisplay(dimension)
            },
            {
                system: valueSystem,
                code: value,
                display: valueDisplay || value
            }
        ],
        text: valueDisplay || value
    };
}
/** Maps careModality value to characteristic visitMode. @agentic @benchmark/core */ function mapCareModalityToVisitMode(careModality) {
    // Find display from constants - single source of truth
    const option = SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS.find((opt)=>opt.value === careModality);
    if (option) {
        return {
            code: careModality,
            display: option.label
        };
    }
    // Legacy mapping for backward compatibility
    const legacyMapping = {
        "in-person-only": {
            code: "in-person",
            display: "In-Person"
        },
        "virtual-only": {
            code: "telehealth-video",
            display: "Telehealth (Video)"
        },
        hybrid: {
            code: "hybrid",
            display: "Hybrid"
        }
    };
    return legacyMapping[careModality] || null;
}
/**
 * Converts form data to FHIR HealthcareService resource. Pure mapping, no IO.
 * @param formData - HealthcareService form data
 * @param existingHealthcareService - Existing healthcare service resource (for updates)
 * @returns HealthcareService domain model
 * @agentic @benchmark/core
 */ function healthcareServiceFromForm(formData, existingHealthcareService) {
    // Start with existing healthcare service or create new one
    const healthcareService = existingHealthcareService ? {
        ...existingHealthcareService
    } : {
        resourceType: "HealthcareService"
    };
    // Set id if updating
    if (existingHealthcareService?.id) {
        healthcareService.id = existingHealthcareService.id;
    }
    // Basic fields
    healthcareService.active = formData.active === "true";
    healthcareService.name = formData.name;
    if (formData.description) {
        healthcareService.comment = formData.description;
    }
    // Category
    if (formData.category) {
        const categoryDisplay = getDisplayForCode$1(SERVICE_CATEGORY_OPTIONS, formData.category);
        healthcareService.category = [
            createCodeableConcept(HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CATEGORY, formData.category, categoryDisplay)
        ];
    }
    // Type
    if (formData.type) {
        const typeDisplay = getDisplayForCode$1(SERVICE_TYPE_OPTIONS, formData.type);
        healthcareService.type = [
            createCodeableConcept(HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_TYPE, formData.type, typeDisplay)
        ];
    }
    // Specialty - support both new multi-dimensional format and legacy flat format
    const specialtyList = [];
    // New multi-dimensional specialty format (preferred)
    if (formData.specialty && formData.specialty.length > 0) {
        formData.specialty.forEach((spec)=>{
            const specialtyValue = createSpecialtyDimensionValue(spec.dimension, spec.value, spec.valueDisplay);
            if (specialtyValue) {
                specialtyList.push(specialtyValue);
            }
        });
    }
    // Legacy flat format (backward compatibility)
    if (formData.specialtyLegacy && formData.specialtyLegacy.length > 0) {
        formData.specialtyLegacy.forEach((spec)=>{
            // Use the code as display (can be enhanced later with lookup)
            const display = spec;
            specialtyList.push({
                coding: [
                    {
                        system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY,
                        code: spec,
                        display: display
                    },
                    // Optionally add SNOMED mapping if it's a SNOMED code
                    ...spec.match(/^\d+$/) ? [
                        {
                            system: "http://snomed.info/sct",
                            code: spec,
                            display: display
                        }
                    ] : []
                ],
                text: display
            });
        });
    }
    if (specialtyList.length > 0) {
        healthcareService.specialty = specialtyList;
    }
    // Provided by organization
    if (formData.providedBy) {
        healthcareService.providedBy = buildOrganizationReference(toOrganizationId(formData.providedBy), formData["providedBy-display"] || `Organization/${formData.providedBy}`);
    }
    // Locations - support both new format and legacy format
    if (formData.locations && formData.locations.length > 0) {
        // New format - filter out new locations (handled by builder)
        // Only include existing location references here
        const existingLocationRefs = formData.locations.filter((loc)=>!loc.isNew && loc.existingId).map((loc)=>buildLocationReference(loc.existingId, loc.existingDisplay || `Location/${loc.existingId}`));
        if (existingLocationRefs.length > 0) {
            healthcareService.location = existingLocationRefs;
        }
    } else if (formData.location && formData.location.length > 0) {
        // Legacy format - backward compatibility
        healthcareService.location = formData.location.map((loc, index)=>buildLocationReference(loc, formData["location-displays"]?.[index] || `Location/${loc}`));
    }
    // Coverage areas — only resolved entries (those with a Location id) are
    // serialized here. Unresolved entries (form submitted before the action
    // layer ran ensureCoverageAreaLocations) are intentionally dropped; the
    // builder owns the canonical override path the same way it does for
    // locations.
    if (formData.coverageAreas && formData.coverageAreas.length > 0) {
        const coverageAreaRefs = formData.coverageAreas.flatMap((area)=>{
            const locationId = area.locationId;
            if (!locationId) {
                return [];
            }
            return [
                buildLocationReference(locationId, area.locationDisplay || getCoverageAreaLocationName(area.state))
            ];
        });
        if (coverageAreaRefs.length > 0) {
            healthcareService.coverageArea = coverageAreaRefs;
        }
    }
    // Programs - use dimension + value coding pattern
    if (formData.program && formData.program.length > 0) {
        const programValues = formData.program.map((prog)=>createProgramDimensionValue(prog.dimension, prog.value, prog.valueDisplay)).filter((p)=>p !== null);
        if (programValues.length > 0) {
            healthcareService.program = programValues;
        }
    }
    // Simplified insurances field - convert to program with insurancePlan dimension
    // This takes precedence over program field if both are provided
    if (formData.insurances && formData.insurances.length > 0) {
        const insuranceProgramValues = formData.insurances.map((insuranceId, index)=>createProgramDimensionValue("insurancePlan", insuranceId, formData["insurances-displays"]?.[index] || `Insurance/${insuranceId}`)).filter((p)=>p !== null);
        if (insuranceProgramValues.length > 0) {
            healthcareService.program = insuranceProgramValues;
        }
    }
    // Characteristic - create from careModality, partnershipType, and structured characteristic data
    const characteristics = [];
    // Visit mode from careModality (now supports multiple modalities)
    if (formData.careModality && formData.careModality.length > 0) {
        formData.careModality.forEach((modality)=>{
            const visitMode = mapCareModalityToVisitMode(modality);
            if (visitMode) {
                characteristics.push({
                    coding: [
                        {
                            system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION,
                            code: "visitMode",
                            display: "Visit Mode"
                        },
                        {
                            system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_VISIT_MODE,
                            code: visitMode.code,
                            display: visitMode.display
                        }
                    ],
                    text: visitMode.display
                });
            }
        });
    }
    // Partnership from partnershipType (legacy field)
    if (formData.partnershipType) {
        const partnershipDisplay = getDisplayForCode$1(SERVICE_CHARACTERISTIC_PARTNERSHIP_OPTIONS, formData.partnershipType);
        characteristics.push({
            coding: [
                {
                    system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION,
                    code: "partnership",
                    display: "Partnership"
                },
                {
                    system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_PARTNERSHIP,
                    code: formData.partnershipType,
                    display: partnershipDisplay
                }
            ],
            text: partnershipDisplay
        });
    }
    // Structured characteristic data (access, availability, scheduling, waitTime, etc.)
    if (formData.characteristic && formData.characteristic.length > 0) {
        formData.characteristic.forEach((char)=>{
            const dimensionSystemMap = {
                access: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_ACCESS,
                availability: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_AVAILABILITY,
                scheduling: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_SCHEDULING,
                waitTime: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_WAIT_TIME
            };
            const valueSystem = dimensionSystemMap[char.dimension];
            if (valueSystem) {
                characteristics.push({
                    coding: [
                        {
                            system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION,
                            code: char.dimension,
                            display: getDimensionDisplay(char.dimension)
                        },
                        {
                            system: valueSystem,
                            code: char.value,
                            display: char.valueDisplay || char.value
                        }
                    ],
                    text: char.valueDisplay || char.value
                });
            }
        });
    }
    // Denormalize age group eligibility into characteristic for FHIR searchability
    if (formData.eligibility) {
        formData.eligibility.filter((elig)=>elig.dimension === "age").forEach((elig)=>{
            characteristics.push({
                coding: [
                    {
                        system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION,
                        code: "ageGroup",
                        display: "Age Group"
                    },
                    {
                        system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_AGE_GROUP,
                        code: elig.value,
                        display: elig.valueDisplay || elig.value
                    }
                ],
                text: elig.valueDisplay || elig.value
            });
        });
    }
    if (characteristics.length > 0) {
        healthcareService.characteristic = characteristics;
    }
    // Meta tags
    const tags = [];
    // Keep first careModality in meta tags for backward compatibility
    if (formData.careModality && formData.careModality.length > 0) {
        tags.push({
            system: "http://benchmarkhealth.com/fhir/care-modality",
            code: formData.careModality[0]
        });
    }
    if (formData.partnershipType) {
        tags.push({
            system: "http://benchmarkhealth.com/fhir/partnership-type",
            code: formData.partnershipType
        });
    }
    if (tags.length > 0) {
        healthcareService.meta = {
            ...healthcareService.meta,
            tag: tags
        };
    }
    // Extra details
    if (formData.extraDetails) {
        healthcareService.extraDetails = formData.extraDetails;
    }
    // Service provision codes - use service-provision CodeSystem
    if (formData.serviceProvisionCode && formData.serviceProvisionCode.length > 0) {
        healthcareService.serviceProvisionCode = formData.serviceProvisionCode.map((code)=>createCodeableConcept(HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROVISION, code, getDisplayForCode$1(SERVICE_PROVISION_OPTIONS, code)));
    }
    // Eligibility - use dimension + value coding pattern
    if (formData.eligibility && formData.eligibility.length > 0) {
        const eligibilityValues = formData.eligibility.map((elig)=>{
            const code = createEligibilityDimensionValue(elig.dimension, elig.value, elig.valueDisplay);
            return code ? {
                code
            } : null;
        }).filter((e)=>e !== null);
        if (eligibilityValues.length > 0) {
            healthcareService.eligibility = eligibilityValues;
        }
    } else if (formData.eligibilityCriteria) {
        // Backward compatibility: keep old text-based eligibility
        healthcareService.eligibility = [
            {
                comment: formData.eligibilityCriteria
            }
        ];
    }
    // Telecom
    if (formData.telecom && formData.telecom.length > 0) {
        healthcareService.telecom = formData.telecom.map((contact)=>({
                system: contact.system,
                value: contact.value,
                use: contact.use
            }));
    }
    // Identifier
    if (formData.identifier && formData.identifier.length > 0) {
        healthcareService.identifier = formData.identifier;
    }
    return healthcareService;
}

/** Looks up display name for a code from options array. @agentic @benchmark/core */ function getDisplayForCode(options, code) {
    if (!code) return undefined;
    return options.find((opt)=>opt.value === code)?.label;
}
/**
 * Extracts the state code from a coverage-area Reference's display
 * text. Recognises the canonical "<State> Coverage Area" name pattern
 * emitted by `getCoverageAreaLocationName`. Returns null when the
 * display can't be mapped to a supported state.
 * @agentic @benchmark/core
 */ function inferCoverageAreaStateFromDisplay(display) {
    if (!display) {
        return null;
    }
    for (const code of SUPPORTED_COVERAGE_AREA_STATE_CODES){
        if (display === `${getCoverageAreaStateDisplay(code)} Coverage Area`) {
            return code;
        }
        if (display === getCoverageAreaStateDisplay(code)) {
            return code;
        }
    }
    return null;
}
/**
 * Maps a HealthcareService.coverageArea reference array into the
 * CoverageAreaFormData[] shape consumed by the form. References that
 * can't be mapped to a supported state are skipped (the detail view
 * still renders them via getDisplayValueOrReference).
 * @agentic @benchmark/core
 */ function extractCoverageAreas(coverageArea) {
    if (!coverageArea || coverageArea.length === 0) {
        return [];
    }
    const out = [];
    for (const ref of coverageArea){
        const reference = ref.reference ?? "";
        if (!reference.startsWith("Location/")) {
            continue;
        }
        const locationId = reference.slice("Location/".length);
        if (!locationId) {
            continue;
        }
        const stateFromDisplay = inferCoverageAreaStateFromDisplay(ref.display);
        const state = stateFromDisplay;
        if (!state || !isCoverageAreaStateCode(state)) {
            continue;
        }
        out.push({
            state,
            locationId,
            locationDisplay: ref.display
        });
    }
    return out;
}
/** Gets display name for specialty value by dimension. @agentic @benchmark/core */ function getSpecialtyDisplay(dimension, code) {
    if (dimension === "treatmentModality") {
        return getDisplayForCode(SERVICE_SPECIALTY_TREATMENT_MODALITY_OPTIONS, code);
    }
    if (dimension === "treatmentCondition") {
        return getDisplayForCode(SERVICE_SPECIALTY_TREATMENT_CONDITION_OPTIONS, code);
    }
    return undefined;
}
/**
 * Converts FHIR HealthcareService resource to form data. Pure mapping, no IO.
 * @param healthcareService - HealthcareService resource
 * @returns HealthcareService form data
 * @agentic @benchmark/core
 */ function healthcareServiceToForm(healthcareService) {
    // Extract category from category field (service-category)
    const category = healthcareService.category?.find((cat)=>cat.coding?.some((c)=>c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CATEGORY))?.coding?.find((c)=>c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CATEGORY)?.code || "";
    // Extract type from type field (service-type)
    const typeCoding = healthcareService.type?.find((t)=>t.coding?.some((c)=>c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_TYPE))?.coding?.find((c)=>c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_TYPE) || undefined;
    const type = typeCoding?.code || "";
    const typeDisplay = typeCoding?.display || getDisplayForCode(SERVICE_TYPE_OPTIONS, typeCoding?.code) || typeCoding?.code || "";
    // Extract specialty - support both new multi-dimensional format and legacy flat format
    const specialty = [];
    const specialtyLegacy = [];
    if (healthcareService.specialty && healthcareService.specialty.length > 0) {
        healthcareService.specialty.forEach((spec)=>{
            // Check for new dimension + value pattern
            const dimensionCoding = spec.coding?.find((c)=>c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY_DIMENSION);
            if (dimensionCoding?.code) {
                // New format: find the dimension-specific value coding (exclude dimension CodeSystem)
                const valueCoding = spec.coding?.find((c)=>c.system?.startsWith("https://benchmarkhealth.com/fhir/CodeSystem/service-specialty-") && c.system !== HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY_DIMENSION);
                if (valueCoding?.code) {
                    specialty.push({
                        dimension: dimensionCoding.code,
                        value: valueCoding.code,
                        valueDisplay: valueCoding.display || getSpecialtyDisplay(dimensionCoding.code, valueCoding.code) || valueCoding.code
                    });
                    return;
                }
            }
            // Legacy format: extract from old service-specialty CodeSystem
            const serviceSpecialtyCoding = spec.coding?.find((c)=>c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY);
            if (serviceSpecialtyCoding?.code) {
                specialtyLegacy.push(serviceSpecialtyCoding.code);
                return;
            }
            // Fallback to SNOMED if available (legacy)
            const snomedCoding = spec.coding?.find((c)=>c.system === "http://snomed.info/sct");
            if (snomedCoding?.code) {
                specialtyLegacy.push(snomedCoding.code);
            }
        });
    }
    // Extract careModality from characteristic field (visitMode) - now supports multiple
    const careModality = [];
    const newVisitModes = [
        "in-person",
        "telehealth-video",
        "telehealth-phone",
        "text-sms",
        "chat",
        "async-messaging",
        "digital-self-guided",
        "hybrid"
    ];
    // Legacy mapping for backward compatibility
    const visitModeToCareModality = {
        "in-person": "in-person",
        "in-person-only": "in-person",
        telehealth: "telehealth-video",
        "virtual-only": "telehealth-video",
        hybrid: "hybrid"
    };
    // Find all visitMode characteristics (supports multiple modalities)
    const visitModeCharacteristics = healthcareService.characteristic?.filter((char)=>char.coding?.some((c)=>c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_VISIT_MODE || c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION && c.code === "visitMode")) || [];
    visitModeCharacteristics.forEach((visitModeCharacteristic)=>{
        const visitModeCoding = visitModeCharacteristic.coding?.find((c)=>c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_VISIT_MODE);
        if (visitModeCoding?.code) {
            if (newVisitModes.includes(visitModeCoding.code)) {
                // New visit mode values pass through directly
                if (!careModality.includes(visitModeCoding.code)) {
                    careModality.push(visitModeCoding.code);
                }
            } else if (visitModeToCareModality[visitModeCoding.code]) {
                // Map legacy values to new format
                const mappedValue = visitModeToCareModality[visitModeCoding.code];
                if (!careModality.includes(mappedValue)) {
                    careModality.push(mappedValue);
                }
            }
        }
    });
    // Fallback to meta tags if not found in characteristic
    if (careModality.length === 0) {
        const careModalityTag = healthcareService.meta?.tag?.find((tag)=>tag.system === "http://benchmarkhealth.com/fhir/care-modality");
        if (careModalityTag?.code) {
            const mappedValue = visitModeToCareModality[careModalityTag.code] || careModalityTag.code;
            if (newVisitModes.includes(mappedValue)) {
                careModality.push(mappedValue);
            }
        }
    }
    // Extract partnership type from characteristic field (partnership) or meta tags
    let partnershipType = "";
    const partnershipCharacteristic = healthcareService.characteristic?.find((char)=>char.coding?.some((c)=>c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_PARTNERSHIP || c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION && c.code === "partnership"));
    if (partnershipCharacteristic) {
        const partnershipCoding = partnershipCharacteristic.coding?.find((c)=>c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_PARTNERSHIP);
        partnershipType = partnershipCoding?.code || "";
    }
    // Fallback to meta tags if not found in characteristic
    if (!partnershipType) {
        const partnershipTypeTag = healthcareService.meta?.tag?.find((tag)=>tag.system === "http://benchmarkhealth.com/fhir/partnership-type");
        partnershipType = partnershipTypeTag?.code || "";
    }
    // Extract characteristic data (access, availability, etc.) - exclude visitMode and partnership which are handled separately
    const characteristic = healthcareService.characteristic?.filter((char)=>{
        // Exclude visitMode and partnership (handled via careModality and partnershipType)
        const dimensionCoding = char.coding?.find((c)=>c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION);
        return dimensionCoding?.code !== "visitMode" && dimensionCoding?.code !== "partnership" && dimensionCoding?.code !== "ageGroup";
    }).map((char)=>{
        // Try dimension + value pattern first
        if (char.coding && char.coding.length >= 2) {
            const dimensionCoding = char.coding[0];
            const valueCoding = char.coding[1];
            if (dimensionCoding?.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION && dimensionCoding.code && valueCoding?.code) {
                return {
                    dimension: dimensionCoding.code,
                    value: valueCoding.code,
                    valueDisplay: valueCoding.display
                };
            }
        }
        // Fallback: try dimension-specific CodeSystem
        const accessCoding = char.coding?.find((c)=>c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_ACCESS);
        if (accessCoding?.code) {
            return {
                dimension: "access",
                value: accessCoding.code,
                valueDisplay: accessCoding.display
            };
        }
        const availabilityCoding = char.coding?.find((c)=>c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_AVAILABILITY);
        if (availabilityCoding?.code) {
            return {
                dimension: "availability",
                value: availabilityCoding.code,
                valueDisplay: availabilityCoding.display
            };
        }
        return null;
    }).filter((c)=>c !== null) || [];
    // Extract eligibility from eligibility array (dimension + value)
    const eligibility = healthcareService.eligibility?.map((elig)=>{
        const codeableConcept = elig.code || elig.code;
        if (!codeableConcept?.coding || codeableConcept.coding.length < 2) {
            return null;
        }
        const dimensionCoding = codeableConcept.coding[0];
        const valueCoding = codeableConcept.coding[1];
        if (dimensionCoding?.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_DIMENSION && dimensionCoding.code && valueCoding?.code) {
            return {
                dimension: dimensionCoding.code,
                value: valueCoding.code,
                valueDisplay: valueCoding.display
            };
        }
        return null;
    }).filter((e)=>e !== null) || [];
    // Extract eligibilityCriteria from old format (backward compatibility)
    const eligibilityCriteria = healthcareService.eligibility?.[0]?.comment || "";
    // Helper function to infer program dimension from CodeSystem
    const inferProgramDimension = (system)=>{
        if (!system) return null;
        if (system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_PAYER_NETWORK) {
            return "payerNetwork";
        }
        if (system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_STATE) {
            return "stateProgram";
        }
        if (system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_INSURANCE_PLAN) {
            return "insurancePlan";
        }
        return null;
    };
    // Extract program from program array (dimension + value, with legacy fallback)
    const program = healthcareService.program?.map((prog)=>{
        // Try new dimension + value pattern first
        if (prog.coding && prog.coding.length >= 2) {
            const dimensionCoding = prog.coding[0];
            const valueCoding = prog.coding[1];
            if (dimensionCoding?.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_DIMENSION && dimensionCoding.code && valueCoding?.code) {
                return {
                    dimension: dimensionCoding.code,
                    value: valueCoding.code,
                    valueDisplay: valueCoding.display
                };
            }
        }
        // Fallback: legacy single-coding format
        const legacyCoding = prog.coding?.[0];
        if (legacyCoding?.code) {
            const inferredDimension = inferProgramDimension(legacyCoding.system);
            // If we can infer the dimension, use it; otherwise use unknown
            return {
                dimension: inferredDimension || "unknown",
                value: legacyCoding.code,
                valueDisplay: legacyCoding.display
            };
        }
        return null;
    }).filter((p)=>p !== null) || [];
    // Extract insurances from program array (insurancePlan dimension only)
    const insurances = program.filter((p)=>p.dimension === "insurancePlan").map((p)=>p.value);
    // Extract serviceProvisionCode values from serviceProvisionCode array
    const serviceProvisionCode = (healthcareService.serviceProvisionCode || []).flatMap((spc)=>{
        const matchingCoding = spc.coding?.find((c)=>c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROVISION);
        if (matchingCoding?.code) {
            return [
                matchingCoding.code
            ];
        }
        const legacyCoding = spc.coding?.[0];
        return legacyCoding?.code ? [
            legacyCoding.code
        ] : [];
    });
    return {
        name: healthcareService.name || "",
        description: healthcareService.comment || "",
        category,
        type,
        "type-display": typeDisplay,
        specialty: specialty.length > 0 ? specialty : undefined,
        specialtyLegacy: specialtyLegacy.length > 0 ? specialtyLegacy : undefined,
        careModality,
        partnershipType,
        providedBy: healthcareService.providedBy?.reference?.replace("Organization/", "") || "",
        "providedBy-display": healthcareService.providedBy?.display || "",
        // Convert providedBy to new providerOrganization format
        // Always return a valid object (not undefined) so Mantine form can update nested fields
        providerOrganization: healthcareService.providedBy?.reference ? {
            isNew: false,
            existingId: healthcareService.providedBy.reference.replace("Organization/", ""),
            existingDisplay: healthcareService.providedBy.display || ""
        } : {
            isNew: false
        },
        location: healthcareService.location?.map((loc)=>loc.reference?.replace("Location/", "") || "").filter(Boolean) || [],
        "location-displays": healthcareService.location?.map((loc)=>loc.display || "") || [],
        // Convert location references to new format
        locations: (healthcareService.location || []).map((ref)=>{
            const locationId = ref.reference?.replace("Location/", "") || "";
            return {
                isNew: false,
                existingId: locationId,
                existingDisplay: ref.display || `Location/${locationId}`
            };
        }),
        coverageAreas: extractCoverageAreas(healthcareService.coverageArea),
        eligibility,
        eligibilityCriteria,
        program,
        insurances,
        active: healthcareService.active ? "true" : "false",
        comment: "",
        extraDetails: healthcareService.extraDetails || "",
        serviceProvisionCode,
        characteristic: characteristic.length > 0 ? characteristic : undefined,
        telecom: healthcareService.telecom?.map((contact)=>({
                system: contact.system || "",
                value: contact.value || "",
                use: contact.use || ""
            })) || [],
        identifier: healthcareService.identifier || []
    };
}

/**
 * Builds the individual-pronouns extension for Patient.extension (trimmed free text in value slice).
 * @agentic @benchmark/core
 */ const buildIndividualPronounsExtension = (pronouns)=>{
    const text = pronouns.trim();
    return {
        url: INDIVIDUAL_PRONOUNS_EXTENSION_URL,
        extension: [
            {
                url: "value",
                valueCodeableConcept: {
                    text
                }
            }
        ]
    };
};
/**
 * Reads pronouns display text from Patient if the individual-pronouns extension is present.
 * @agentic @benchmark/core
 */ const extractIndividualPronounsFromPatient = (patient)=>{
    const root = patient.extension?.find((e)=>e.url === INDIVIDUAL_PRONOUNS_EXTENSION_URL);
    const valueSlice = root?.extension?.find((e)=>e.url === "value");
    const concept = valueSlice?.valueCodeableConcept;
    if (concept?.text?.trim()) {
        return concept.text.trim();
    }
    const display = concept?.coding?.[0]?.display?.trim();
    if (display) {
        return display;
    }
    const code = concept?.coding?.[0]?.code?.trim();
    return code || undefined;
};

/**
 * Library (clinical content management) domain constants.
 *
 * Models the FHIR R4 `Library` resource as the persistence container for
 * editable clinical documentation (Clinical SOPs, Conversation Guides). One
 * `Library` represents one document; the markdown body is stored as a single
 * embedded `content` Attachment. Provides the canonical system URLs, type /
 * topic / category codings, the document-category configuration map, and slug /
 * name helpers used by the builder and adapters.
 *
 * @agentic @benchmark/core
 */ /** Canonical URL prefix for Benchmark `Library` resources. @agentic @benchmark/core */ const LIBRARY_CANONICAL_URL_BASE = "https://benchmark.health/fhir/Library";
/** Business identifier system for `Library` resources. @agentic @benchmark/core */ const LIBRARY_IDENTIFIER_SYSTEM = "https://benchmark.health/library-id";
/** Benchmark document-category code system (carried in `useContext.value`). @agentic @benchmark/core */ const LIBRARY_CATEGORY_SYSTEM = "https://benchmark.health/library-category";
/** Benchmark usage-context-type system for the document-category context. @agentic @benchmark/core */ const LIBRARY_USE_CONTEXT_TYPE_SYSTEM = "https://benchmark.health/library-context-type";
/** `useContext.code` value marking the document-category context entry. @agentic @benchmark/core */ const LIBRARY_USE_CONTEXT_CATEGORY_CODE = "document-category";
/** HL7 R4 `library-type` CodeSystem URL (`Library.type`). @agentic @benchmark/core */ const LIBRARY_TYPE_SYSTEM = "http://terminology.hl7.org/CodeSystem/library-type";
/** HL7 R4 `definition-topic` CodeSystem URL (`Library.topic`). @agentic @benchmark/core */ const LIBRARY_TOPIC_SYSTEM = "http://terminology.hl7.org/CodeSystem/definition-topic";
/** Content type for the single embedded markdown body Attachment. @agentic @benchmark/core */ const LIBRARY_MARKDOWN_CONTENT_TYPE = "text/markdown";
/** `library-type` codings used per document category. @agentic @benchmark/core */ const LIBRARY_TYPE_CODINGS = {
    "logic-library": {
        system: LIBRARY_TYPE_SYSTEM,
        code: "logic-library",
        display: "Logic Library"
    },
    "asset-collection": {
        system: LIBRARY_TYPE_SYSTEM,
        code: "asset-collection",
        display: "Asset Collection"
    }
};
/** `definition-topic` coding options selectable on the authoring form. @agentic @benchmark/core */ const LIBRARY_TOPIC_OPTIONS = [
    {
        code: "treatment",
        display: "Treatment"
    },
    {
        code: "education",
        display: "Education"
    },
    {
        code: "assessment",
        display: "Assessment"
    }
];
/**
 * Per-category FHIR coding configuration. `clinical-sop` is locked to
 * `type = logic-library` and `topic = treatment` (D10).
 * @agentic @benchmark/core
 */ const LIBRARY_CATEGORY_CONFIG = {
    "clinical-sop": {
        typeCoding: LIBRARY_TYPE_CODINGS["logic-library"],
        defaultTopicCodes: [
            "treatment"
        ],
        categoryCoding: {
            system: LIBRARY_CATEGORY_SYSTEM,
            code: "clinical-sop",
            display: "Clinical SOP"
        }
    },
    "conversation-guide": {
        typeCoding: LIBRARY_TYPE_CODINGS["asset-collection"],
        defaultTopicCodes: [],
        categoryCoding: {
            system: LIBRARY_CATEGORY_SYSTEM,
            code: "conversation-guide",
            display: "Conversation Guide"
        }
    }
};
/**
 * Type guard narrowing an arbitrary string to a known document category.
 * @agentic @benchmark/core
 */ function isLibraryDocumentCategory(value) {
    return value === "clinical-sop" || value === "conversation-guide";
}
/** Returns the FHIR coding configuration for a document category. @agentic @benchmark/core */ function getLibraryCategoryConfig(category) {
    return LIBRARY_CATEGORY_CONFIG[category];
}
/** Returns the display label for a `definition-topic` code. @agentic @benchmark/core */ function getLibraryTopicDisplay(code) {
    return LIBRARY_TOPIC_OPTIONS.find((o)=>o.code === code)?.display ?? code;
}
/**
 * Builds a Benchmark `Coding` for a `definition-topic` code, preserving the
 * known display where one exists.
 * @agentic @benchmark/core
 */ function buildLibraryTopicCoding(code) {
    return {
        system: LIBRARY_TOPIC_SYSTEM,
        code,
        display: getLibraryTopicDisplay(code)
    };
}
/**
 * Slugifies a title into a URL-safe stem (lowercase, hyphen-separated,
 * alphanumeric). Empty/symbol-only titles fall back to `document`.
 * @agentic @benchmark/core
 */ function slugifyLibraryTitle(title) {
    const stem = title.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-")// Runs of non-alphanumerics are already collapsed to a single "-" above,
    // so trimming a single leading/trailing hyphen is sufficient. A
    // quantifier-free pattern avoids the polynomial-ReDoS backtracking that
    // `/^-+|-+$/` exhibits on uncontrolled input (CodeQL js/polynomial-redos).
    .replace(/^-|-$/g, "").slice(0, 64);
    return stem.length > 0 ? stem : "document";
}
/**
 * Generates a short, lowercase alphanumeric suffix for slug uniqueness.
 * @agentic @benchmark/core
 */ function generateLibrarySlugSuffix(length = 6) {
    const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
    let out = "";
    for(let i = 0; i < length; i++){
        out += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    return out;
}
/**
 * Builds a unique slug for a document from its title plus a random suffix.
 * Pass an explicit `suffix` for deterministic output (e.g. in tests).
 * @agentic @benchmark/core
 */ function buildLibrarySlug(title, suffix) {
    return `${slugifyLibraryTitle(title)}-${generateLibrarySlugSuffix()}`;
}
/** Builds the canonical `Library.url` for a slug. @agentic @benchmark/core */ function buildLibraryCanonicalUrl(slug) {
    return `${LIBRARY_CANONICAL_URL_BASE}/${slug}`;
}
/**
 * Builds a PascalCase machine `name` from a title (FHIR `Library.name`).
 * Falls back to `Document` for empty/symbol-only titles.
 * @agentic @benchmark/core
 */ function buildLibraryName(title) {
    const name = title.replace(/[^a-zA-Z0-9 ]+/g, " ").split(/\s+/).filter(Boolean).map((word)=>word.charAt(0).toUpperCase() + word.slice(1)).join("").slice(0, 64);
    return name.length > 0 ? name : "Document";
}

/** Encodes a UTF-8 string to base64 (browser + Node safe). @agentic @benchmark/core */ function encodeBase64Utf8(value) {
    const bytes = new TextEncoder().encode(value);
    let binary = "";
    for (const byte of bytes){
        binary += String.fromCharCode(byte);
    }
    return btoa(binary);
}
/** Decodes a base64 string to UTF-8 (browser + Node safe). @agentic @benchmark/core */ function decodeBase64Utf8(data) {
    const binary = atob(data);
    const bytes = Uint8Array.from(binary, (char)=>char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
}
/**
 * Builds the single embedded markdown `content` Attachment for a document.
 * @agentic @benchmark/core
 */ function encodeMarkdownAttachment(markdown, title) {
    return {
        contentType: LIBRARY_MARKDOWN_CONTENT_TYPE,
        data: encodeBase64Utf8(markdown),
        title
    };
}
/**
 * Decodes the markdown body from a `Library`'s embedded content. Prefers the
 * `text/markdown` attachment, falling back to the first attachment carrying
 * inline `data`. Returns an empty string when no embedded content exists.
 * @agentic @benchmark/core
 */ function decodeMarkdownAttachment(library) {
    const content = library.content ?? [];
    const markdownEntry = content.find((attachment)=>attachment.contentType === LIBRARY_MARKDOWN_CONTENT_TYPE && typeof attachment.data === "string") ?? content.find((attachment)=>typeof attachment.data === "string");
    if (!markdownEntry?.data) {
        return "";
    }
    return decodeBase64Utf8(markdownEntry.data);
}

/** Extracts the email from a `ContactDetail`'s telecom list. @agentic @benchmark/core */ function getContactDetailEmail(contact) {
    return contact.telecom?.find((telecom)=>telecom.system === "email" && !!telecom.value)?.value;
}

/** Stable identity key for de-duplicating stakeholders (email, else name). */ function stakeholderKey(contact) {
    return (getContactDetailEmail(contact) ?? contact.name ?? "").toLowerCase();
}
/** Appends a stakeholder unless an entry with the same identity already exists. */ function appendStakeholder(list, actor) {
    const existing = list ?? [];
    const key = stakeholderKey(actor);
    if (key && existing.some((contact)=>stakeholderKey(contact) === key)) {
        return existing;
    }
    return [
        ...existing,
        actor
    ];
}
/** Increments an integer-as-string version, defaulting to 1. */ function bumpVersion(version) {
    const parsed = parseInt(version ?? "", 10);
    const base = Number.isFinite(parsed) ? parsed : 0;
    return String(base + 1);
}
/** YYYY-MM-DD for FHIR `date`-typed fields. */ function toDateOnly(now) {
    return now.toISOString().slice(0, 10);
}
/** Builds the document-category `useContext` entry (P4). */ function buildCategoryUseContext(categoryCoding) {
    return {
        code: {
            system: LIBRARY_USE_CONTEXT_TYPE_SYSTEM,
            code: LIBRARY_USE_CONTEXT_CATEGORY_CODE
        },
        valueCodeableConcept: {
            coding: categoryCoding
        }
    };
}
/** Maps form topic codes (falling back to category defaults) to `Library.topic`. */ function buildTopics(formData) {
    const config = getLibraryCategoryConfig(formData.category);
    const codes = formData.topicCodes.length > 0 ? formData.topicCodes : [
        ...config.defaultTopicCodes
    ];
    return codes.map((code)=>({
            coding: [
                buildLibraryTopicCoding(code)
            ]
        }));
}
/** Applies create/edit form content + coding to a draft resource shell. */ function applyFormContent(library, formData) {
    const config = getLibraryCategoryConfig(formData.category);
    library.title = formData.title;
    library.type = {
        coding: [
            config.typeCoding
        ]
    };
    library.topic = buildTopics(formData);
    library.useContext = [
        buildCategoryUseContext([
            config.categoryCoding
        ])
    ];
    library.content = [
        encodeMarkdownAttachment(formData.markdownBody, formData.title)
    ];
}
/**
 * Builds or transitions a `Library` resource for the given action.
 * @throws Error when required `formData`/`existing` inputs are missing.
 * @agentic @benchmark/core
 */ function buildLibraryResource(input) {
    const { action, actor, formData, existing } = input;
    const now = input.now ?? new Date();
    const nowIso = now.toISOString();
    if (action === "create") {
        if (!formData) {
            throw new Error("formData is required to create a Library");
        }
        const slug = buildLibrarySlug(formData.title);
        const config = getLibraryCategoryConfig(formData.category);
        const library = {
            resourceType: "Library",
            url: buildLibraryCanonicalUrl(slug),
            identifier: [
                {
                    system: LIBRARY_IDENTIFIER_SYSTEM,
                    value: slug
                }
            ],
            version: "1",
            name: buildLibraryName(formData.title),
            status: "draft",
            date: nowIso,
            type: {
                coding: [
                    config.typeCoding
                ]
            },
            author: [
                actor
            ],
            title: formData.title
        };
        applyFormContent(library, formData);
        return library;
    }
    if (!existing) {
        throw new Error(`existing Library is required to ${action}`);
    }
    const library = {
        ...existing,
        date: nowIso
    };
    switch(action){
        case "edit":
            {
                if (!formData) {
                    throw new Error("formData is required to edit a Library");
                }
                applyFormContent(library, formData);
                // Re-opening an active document for revision returns it to draft (P6).
                if (existing.status === "active") {
                    library.status = "draft";
                }
                library.editor = appendStakeholder(existing.editor, actor);
                break;
            }
        case "review":
            {
                library.reviewer = appendStakeholder(existing.reviewer, actor);
                library.lastReviewDate = toDateOnly(now);
                break;
            }
        case "endorse":
            {
                library.endorser = appendStakeholder(existing.endorser, actor);
                library.approvalDate = toDateOnly(now);
                library.status = "active";
                library.version = bumpVersion(existing.version);
                break;
            }
        case "retire":
            {
                library.status = "retired";
                break;
            }
    }
    return library;
}

/**
 * Converts authoring form data to a `Library` resource. Passing `existing`
 * produces an edit (preserving url/identifier/name); omitting it produces a
 * new draft.
 * @agentic @benchmark/core
 */ function libraryFromForm(formData, actor, existing) {
    return buildLibraryResource({
        action: existing ? "edit" : "create",
        actor,
        formData,
        existing
    });
}

/**
 * Returns the document category from a `Library`'s `useContext`, or undefined
 * for legacy/hand-authored resources without the category context.
 * @agentic @benchmark/core
 */ function getLibraryDocumentCategory(library) {
    for (const context of library.useContext ?? []){
        for (const coding of context.valueCodeableConcept?.coding ?? []){
            if (coding.system === LIBRARY_CATEGORY_SYSTEM && isLibraryDocumentCategory(coding.code)) {
                return coding.code;
            }
        }
    }
    return undefined;
}
/** Returns the `definition-topic` codes on a `Library`. @agentic @benchmark/core */ function getLibraryTopicCodes(library) {
    const codes = [];
    for (const topic of library.topic ?? []){
        for (const coding of topic.coding ?? []){
            if (coding.system === LIBRARY_TOPIC_SYSTEM && coding.code) {
                codes.push(coding.code);
            }
        }
    }
    return codes;
}

/**
 * Converts a `Library` resource into editable form data.
 * @agentic @benchmark/core
 */ function libraryToForm(library) {
    return {
        category: getLibraryDocumentCategory(library) ?? "clinical-sop",
        title: library.title ?? "",
        topicCodes: getLibraryTopicCodes(library),
        markdownBody: decodeMarkdownAttachment(library)
    };
}

/**
 * Maps LocationFormData to Location FHIR resource. Pure transformation, no IO. Used for provider/clinic locations.
 * @agentic @benchmark/core
 */ /**
 * Converts form data to FHIR Location resource.
 * @agentic @benchmark/core
 */ function locationFromForm(formData, managingOrganizationId, managingOrganizationDisplay) {
    const location = {
        resourceType: "Location",
        status: "active",
        mode: "instance"
    };
    // Name
    if (formData.name) {
        location.name = formData.name;
    }
    // Address
    if (formData.street || formData.city || formData.state || formData.zip) {
        const lines = [];
        if (formData.street) {
            lines.push(formData.street);
        }
        if (formData.line2) {
            lines.push(formData.line2);
        }
        location.address = {
            use: "work",
            type: "physical",
            line: lines.length > 0 ? lines : undefined,
            city: formData.city,
            state: formData.state,
            postalCode: formData.zip
        };
    }
    // Position (geographic coordinates)
    if (typeof formData.latitude === "number" && typeof formData.longitude === "number") {
        location.position = {
            latitude: formData.latitude,
            longitude: formData.longitude
        };
    }
    // Phone
    if (formData.phone) {
        location.telecom = [
            {
                system: "phone",
                value: formData.phone,
                use: "work"
            }
        ];
    }
    // Managing Organization (from HealthcareService.providedBy)
    if (managingOrganizationId) {
        location.managingOrganization = {
            reference: `Organization/${managingOrganizationId}`,
            display: managingOrganizationDisplay || `Organization/${managingOrganizationId}`
        };
    }
    return location;
}
/**
 * Generates display name for Location from form data.
 * @agentic @benchmark/core
 */ function getLocationDisplayFromForm(formData) {
    if (formData.existingDisplay) {
        return formData.existingDisplay;
    }
    const parts = [];
    if (formData.name) parts.push(formData.name);
    if (formData.city && formData.state) {
        parts.push(`${formData.city}, ${formData.state}`);
    } else if (formData.city) {
        parts.push(formData.city);
    } else if (formData.state) {
        parts.push(formData.state);
    }
    return parts.join(" - ") || "New Location";
}

/**
 * Extracts Mirah Patient ID from Patient resource identifiers.
 * @agentic @benchmark/core
 */ function getMirahPatientId(patient) {
    const mirahIdentifier = patient.identifier?.find((id)=>id.system === MIRAH_PATIENT_ID_SYSTEM);
    return mirahIdentifier?.value || null;
}
/**
 * Extracts Mirah instance ID from Patient resource identifiers.
 * @agentic @benchmark/core
 */ function getMirahInstanceIdFromPatient(patient) {
    const mirahIdentifier = patient.identifier?.find((id)=>id.system === MIRAH_INSTITUTE_ID_SYSTEM);
    return mirahIdentifier?.value || null;
}

/**
 * Converts form data to FHIR Observation resource. Pure mapping, no IO.
 * @param formData - Observation form data
 * @param patient - Patient resource (for building references)
 * @param practitionerRole - PractitionerRole resource (for performer)
 * @param existingObservation - Existing observation resource (for updates)
 * @returns Observation domain model
 * @agentic @benchmark/core
 */ function observationFromForm(formData, patient, practitionerRole, existingObservation) {
    // Find the observation type details
    const observationType = formData["observation-type"] ? OBSERVATION_TYPES$1.find((type)=>type.code === formData["observation-type"]) : undefined;
    if (!observationType) {
        throw new Error("Invalid observation type");
    }
    // Build code first (required)
    const code = {
        coding: [
            {
                system: observationType.system,
                code: observationType.code,
                display: observationType.display
            }
        ],
        text: observationType.display
    };
    // Start with existing observation or create new one
    const observation = existingObservation ? {
        ...existingObservation
    } : {
        resourceType: "Observation",
        status: "final",
        code,
        subject: buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient)),
        performer: [
            buildPractitionerRoleReference(practitionerRole.id ?? "", practitionerRole.practitioner?.display || `PractitionerRole/${practitionerRole.id ?? ""}`)
        ]
    };
    // Set id if updating
    if (existingObservation?.id) {
        observation.id = existingObservation.id;
    }
    // Code (always set, even for updates)
    observation.code = code;
    // Category
    observation.category = [
        SURVEY_OBSERVATION_CATEGORY
    ];
    // Effective date/time
    if (formData.effective) {
        observation.effectiveDateTime = formData.effective;
    } else if (!existingObservation) {
        // Use current datetime for new observations if not provided
        observation.effectiveDateTime = new Date().toISOString();
    }
    // For updates, preserve existing effectiveDateTime if not provided
    // Value
    observation.valueInteger = formData.value;
    // Notes
    if (formData.note) {
        observation.note = [
            {
                text: formData.note
            }
        ];
    } else if (existingObservation) {
        // Preserve existing notes if not updating
        observation.note = existingObservation.note;
    }
    // Issued timestamp
    observation.issued = new Date().toISOString();
    return observation;
}

/**
 * Maps Observation domain model to ObservationFormData. Pure transformation, no IO.
 * @agentic @benchmark/core
 */ /**
 * Converts FHIR Observation resource to form data. Pure mapping, no IO.
 * @param observation - Observation resource
 * @returns Observation form data
 * @agentic @benchmark/core
 */ function observationToForm(observation) {
    const formData = {};
    // Extract observation type from code - only if code exists
    if (observation.code?.coding?.[0]?.code) {
        formData["observation-type"] = observation.code.coding[0].code;
    }
    // Extract effective date/time
    if (observation.effectiveDateTime) {
        formData["effective"] = observation.effectiveDateTime;
    }
    // Extract value - only handle integer values
    if (observation.valueInteger !== undefined) {
        formData["value"] = observation.valueInteger;
    }
    // Extract notes from note field
    if (observation.note?.[0]?.text) {
        formData["note"] = observation.note[0].text;
    }
    return formData;
}

/**
 * Converts form data to FHIR Organization resource.
 * @agentic @benchmark/core
 */ function organizationFromForm(formData) {
    const organization = {
        resourceType: "Organization",
        active: true
    };
    // Name (required for new organizations)
    if (formData.name) {
        organization.name = formData.name;
    }
    // Type (required for new organizations)
    if (formData.type) {
        const typeSystem = getOrganizationTypeSystem(formData.type);
        organization.type = [
            {
                coding: [
                    {
                        system: typeSystem,
                        code: formData.type,
                        display: getOrganizationTypeDisplay(formData.type)
                    }
                ]
            }
        ];
    }
    // Tier (optional - stored in meta.tag)
    if (formData.tier) {
        organization.meta = {
            ...organization.meta,
            tag: [
                ...organization.meta?.tag || [],
                {
                    system: ORGANIZATION_TIER_SYSTEM,
                    code: formData.tier
                }
            ]
        };
    }
    // Alias (optional)
    if (formData.alias && formData.alias.length > 0) {
        organization.alias = formData.alias;
    }
    return organization;
}
/**
 * Generates display name for Organization from form data.
 * @agentic @benchmark/core
 */ function getOrganizationDisplayFromForm(formData) {
    if (formData.existingDisplay) {
        return formData.existingDisplay;
    }
    return formData.name || "New Organization";
}

/**
 * Build the `cocm-consent` complex extension from consent values, or `null`
 * when neither an explicit answer nor a date is present. False is a recorded No. The sub-extension order
 * (`verified` then `date`) matches the form adapters' historical output.
 * @agentic @benchmark/core
 */ function buildCoCMConsentExtension(consent) {
    const subExtensions = [];
    if (consent.verified !== undefined) {
        subExtensions.push({
            url: "verified",
            valueBoolean: consent.verified
        });
    }
    if (consent.date) {
        subExtensions.push({
            url: "date",
            valueDate: consent.date
        });
    }
    if (subExtensions.length === 0) {
        return null;
    }
    return {
        url: COCM_CONSENT_EXTENSION_URL,
        extension: subExtensions
    };
}
/**
 * Return a copy of `patient` with its `cocm-consent` extension upserted from
 * the given consent values. When consent has neither an explicit answer nor a
 * date, any existing `cocm-consent` extension is removed. All other extensions
 * are preserved. Pure — the input patient is not mutated.
 * @agentic @benchmark/core
 */ function withCoCMConsentExtension(patient, consent) {
    const others = (patient.extension ?? []).filter((ext)=>ext.url !== COCM_CONSENT_EXTENSION_URL);
    const consentExtension = buildCoCMConsentExtension(consent);
    const next = consentExtension ? [
        ...others,
        consentExtension
    ] : others;
    return {
        ...patient,
        extension: next.length > 0 ? next : undefined
    };
}

/**
 * FHIR-Compliant Contact Relationship Constants
 *
 * Based on FHIR R4 v2-0131 Contact Role code system
 * http://terminology.hl7.org/CodeSystem/v2-0131
 *
 * These constants define the types of relationships a patient contact can have.
 * Used in Patient.contact[].relationship field.
 */ // FHIR v2-0131 system URL
const CONTACT_RELATIONSHIP_SYSTEM = "http://terminology.hl7.org/CodeSystem/v2-0131";
const CONTACT_RELATIONSHIP_OPTIONS = [
    /*
  // Removed since covered elsewhere
   {
    code: "EP",
    display: "Emergency contact person",
    system: CONTACT_RELATIONSHIP_SYSTEM,
    description: "Emergency contact person",
  },

    code: "PR",
    display: "Person preparing referral",
    system: CONTACT_RELATIONSHIP_SYSTEM,
    description: "Person preparing referral",
  },
  {
    code: "C",
    display: "Emergency Contact",
    system: CONTACT_RELATIONSHIP_SYSTEM,
    description: "Emergency Contact",
  },
  */ // Deprecated codes (v2.9+) - included for backwards compatibility
    {
        code: "BP",
        display: "Billing contact person",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Billing contact person"
    },
    {
        code: "CP",
        display: "Contact person",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Contact person"
    },
    // Active codes
    {
        code: "E",
        display: "Employer",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Employer"
    },
    {
        code: "F",
        display: "Federal Agency",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Federal Agency"
    },
    {
        code: "I",
        display: "Insurance Company",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Insurance Company"
    },
    {
        code: "N",
        display: "Next-of-Kin",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Next-of-Kin"
    },
    {
        code: "O",
        display: "Other",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Other"
    },
    {
        code: "S",
        display: "State Agency",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "State Agency"
    },
    {
        code: "U",
        display: "Unknown",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Unknown"
    }
];
/**
 * Simplified options for Mantine Select components
 * Maps to { value, label } format expected by Select
 */ CONTACT_RELATIONSHIP_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display
    }));

/** Code system URL for family and caregiver patient contacts. @agentic @benchmark/core */ const PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM = CODE_SYSTEM.V3_ROLE_CODE;
const PATIENT_FAMILY_CONTACT_RELATIONSHIP_OPTIONS = [
    {
        code: "MTH",
        display: "Mother",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "FTH",
        display: "Father",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "PRN",
        display: "Parent",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "CHILD",
        display: "Child",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "SONC",
        display: "Son",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "DAUC",
        display: "Daughter",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "SPS",
        display: "Spouse",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "SIB",
        display: "Sibling",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "FRND",
        display: "Friend",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "GUARD",
        display: "Primary caregiver",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    }
];
/** Set of curated family and caregiver relationship codes. @agentic @benchmark/core */ const PATIENT_FAMILY_CONTACT_RELATIONSHIP_CODES = new Set(PATIENT_FAMILY_CONTACT_RELATIONSHIP_OPTIONS.map((option)=>option.code));
/** Select options for family and caregiver contact relationships. @agentic @benchmark/core */ PATIENT_FAMILY_CONTACT_RELATIONSHIP_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display
    }));
/**
 * Returns display text for a curated family contact relationship code.
 * @agentic @benchmark/core
 */ const getPatientFamilyContactRelationshipDisplay = (code)=>PATIENT_FAMILY_CONTACT_RELATIONSHIP_OPTIONS.find((option)=>option.code === code)?.display;
/**
 * Returns whether a contact relationship belongs to the family/caregiver set.
 * @agentic @benchmark/core
 */ const isPatientFamilyContactRelationship = (code, system)=>{
    if (system === PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM) {
        return true;
    }
    if (system === CODE_SYSTEM.V2_0131) {
        return false;
    }
    if (!code) {
        return false;
    }
    return PATIENT_FAMILY_CONTACT_RELATIONSHIP_CODES.has(code);
};
/**
 * Resolves the relationship code system for a patient contact row.
 * @agentic @benchmark/core
 */ const inferPatientContactRelationshipSystem = (code, system)=>{
    if (system) {
        return system;
    }
    if (code && isPatientFamilyContactRelationship(code)) {
        return PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM;
    }
    return CODE_SYSTEM.V2_0131;
};

const getContactRelationshipDisplay = (code, system)=>{
    if (system === CODE_SYSTEM.V3_ROLE_CODE) {
        return getPatientFamilyContactRelationshipDisplay(code);
    }
    return CONTACT_RELATIONSHIP_OPTIONS.find((option)=>option.code === code)?.display;
};
/**
 * Maps a single FHIR Patient.contact entry to form data, or null when empty.
 * @agentic @benchmark/core
 */ const patientContactFromFhir = (contact)=>{
    if (!contact.name && !contact.telecom) {
        return null;
    }
    const coding = contact.relationship?.[0]?.coding?.[0];
    const relationship = coding?.code || "";
    const relationshipSystem = inferPatientContactRelationshipSystem(relationship, coding?.system);
    return {
        "first-name": contact.name?.given?.[0] || "",
        "last-name": contact.name?.family || "",
        phone: contact.telecom?.find((entry)=>entry.system === "phone")?.value || "",
        relationship,
        "relationship-system": relationshipSystem
    };
};
/**
 * Maps FHIR Patient.contact[] to patient form contact rows.
 * @agentic @benchmark/core
 */ const patientContactsFromFhir = (contacts)=>{
    if (!contacts || contacts.length === 0) {
        return [];
    }
    return contacts.map((contact)=>patientContactFromFhir(contact)).filter((contact)=>contact !== null);
};
/**
 * Maps patient form contact rows to FHIR Patient.contact[].
 * @agentic @benchmark/core
 */ const patientContactsToFhir = (contacts)=>contacts.map((contact)=>{
        const relationshipSystem = inferPatientContactRelationshipSystem(contact.relationship, contact["relationship-system"]);
        return {
            name: contact["first-name"] || contact["last-name"] ? {
                given: contact["first-name"] ? [
                    contact["first-name"]
                ] : undefined,
                family: contact["last-name"]
            } : undefined,
            telecom: contact.phone ? [
                {
                    system: "phone",
                    value: contact.phone
                }
            ] : undefined,
            relationship: contact.relationship ? [
                {
                    coding: [
                        {
                            system: relationshipSystem,
                            code: contact.relationship,
                            display: getContactRelationshipDisplay(contact.relationship, relationshipSystem)
                        }
                    ]
                }
            ] : undefined
        };
    });
/**
 * Partitions form contacts into general (v2-0131) and family/caregiver (v3-RoleCode) rows.
 * @agentic @benchmark/core
 */ const partitionPatientContacts = (contacts)=>{
    const general = [];
    const family = [];
    contacts.forEach((contact, index)=>{
        const entry = {
            contact,
            index
        };
        if (isPatientFamilyContactRelationship(contact.relationship, contact["relationship-system"])) {
            family.push(entry);
        } else {
            general.push(entry);
        }
    });
    return {
        general,
        family
    };
};

/**
 * Converts form data to FHIR Patient resource. Pure mapping, no IO.
 * @param formData - Patient form data
 * @param authorReference - Optional PractitionerRole reference for summary extension author
 * @returns Patient domain model
 * @agentic @benchmark/core
 */ function patientFromForm(formData, authorReference) {
    // Start with basic patient structure
    const patient = {
        resourceType: "Patient",
        active: true
    };
    // Basic patient information
    if (formData["first-name"] || formData["last-name"]) {
        patient.name = [
            {
                use: "official",
                given: formData["first-name"] ? [
                    formData["first-name"]
                ] : undefined,
                family: formData["last-name"]
            }
        ];
        if (formData["preferred-name"]?.trim()) {
            patient.name.push({
                use: "nickname",
                given: [
                    formData["preferred-name"].trim()
                ]
            });
        }
    }
    if (formData.dob) {
        patient.birthDate = formData.dob;
    }
    if (formData["gender-identity"]) {
        patient.gender = formData["gender-identity"];
    }
    // Contact information
    if (formData.phoneNumbers || formData.email) {
        const telecom = [];
        // Add phone numbers with rank
        if (formData.phoneNumbers && formData.phoneNumbers.length > 0) {
            formData.phoneNumbers.forEach((entry)=>{
                if (entry.value) {
                    telecom.push({
                        system: "phone",
                        value: toE164US(entry.value),
                        use: entry.use,
                        rank: entry.rank
                    });
                }
            });
        }
        if (formData.email) {
            telecom.push({
                system: "email",
                value: formData.email
            });
        }
        if (telecom.length > 0) {
            patient.telecom = telecom;
        }
    }
    // Address (skip when explicitly marked as not available)
    if (!formData["address-not-available"] && (formData.street || formData.city || formData.state || formData.zip)) {
        const addressLines = [];
        if (formData.street) addressLines.push(formData.street);
        if (formData["street-2"]) addressLines.push(formData["street-2"]);
        patient.address = [
            {
                line: addressLines.length > 0 ? addressLines : undefined,
                city: formData.city,
                state: formData.state,
                postalCode: formData.zip
            }
        ];
    }
    // Preferred language
    if (formData["preferred-language"]) {
        patient.communication = [
            {
                language: {
                    coding: [
                        {
                            system: "urn:ietf:bcp:47",
                            code: formData["preferred-language"]
                        }
                    ]
                }
            }
        ];
    }
    // Identifiers (MRN/EMR)
    const identifiers = [];
    if (formData.mrn) {
        identifiers.push({
            type: {
                coding: [
                    {
                        system: "http://terminology.hl7.org/CodeSystem/v2-0203",
                        code: "MR",
                        display: "Medical Record Number"
                    }
                ]
            },
            system: formData.emr,
            value: formData.mrn
        });
    }
    if (identifiers.length > 0) {
        patient.identifier = identifiers;
    }
    // Build generalPractitioner array with both PCP and LCSW
    const generalPractitioners = [];
    // Add referring provider (PCP)
    if (formData["primary-care-provider"]) {
        generalPractitioners.push({
            reference: `PractitionerRole/${formData["primary-care-provider"]}`,
            display: formData["primary-care-provider-display"] || "Practitioner",
            extension: [
                {
                    url: GP_ROLE_TYPE_EXTENSION,
                    valueCode: GP_ROLE_TYPES.REFERRING_PROVIDER
                }
            ]
        });
        // Set managingOrganization from the primary care provider's organization
        if (formData["primary-care-provider-organization-ref"] && formData["primary-care-provider-organization-display"]) {
            patient.managingOrganization = {
                reference: formData["primary-care-provider-organization-ref"],
                display: formData["primary-care-provider-organization-display"]
            };
        }
    }
    // Add Benchmark primary advocate
    if (formData["benchmark-lcsw"]) {
        generalPractitioners.push({
            reference: `PractitionerRole/${formData["benchmark-lcsw"]}`,
            display: formData["benchmark-lcsw-display"] || "Primary Advocate",
            extension: [
                {
                    url: GP_ROLE_TYPE_EXTENSION,
                    valueCode: GP_ROLE_TYPES.BENCHMARK_PRIMARY_ADVOCATE
                }
            ]
        });
    }
    if (generalPractitioners.length > 0) {
        patient.generalPractitioner = generalPractitioners;
    }
    // Extensions (race, ethnicity, sexual orientation)
    const extensions = [];
    // Race extension
    if (formData.race) {
        const raceDisplayMapping = {
            "1002-5": "American Indian or Alaska Native",
            "2028-9": "Asian",
            "2054-5": "Black or African American",
            "2076-8": "Native Hawaiian or Other Pacific Islander",
            "2106-3": "White",
            UNK: "Unknown",
            ASKU: "Asked but no answer"
        };
        const raceCode = formData.race;
        extensions.push({
            url: "http://hl7.org/fhir/us/core/StructureDefinition/us-core-race",
            extension: [
                {
                    url: "ombCategory",
                    valueCoding: {
                        system: "urn:oid:2.16.840.1.113883.6.238",
                        code: raceCode,
                        display: raceDisplayMapping[raceCode] || "Unknown"
                    }
                }
            ]
        });
    }
    // Ethnicity extension
    if (formData.ethnicity) {
        const ethnicityDisplayMapping = {
            "2135-2": "Hispanic or Latino",
            "2186-5": "Not Hispanic or Latino",
            UNK: "Unknown",
            ASKU: "Asked but no answer"
        };
        const ethnicityCode = formData.ethnicity;
        extensions.push({
            url: "http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity",
            extension: [
                {
                    url: "ombCategory",
                    valueCoding: {
                        system: "urn:oid:2.16.840.1.113883.6.238",
                        code: ethnicityCode,
                        display: ethnicityDisplayMapping[ethnicityCode] || "Unknown"
                    }
                }
            ]
        });
    }
    // Sexual orientation extension
    if (formData["sexual-orientation"] && formData["sexual-orientation"].length > 0) {
        extensions.push({
            url: "http://hl7.org/fhir/StructureDefinition/patient-sexualOrientation",
            valueCodeableConcept: {
                coding: formData["sexual-orientation"].map((orientation)=>({
                        code: orientation,
                        display: orientation
                    }))
            }
        });
    }
    if (formData.pronouns?.trim()) {
        extensions.push(buildIndividualPronounsExtension(formData.pronouns));
    }
    // Patient summary extension
    if (formData.summary?.trim()) {
        if (authorReference) {
            const summaryExtension = buildPatientSummaryExtension({
                text: formData.summary.trim(),
                authorReference,
                generatedBy: "manual"
            });
            extensions.push(summaryExtension);
        } else {
            // Summary provided but no authorReference - this should not happen in normal UI flow
            // Log warning to catch any code paths that don't pass authorReference
            console.warn("Patient summary was provided but authorReference is missing. Summary will not be saved.", {
                summaryLength: formData.summary.trim().length
            });
        }
    }
    // CoCM consent extension
    const cocmConsentExtension = buildCoCMConsentExtension({
        verified: formData["cocm-consent-verified"],
        date: formData["cocm-consent-date"]
    });
    if (cocmConsentExtension) {
        extensions.push(cocmConsentExtension);
    }
    if (extensions.length > 0) {
        patient.extension = extensions;
    }
    // Build tags for new patient (engagement status and veteran status)
    const tags = [];
    if (formData["patient-engagement-status"]) {
        const code = formData["patient-engagement-status"];
        const option = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === code);
        if (option) {
            tags.push({
                system: PATIENT_TAG_MAPPINGS.engagementStatus.system,
                code: option.code,
                display: option.display
            });
        }
    }
    if (formData["veteran-status"]) {
        tags.push({
            system: "http://terminology.hl7.org/CodeSystem/v3-VeteranMilitaryStatus",
            code: "veteran",
            display: "Veteran"
        });
    }
    if (tags.length > 0) {
        patient.meta = {
            tag: tags
        };
    }
    // Patient contacts
    if (formData["patient-contacts"] && formData["patient-contacts"].length > 0) {
        patient.contact = patientContactsToFhir(formData["patient-contacts"]);
    }
    return patient;
}

/**
 * Get patient engagement status (V2 preferred, falls back to V1)
 */ function getEngagementStatus(patient) {
    if (!patient.meta?.tag) return null;
    // First check for V2 system tags (preferred)
    const v2Tags = patient.meta.tag.filter((tag)=>tag.system === PATIENT_ENGAGEMENT_STATUS_SYSTEM);
    if (v2Tags.length > 0) {
        const mostRecentV2Tag = v2Tags[v2Tags.length - 1];
        if (mostRecentV2Tag?.code) {
            // Try to find matching V2 option
            const v2Option = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((option)=>option.code === mostRecentV2Tag.code);
            if (v2Option) {
                return {
                    code: v2Option.code,
                    display: v2Option.display,
                    system: PATIENT_ENGAGEMENT_STATUS_SYSTEM
                };
            }
            // Fallback to tag display if no matching option found
            return {
                code: mostRecentV2Tag.code,
                display: mostRecentV2Tag.display || mostRecentV2Tag.code,
                system: mostRecentV2Tag.system
            };
        }
    }
    // Fall back to V1 system tags (which may contain V2 codes in production)
    const v1Tags = patient.meta.tag.filter((tag)=>tag.system === LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM);
    if (v1Tags.length === 0) return null;
    const mostRecentV1Tag = v1Tags[v1Tags.length - 1];
    if (!mostRecentV1Tag?.code) return null;
    // Try to find matching option from V2 first (V2 codes stored under V1 system), then V1
    const v2Option = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((option)=>option.code === mostRecentV1Tag.code);
    if (v2Option) {
        return {
            code: v2Option.code,
            display: v2Option.display,
            system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM
        };
    }
    const v1Option = LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((option)=>option.code === mostRecentV1Tag.code);
    if (v1Option) {
        return {
            code: v1Option.code,
            display: v1Option.display,
            system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM
        };
    }
    // Fallback to tag display if no matching option found
    return {
        code: mostRecentV1Tag.code,
        display: mostRecentV1Tag.display || mostRecentV1Tag.code,
        system: mostRecentV1Tag.system
    };
}
/**
 * Returns engagement status with version (v1/v2) and migration suggestion.
 * @agentic @benchmark/core
 */ function getPatientEngagementStatus(patient) {
    if (!patient.meta?.tag) return null;
    // Prefer V2 system tags first (if they exist, they're the most current)
    const v2SystemTag = patient.meta.tag.find((tag)=>tag.system === PATIENT_ENGAGEMENT_STATUS_SYSTEM);
    if (v2SystemTag?.code) {
        const statusOption = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === v2SystemTag.code);
        if (statusOption) {
            return {
                code: statusOption.code,
                display: statusOption.display,
                system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
                version: "v2"
            };
        }
        // Fallback: return V2 tag even if code doesn't match options (shouldn't happen, but prevents "Unknown")
        return {
            code: v2SystemTag.code,
            display: v2SystemTag.display || v2SystemTag.code,
            system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
            version: "v2"
        };
    }
    // Check for tags under V1 system URL (which may contain V2 codes)
    const v1SystemTag = patient.meta.tag.find((tag)=>tag.system === LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM);
    if (v1SystemTag?.code) {
        // First try to match as V2 code (current codes can be stored under V1 system)
        const v2Option = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === v1SystemTag.code);
        if (v2Option) {
            return {
                code: v2Option.code,
                display: v2Option.display,
                system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
                version: "v2"
            };
        }
        // Fall back to V1 code matching
        const v1Option = LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === v1SystemTag.code);
        if (v1Option) {
            return {
                code: v1Option.code,
                display: v1Option.display,
                system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
                version: "v1",
                shouldMigrate: true
            };
        }
        // Fallback: return V1 tag even if code doesn't match options (prevents "Unknown")
        return {
            code: v1SystemTag.code,
            display: v1SystemTag.display || v1SystemTag.code,
            system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
            version: "v1",
            shouldMigrate: true
        };
    }
    return null;
}

/**
 * Converts FHIR Patient and related resources to PatientFormData.
 * @param context - All resources needed for transformation
 * @returns PatientFormData ready for form editing
 * @agentic @benchmark/core
 */ function patientToForm(context) {
    const { patient, coverages, relatedPersons, careTeams, conditions, practitionerRole, referralDate } = context;
    const formData = initialisePatientFormData();
    populatePersonalInfo(formData, patient);
    populateIdentifiers(formData, patient, referralDate);
    populateTelecom(formData, patient);
    populateAddress(formData, patient);
    populateCommunicationDetails(formData, patient);
    populateDemographics(formData, patient);
    populatePrimaryCareProvider(formData, patient, practitionerRole);
    populateRelatedResources(formData, patient, coverages, relatedPersons, careTeams);
    populateConditions(formData, conditions);
    populateCocmConsent(formData, patient);
    populateSummary(formData, patient);
    return formData;
}
/** Initializes empty PatientFormData. @agentic @benchmark/core */ const initialisePatientFormData = ()=>({
        "first-name": "",
        "last-name": "",
        "preferred-name": "",
        dob: "",
        "referral-date": "",
        "primary-care-provider": "",
        mrn: "",
        emr: "",
        "cocm-consent-verified": undefined,
        "cocm-consent-date": "",
        "preferred-language": "",
        phoneNumbers: [
            {
                value: "",
                use: "mobile",
                rank: 1
            }
        ],
        email: "",
        street: "",
        "street-2": "",
        city: "",
        state: "",
        zip: "",
        "address-not-available": false,
        "primary-insurance-provider": "",
        "primary-insurance-plan": "",
        "primary-subscriber-id": "",
        "primary-relationship-to-subscriber": "self",
        "primary-subscriber-first-name": "",
        "primary-subscriber-last-name": "",
        "primary-subscriber-dob": "",
        "primary-subscriber-phone": "",
        "has-secondary-insurance": false,
        "secondary-insurance-provider": "",
        "secondary-insurance-plan": "",
        "secondary-subscriber-id": "",
        "secondary-relationship-to-subscriber": "self",
        "secondary-subscriber-first-name": "",
        "secondary-subscriber-last-name": "",
        "secondary-subscriber-dob": "",
        "secondary-subscriber-phone": "",
        "emergency-contact-first-name": "",
        "emergency-contact-last-name": "",
        "emergency-contact-phone": "",
        "patient-contacts": [],
        race: "",
        ethnicity: "",
        "veteran-status": false,
        "gender-identity": "",
        pronouns: "",
        "sexual-orientation": [],
        "self-pay": false,
        "benchmark-lcsw": "",
        "benchmark-advocate": "",
        "icd10-codes": []
    });
/** Populates personal info from Patient. @agentic @benchmark/core */ const populatePersonalInfo = (formData, patient)=>{
    const officialName = patient.name?.find((n)=>n.use === "official") || patient.name?.[0];
    if (officialName) {
        formData["first-name"] = officialName.given?.[0] || "";
        formData["last-name"] = officialName.family || "";
    }
    const nickname = patient.name?.find((n)=>n.use === "nickname");
    if (nickname?.given?.[0]) {
        formData["preferred-name"] = nickname.given[0];
    }
    if (patient.birthDate) {
        formData["dob"] = patient.birthDate;
    }
};
/** Populates identifiers from Patient. @agentic @benchmark/core */ const populateIdentifiers = (formData, patient, referralDate)=>{
    if (referralDate) {
        formData["referral-date"] = referralDate;
    }
    const mrnInfo = getPatientMRN(patient);
    if (mrnInfo) {
        formData["mrn"] = mrnInfo.value;
        let emrSystem = mrnInfo.system;
        // Fallback: legacy EI identifier stored the EMR system name as its value
        if (!emrSystem) {
            const legacyEI = patient.identifier?.find((id)=>id.type?.coding?.[0]?.code === "EI" && !id.system && !!id.value);
            if (legacyEI?.value) {
                emrSystem = legacyEI.value;
            }
        }
        if (emrSystem) {
            const emrMapping = {
                "urn:oid:1.2.36.146.595.217.0.1": "www.athenahealth.com",
                "www.athenahealth.com": "www.athenahealth.com",
                "www.epic.com": "www.epic.com"
            };
            formData["emr"] = emrMapping[emrSystem] || emrSystem;
        }
    }
};
/** Populates telecom from Patient. @agentic @benchmark/core */ const populateTelecom = (formData, patient)=>{
    const telecom = getActivePatientTelecom(patient);
    if (!telecom || telecom.length === 0) return;
    const phoneNumbers = telecom.filter((entry)=>entry.system === "phone" && entry.value).map((entry, index)=>buildPhoneNumberEntry(entry.value, entry.use, entry.rank, index));
    if (phoneNumbers.length > 0) {
        formData.phoneNumbers = phoneNumbers.sort((a, b)=>a.rank - b.rank);
    }
    const email = telecom.find((entry)=>entry.system === "email");
    if (email?.value) {
        formData["email"] = email.value;
    }
};
/** Builds PhoneNumberEntry from telecom. @agentic @benchmark/core */ const buildPhoneNumberEntry = (value, use, rank, index)=>{
    // Map FHIR use codes to PhoneNumberEntry use (exclude "old")
    const validUse = use === "home" || use === "work" || use === "temp" || use === "mobile" ? use : "mobile";
    return {
        value,
        use: validUse,
        rank: rank || index + 1
    };
};
/** Populates address from Patient. @agentic @benchmark/core */ const populateAddress = (formData, patient)=>{
    const address = getActivePatientAddress(patient);
    if (!address) {
        return;
    }
    formData["street"] = address.line?.[0] || "";
    formData["street-2"] = address.line?.[1] || "";
    formData["city"] = address.city || "";
    formData["state"] = address.state || "";
    formData["zip"] = address.postalCode || "";
    if (!hasStructuredAddress(formData) && address.text) {
        populateAddressFromText(formData, address.text);
    }
};
/** Checks if form has structured address. @agentic @benchmark/core */ const hasStructuredAddress = (formData)=>Boolean(formData["street"] || formData["city"] || formData["state"] || formData["zip"]);
/** Populates address from text. @agentic @benchmark/core */ const populateAddressFromText = (formData, addressText)=>{
    const trimmed = addressText.trim();
    // Match 5-digit ZIP (with or without +4 extension)
    const zipMatch = trimmed.match(/(\d{5})-\d{4}/) || trimmed.match(/(\d{5})/);
    if (zipMatch) {
        formData["zip"] = zipMatch[1];
        const zipIndex = trimmed.indexOf(zipMatch[0]);
        const beforeZip = trimmed.substring(0, zipIndex).trim().replace(/,\s*$/, "");
        const stateMatch = beforeZip.match(/,?\s*([A-Z]{2})\s*$/);
        if (stateMatch) {
            formData["state"] = stateMatch[1];
            const withoutState = beforeZip.replace(/,?\s*[A-Z]{2}\s*$/, "").trim();
            parseStreetAndCity(formData, withoutState);
            return;
        }
        formData["street"] = beforeZip;
        return;
    }
    const stateMatch = trimmed.match(/,\s*([A-Z]{2})(?:\s|,|$)/);
    if (stateMatch) {
        formData["state"] = stateMatch[1];
        const beforeState = trimmed.substring(0, stateMatch.index).trim();
        parseStreetAndCity(formData, beforeState);
        return;
    }
    formData["street"] = trimmed;
};
/** Parses street and city from value. @agentic @benchmark/core */ const parseStreetAndCity = (formData, value)=>{
    if (!value) {
        return;
    }
    const duplicateMatch = value.match(/^(\d+\s+[^,]+),\s*\d+\s+[^,]+,\s*(.+)$/);
    const addressToProcess = duplicateMatch ? `${duplicateMatch[1].trim()}, ${duplicateMatch[2].trim()}` : value;
    const streetIndicatorRegex = /\b(St\.?|Street|Ave\.?|Avenue|Rd\.?|Road|Dr\.?|Drive|Ln\.?|Lane|Way|Blvd\.?|Boulevard|Ct\.?|Court|Pl\.?|Place|Pkwy\.?|Parkway|Cir\.?|Circle|Ter\.?|Terrace)\b/i;
    if (streetIndicatorRegex.test(addressToProcess)) {
        const matches = [];
        const regex = new RegExp(streetIndicatorRegex.source, "gi");
        let match;
        while((match = regex.exec(addressToProcess)) !== null){
            matches.push(match);
        }
        if (matches.length > 0) {
            const lastMatch = matches[matches.length - 1];
            const lastIndicatorEnd = lastMatch.index + lastMatch[0].length;
            formData["street"] = addressToProcess.substring(0, lastIndicatorEnd).trim();
            formData["city"] = addressToProcess.substring(lastIndicatorEnd).replace(/^[,\s]+/, "").trim();
            return;
        }
    }
    const lastCommaIndex = addressToProcess.lastIndexOf(",");
    if (lastCommaIndex > 0) {
        formData["street"] = addressToProcess.substring(0, lastCommaIndex).trim();
        formData["city"] = addressToProcess.substring(lastCommaIndex + 1).trim();
        return;
    }
    const words = addressToProcess.split(/\s+/);
    if (words.length > 3) {
        if (words.length > 4) {
            formData["city"] = words.slice(-2).join(" ");
            formData["street"] = words.slice(0, -2).join(" ");
        } else {
            formData["city"] = words[words.length - 1];
            formData["street"] = words.slice(0, -1).join(" ");
        }
        return;
    }
    formData["street"] = addressToProcess;
};
/**
 * Maps the first Patient.communication language to the form's language option
 * code, resolving display names and free text against LANGUAGES so the edit
 * Select can show it. Values outside the list pass through unchanged.
 * @agentic @benchmark/core
 */ const getPreferredLanguageFormValue = (patient)=>{
    const language = patient.communication?.[0]?.language;
    const value = (language?.coding?.[0]?.code || language?.coding?.[0]?.display || language?.text || "").trim();
    return LANGUAGES.find((option)=>option.display.toLowerCase() === value.toLowerCase())?.code ?? value;
};
/** Populates communication details from Patient. @agentic @benchmark/core */ const populateCommunicationDetails = (formData, patient)=>{
    formData["preferred-language"] = getPreferredLanguageFormValue(patient);
};
/** Populates demographics from Patient. @agentic @benchmark/core */ const populateDemographics = (formData, patient)=>{
    if (patient.gender) {
        formData["gender-identity"] = patient.gender;
    }
    const extensions = patient.extension || [];
    const raceExtension = extensions.find((ext)=>ext.url === "http://hl7.org/fhir/us/core/StructureDefinition/us-core-race");
    if (raceExtension?.extension) {
        const raceCategory = raceExtension.extension.find((ext)=>ext.url === "ombCategory");
        if (raceCategory?.valueCoding?.code) {
            formData["race"] = raceCategory.valueCoding.code;
        }
    }
    const ethnicityExtension = extensions.find((ext)=>ext.url === "http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity");
    if (ethnicityExtension?.extension) {
        const ethnicityCategory = ethnicityExtension.extension.find((ext)=>ext.url === "ombCategory");
        if (ethnicityCategory?.valueCoding?.code) {
            formData["ethnicity"] = ethnicityCategory.valueCoding.code;
        }
    }
    const sexualOrientationExtension = extensions.find((ext)=>ext.url === "http://hl7.org/fhir/StructureDefinition/patient-sexualOrientation");
    if (sexualOrientationExtension?.valueCodeableConcept?.coding) {
        formData["sexual-orientation"] = sexualOrientationExtension.valueCodeableConcept.coding.map((coding)=>coding.code).filter(Boolean);
    }
    const pronounsText = extractIndividualPronounsFromPatient(patient);
    if (pronounsText) {
        formData.pronouns = pronounsText;
    }
    const veteranTag = patient.meta?.tag?.find((tag)=>tag.system === "http://terminology.hl7.org/CodeSystem/v3-VeteranMilitaryStatus");
    formData["veteran-status"] = veteranTag?.code === "veteran";
    // Read engagement status preferring the V2 system, then falling back to V1.
    // The engagement modal writes V2-system tags (e.g. "activated"), so reading
    // only the V1 system — as this adapter previously did — missed those
    // statuses entirely. When the status was not read, the edit form kept its
    // default value ("referred") and silently overwrote the real status on the
    // next profile save, resetting activated patients back to referred.
    const engagementStatus = getEngagementStatus(patient);
    if (engagementStatus?.code) {
        formData["patient-engagement-status"] = engagementStatus.code;
    }
};
/** Finds general practitioner by role type. @agentic @benchmark/core */ const findGeneralPractitionerByType = (patient, roleType)=>{
    return patient.generalPractitioner?.find((ref)=>ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION && ext.valueCode === roleType));
};
/** Populates primary care provider from Patient. @agentic @benchmark/core */ const populatePrimaryCareProvider = (formData, patient, practitionerRole)=>{
    // Extract PCP (referring provider)
    const pcpRef = findGeneralPractitionerByType(patient, GP_ROLE_TYPES.REFERRING_PROVIDER) || patient.generalPractitioner?.[0]; // Fallback for legacy data
    if (pcpRef?.reference) {
        if (pcpRef.reference.startsWith("PractitionerRole/")) {
            formData["primary-care-provider"] = pcpRef.reference.replace("PractitionerRole/", "");
            if (pcpRef.display) {
                formData["primary-care-provider-display"] = pcpRef.display;
            }
        } else if (practitionerRole?.id) {
            formData["primary-care-provider"] = practitionerRole.id;
            // Try to get display name from practitionerRole
            const displayName = practitionerRole.practitioner?.display || practitionerRole.organization?.display;
            if (displayName) {
                formData["primary-care-provider-display"] = displayName;
            }
        }
    }
    // Populate managing organization fields from patient.managingOrganization
    // This ensures the organization reference is preserved during form round-trips
    if (patient.managingOrganization?.reference) {
        formData["primary-care-provider-organization-ref"] = patient.managingOrganization.reference;
        if (patient.managingOrganization.display) {
            formData["primary-care-provider-organization-display"] = patient.managingOrganization.display;
        }
    }
};
/** Populates related resources (insurance, contacts, care team). @agentic @benchmark/core */ const populateRelatedResources = (formData, patient, coverages, relatedPersons, careTeams)=>{
    populateInsurance(formData, patient, coverages, relatedPersons);
    populateEmergencyContact(formData, relatedPersons);
    populatePatientContacts(formData, patient);
    populateCareTeam(formData, patient, careTeams);
};
/** Populates insurance from coverages. @agentic @benchmark/core */ const populateInsurance = (formData, patient, coverages, relatedPersons)=>{
    // Only populate form with active coverages - cancelled coverages should not appear
    const activeCoverages = coverages.filter((coverage)=>coverage.status === "active");
    formData["self-pay"] = activeCoverages.length === 0;
    if (activeCoverages.length > 0) {
        populateCoverage(formData, patient, activeCoverages[0], "primary", relatedPersons);
    }
    if (activeCoverages.length > 1) {
        formData["has-secondary-insurance"] = true;
        populateCoverage(formData, patient, activeCoverages[1], "secondary", relatedPersons);
    }
};
/** Populates coverage for primary/secondary. @agentic @benchmark/core */ const populateCoverage = (formData, patient, coverage, role, relatedPersons)=>{
    const prefix = role === "primary" ? "primary" : "secondary";
    const payor = coverage.payor?.[0];
    if (payor?.reference) {
        formData[`${prefix}-insurance-provider`] = payor.reference.replace("Organization/", "");
        // Extract display name for the insurance provider
        if (payor.display) {
            formData[`${prefix}-insurance-provider-display`] = payor.display;
        }
    }
    // Extract InsurancePlan ID and display name from Coverage.class[type=plan]
    if (coverage.class && Array.isArray(coverage.class)) {
        const planClass = coverage.class.find((cls)=>cls.type?.coding?.[0]?.code === "plan");
        if (planClass?.value) {
            formData[`${prefix}-insurance-plan`] = planClass.value;
        }
        // Extract display name for the insurance plan
        if (planClass?.name) {
            formData[`${prefix}-insurance-plan-display`] = planClass.name;
        }
    }
    if (coverage.subscriberId) {
        formData[`${prefix}-subscriber-id`] = coverage.subscriberId;
    }
    const relationshipCode = coverage.relationship?.coding?.[0]?.code;
    if (relationshipCode) {
        formData[`${prefix}-relationship-to-subscriber`] = relationshipCode;
    }
    populateSubscriberInfo(formData, coverage.subscriber, prefix, relationshipCode, patient, relatedPersons);
};
/** Populates subscriber info from coverage. @agentic @benchmark/core */ const populateSubscriberInfo = (formData, subscriberRef, prefix, relationshipCode, patient, relatedPersons)=>{
    if (!subscriberRef?.reference) {
        return;
    }
    if (relationshipCode === "self" && patient) {
        formData[`${prefix}-subscriber-first-name`] = patient.name?.[0]?.given?.[0] || "";
        formData[`${prefix}-subscriber-last-name`] = patient.name?.[0]?.family || "";
        formData[`${prefix}-subscriber-dob`] = patient.birthDate || "";
        const phone = getActivePatientPhone(patient);
        if (phone) {
            formData[`${prefix}-subscriber-phone`] = phone;
        }
        return;
    }
    // For non-self relationships, try to find subscriber in RelatedPersons
    if (relatedPersons) {
        const subscriberRelatedPerson = relatedPersons.find((rp)=>`RelatedPerson/${rp.id}` === subscriberRef.reference);
        if (subscriberRelatedPerson) {
            fillSubscriberFromRelatedPerson(formData, subscriberRelatedPerson, prefix);
            return;
        }
    }
// Note: Patient subscribers (non-self) would need to be loaded separately
// This is a less common case and can be enhanced if needed
};
/** Fills subscriber fields from RelatedPerson. @agentic @benchmark/core */ const fillSubscriberFromRelatedPerson = (formData, subscriber, prefix)=>{
    const data = formData;
    const name = subscriber.name?.[0];
    if (name) {
        data[`${prefix}-subscriber-first-name`] = name.given?.[0] || "";
        data[`${prefix}-subscriber-last-name`] = name.family || "";
    }
    if (subscriber.birthDate) {
        data[`${prefix}-subscriber-dob`] = subscriber.birthDate;
    }
    const phone = subscriber.telecom?.find((t)=>t.system === "phone");
    if (phone?.value) {
        data[`${prefix}-subscriber-phone`] = phone.value;
    }
};
/** Populates emergency contact from RelatedPersons. @agentic @benchmark/core */ const populateEmergencyContact = (formData, relatedPersons)=>{
    const emergencyContact = relatedPersons.find((rp)=>rp.relationship?.[0]?.coding?.[0]?.code === "emergency");
    if (!emergencyContact) {
        return;
    }
    const name = emergencyContact.name?.[0];
    if (name) {
        formData["emergency-contact-first-name"] = name.given?.[0] || "";
        formData["emergency-contact-last-name"] = name.family || "";
    }
    const phone = emergencyContact.telecom?.find((t)=>t.system === "phone");
    if (phone?.value) {
        formData["emergency-contact-phone"] = phone.value;
    }
};
/** Populates patient contacts from Patient.contact. @agentic @benchmark/core */ const populatePatientContacts = (formData, patient)=>{
    if (!patient.contact || patient.contact.length === 0) {
        return;
    }
    formData["patient-contacts"] = patientContactsFromFhir(patient.contact);
};
/** Populates care team (LCSW, advocate) from CareTeams. @agentic @benchmark/core */ const populateCareTeam = (formData, patient, careTeams)=>{
    // Extract LCSW from generalPractitioner (new!)
    const lcswRef = getPatientBenchmarkLCSW(patient);
    if (lcswRef?.reference?.startsWith("PractitionerRole/")) {
        formData["benchmark-lcsw"] = lcswRef.reference.replace("PractitionerRole/", "");
        formData["benchmark-lcsw-display"] = lcswRef.display;
    }
    // NOTE: CareTeam-based LCSW extraction (existing code) should remain as fallback
    // for patients not yet backfilled. Remove after backfill is complete.
    const benchmarkCareTeam = careTeams.find((ct)=>ct.category?.[0]?.coding?.[0]?.code === "LA28865-6" || ct.name === "Benchmark Care Team");
    if (!benchmarkCareTeam?.participant) {
        return;
    }
    benchmarkCareTeam.participant.forEach((participant)=>{
        const code = participant.role?.[0]?.coding?.[0]?.code;
        const reference = participant.member?.reference;
        if (!code || !reference) {
            return;
        }
        // Only populate LCSW from CareTeam if not already set from generalPractitioner
        if (code === "224598009" && reference.startsWith("PractitionerRole/") && !formData["benchmark-lcsw"]) {
            formData["benchmark-lcsw"] = reference.replace("PractitionerRole/", "");
            formData["benchmark-lcsw-display"] = participant.member?.display;
        }
        if (code === "429577009" && reference.startsWith("PractitionerRole/")) {
            formData["benchmark-advocate"] = reference.replace("PractitionerRole/", "");
            formData["benchmark-advocate-display"] = participant.member?.display;
        }
    });
};
/** Populates ICD-10 codes from Conditions. @agentic @benchmark/core */ const populateConditions = (formData, conditions)=>{
    if (!conditions || conditions.length === 0) {
        return;
    }
    const icd10Codes = conditionsToICD10Codes(conditions);
    if (icd10Codes.length > 0) {
        formData["icd10-codes"] = icd10Codes;
    }
};
/** Populates CoCM consent from Patient extension. @agentic @benchmark/core */ const populateCocmConsent = (formData, patient)=>{
    const cocmExtension = patient.extension?.find((ext)=>ext.url === COCM_CONSENT_EXTENSION_URL);
    if (!cocmExtension?.extension) return;
    const verified = cocmExtension.extension.find((sub)=>sub.url === "verified");
    if (verified?.valueBoolean !== undefined) {
        formData["cocm-consent-verified"] = verified.valueBoolean;
    }
    const date = cocmExtension.extension.find((sub)=>sub.url === "date");
    if (date?.valueDate) {
        formData["cocm-consent-date"] = date.valueDate;
    }
};
/** Populates summary from Patient extension. @agentic @benchmark/core */ const populateSummary = (formData, patient)=>{
    const summary = getPatientSummary(patient);
    if (summary) {
        formData.summary = summary.text;
    }
};

/** Returns true when gp-role-type code is current or legacy primary advocate. @agentic @benchmark/core */ const isBenchmarkPrimaryAdvocateCode = (valueCode)=>valueCode === GP_ROLE_TYPES.BENCHMARK_PRIMARY_ADVOCATE || valueCode === GP_ROLE_TYPES.BENCHMARK_LCSW;
/**
 * Merges form data with existing Patient. Handles telecom, address, identifier lifecycle.
 * @param formData - Patient form data
 * @param existingPatient - Existing patient resource to update
 * @param authorReference - Optional PractitionerRole reference for summary extension author
 * @returns Updated Patient resource ready for update
 * @agentic @benchmark/core
 */ function patientUpdateFromForm(formData, existingPatient, authorReference) {
    // Start with existing patient
    const updatedPatient = {
        ...existingPatient,
        resourceType: "Patient"
    };
    // The form holds only what patientToForm can load, so rebuilding a field the
    // user left alone drops detail it cannot show (detailed race codes, coded
    // pronouns, contact email/address). Rewrite such fields only on a real change.
    const loaded = patientToForm({
        patient: existingPatient,
        coverages: [],
        relatedPersons: [],
        careTeams: [],
        conditions: []
    });
    const changed = (key)=>formData[key] !== undefined && !deepEquals(formData[key], loaded[key]);
    // Update name - direct update for current name
    if (formData["first-name"] || formData["last-name"]) {
        const existingOfficialName = updatedPatient.name?.find((n)=>n.use === "official") || updatedPatient.name?.[0] || {};
        const officialName = {
            ...existingOfficialName,
            use: "official",
            // The form edits only the first given name; keep middle names.
            given: formData["first-name"] ? [
                formData["first-name"],
                ...(existingOfficialName.given ?? []).slice(1)
            ] : existingOfficialName.given,
            family: formData["last-name"] || existingOfficialName.family
        };
        // Preserve any name entries that aren't the official or nickname
        const otherNames = (updatedPatient.name || []).filter((n)=>n.use !== "official" && n.use !== "nickname" && n !== existingOfficialName);
        updatedPatient.name = [
            officialName,
            ...otherNames
        ];
        if (formData["preferred-name"]?.trim()) {
            updatedPatient.name.push({
                use: "nickname",
                given: [
                    formData["preferred-name"].trim()
                ]
            });
        }
    }
    if (formData.dob) {
        updatedPatient.birthDate = formData.dob;
    }
    if (formData["gender-identity"]) {
        updatedPatient.gender = formData["gender-identity"];
    }
    // Update contact information - properly handle lifecycle management
    if (formData.phoneNumbers !== undefined || formData.email !== undefined) {
        const existingTelecom = updatedPatient.telecom || [];
        const newTelecom = [];
        // Handle phone numbers array
        if (formData.phoneNumbers !== undefined) {
            const phoneEntries = formData.phoneNumbers || [];
            if (phoneEntries.length > 0) {
                // Add all new phone numbers, keeping the stored detail of unchanged ones
                phoneEntries.forEach((entry)=>{
                    if (entry.value) {
                        const value = toE164US(entry.value); // Normalize to E.164
                        const unchanged = existingTelecom.find((t)=>t.system === "phone" && !t.period?.end && t.value === value);
                        newTelecom.push({
                            ...unchanged,
                            system: "phone",
                            value,
                            use: entry.use,
                            rank: entry.rank
                        });
                    }
                });
                // Mark any existing phones not in new list as ended
                existingTelecom.forEach((t)=>{
                    if (t.system === "phone" && !t.period?.end) {
                        const stillExists = phoneEntries.some((e)=>toE164US(e.value) === t.value);
                        if (!stillExists) {
                            newTelecom.push({
                                ...t,
                                period: {
                                    ...t.period,
                                    end: localCalendarDateTodayISO()
                                }
                            });
                        }
                    }
                });
            } else {
                // Mark all existing phones as ended if array is empty
                existingTelecom.forEach((t)=>{
                    if (t.system === "phone" && !t.period?.end) {
                        newTelecom.push({
                            ...t,
                            period: {
                                ...t.period,
                                end: localCalendarDateTodayISO()
                            }
                        });
                    }
                });
            }
            // The form loads only current phones; keep the ended ones as history.
            newTelecom.push(...existingTelecom.filter((t)=>t.system === "phone" && t.period?.end));
        } else {
            // Keep existing phone entries unchanged if not in form data
            existingTelecom.forEach((t)=>{
                if (t.system === "phone") {
                    newTelecom.push(t);
                }
            });
        }
        // Handle email
        if (formData.email !== undefined) {
            if (formData.email) {
                // Add new/updated email
                const existingEmail = existingTelecom.find((t)=>t.system === "email" && !t.period?.end);
                const unchangedEmail = existingTelecom.find((t)=>t.system === "email" && !t.period?.end && t.value === formData.email);
                newTelecom.push({
                    ...unchangedEmail,
                    system: "email",
                    value: formData.email,
                    use: existingEmail?.use || "home"
                });
                // Mark any existing active email as old
                existingTelecom.forEach((t)=>{
                    if (t.system === "email" && !t.period?.end && t.value !== formData.email) {
                        newTelecom.push({
                            ...t,
                            period: {
                                ...t.period,
                                end: localCalendarDateTodayISO()
                            }
                        });
                    }
                });
            } else {
                // Mark existing email as ended if user cleared the field
                existingTelecom.forEach((t)=>{
                    if (t.system === "email" && !t.period?.end) {
                        newTelecom.push({
                            ...t,
                            period: {
                                ...t.period,
                                end: localCalendarDateTodayISO()
                            }
                        });
                    }
                });
            }
            newTelecom.push(...existingTelecom.filter((t)=>t.system === "email" && t.period?.end));
        } else {
            // Keep existing email entries unchanged if not in form data
            existingTelecom.forEach((t)=>{
                if (t.system === "email") {
                    newTelecom.push(t);
                }
            });
        }
        // Preserve any other telecom entries not handled by the form
        const otherTelecom = existingTelecom.filter((t)=>t.system !== "phone" && t.system !== "email");
        updatedPatient.telecom = [
            ...newTelecom,
            ...otherTelecom
        ];
    }
    // Update address - properly handle lifecycle management
    if (formData.street !== undefined || formData.city !== undefined || formData.state !== undefined || formData.zip !== undefined) {
        const existingAddresses = updatedPatient.address || [];
        const newAddresses = [];
        // Check if any address fields have content
        const hasAddressData = formData.street || formData.city || formData.state || formData.zip;
        if (hasAddressData) {
            // Create new current address
            const currentAddress = existingAddresses.find((addr)=>addr.use !== "old" && (!addr.period || !addr.period.end)) || {};
            // Don't preserve text field when updating structured fields - it can conflict
            const { text, ...addressMetadata } = currentAddress;
            const addressLines = [];
            if (formData.street) addressLines.push(formData.street);
            if (formData["street-2"]) addressLines.push(formData["street-2"]);
            newAddresses.push({
                ...addressMetadata,
                use: "home",
                line: addressLines.length > 0 ? addressLines : undefined,
                city: formData.city || undefined,
                state: formData.state || undefined,
                postalCode: formData.zip || undefined,
                period: {
                    start: currentAddress.period?.start || localCalendarDateTodayISO()
                }
            });
            // Mark existing addresses as old if they're different from the new one.
            // Normalize empty strings and undefined to match consistently.
            const normalizeField = (val)=>val || "";
            existingAddresses.forEach((addr)=>{
                if (addr.use !== "old" && (!addr.period || !addr.period.end) && (normalizeField(addr.line?.[0]) !== normalizeField(formData.street) || normalizeField(addr.city) !== normalizeField(formData.city) || normalizeField(addr.state) !== normalizeField(formData.state) || normalizeField(addr.postalCode) !== normalizeField(formData.zip))) {
                    newAddresses.push({
                        ...addr,
                        use: "old",
                        period: {
                            ...addr.period,
                            end: localCalendarDateTodayISO()
                        }
                    });
                } else if (addr.use === "old" || addr.period?.end) {
                    // Preserve already-old addresses
                    newAddresses.push(addr);
                }
            });
        } else {
            // Mark all existing addresses as old if all fields are cleared
            existingAddresses.forEach((addr)=>{
                if (addr.use !== "old" && (!addr.period || !addr.period.end)) {
                    newAddresses.push({
                        ...addr,
                        use: "old",
                        period: {
                            ...addr.period,
                            end: localCalendarDateTodayISO()
                        }
                    });
                } else {
                    newAddresses.push(addr);
                }
            });
        }
        updatedPatient.address = newAddresses;
    }
    if (changed("preferred-language")) {
        updatedPatient.communication = formData["preferred-language"] ? [
            {
                language: {
                    coding: [
                        {
                            system: "urn:ietf:bcp:47",
                            code: formData["preferred-language"]
                        }
                    ]
                }
            }
        ] : [];
    }
    // Update MRN identifier (EMR system is stored as the MR identifier's `system` field)
    const existingIdentifiers = updatedPatient.identifier || [];
    const newIdentifiers = [];
    const legacyEmrFromMalformedEI = existingIdentifiers.find((id)=>id.type?.coding?.[0]?.code === "EI" && !id.system && !!id.value)?.value;
    // Handle MRN + EMR as a single MR identifier (system = EMR, value = MRN)
    if (formData.mrn !== undefined || formData.emr !== undefined) {
        const mrnIdentifier = existingIdentifiers.find((id)=>id.type?.coding?.[0]?.code === "MR");
        const newMrn = formData.mrn !== undefined ? formData.mrn : mrnIdentifier?.value;
        const newEmr = formData.emr !== undefined ? formData.emr : mrnIdentifier?.system || legacyEmrFromMalformedEI;
        if (newMrn) {
            newIdentifiers.push({
                ...mrnIdentifier,
                type: mrnIdentifier?.type ?? {
                    coding: [
                        {
                            system: "http://terminology.hl7.org/CodeSystem/v2-0203",
                            code: "MR",
                            display: "Medical Record Number"
                        }
                    ]
                },
                system: newEmr || undefined,
                value: newMrn
            });
        }
    } else {
        // Preserve existing MRN
        const mrnIdentifier = existingIdentifiers.find((id)=>id.type?.coding?.[0]?.code === "MR");
        if (mrnIdentifier) {
            // Backfill legacy EMR data (stored on malformed EI.value) onto MR.system.
            if (!mrnIdentifier.system && legacyEmrFromMalformedEI) {
                newIdentifiers.push({
                    ...mrnIdentifier,
                    system: legacyEmrFromMalformedEI
                });
            } else {
                newIdentifiers.push(mrnIdentifier);
            }
        }
    }
    // Preserve other identifiers (e.g. Mirah patient-id / institute identifiers
    // still attached to legacy patients — historical reads power the patient
    // profile card's Mirah deep-link, even though the form no longer writes them).
    // Drop legacy EI (Employer Identifier) entries — the EMR system is now stored
    // on the MR identifier's `system` field, so standalone EI identifiers are defunct.
    existingIdentifiers.forEach((id)=>{
        const identifierTypeCode = id.type?.coding?.[0]?.code;
        const isMRN = identifierTypeCode === "MR";
        const isLegacyEMR = identifierTypeCode === "EI";
        if (!isMRN && !isLegacyEMR) {
            newIdentifiers.push(id);
        }
    });
    updatedPatient.identifier = newIdentifiers.length > 0 ? newIdentifiers : undefined;
    // Handle generalPractitioner updates (both PCP and primary advocate)
    const shouldUpdatePCP = formData["primary-care-provider"] !== undefined;
    const shouldUpdateLCSW = formData["benchmark-lcsw"] !== undefined;
    if (shouldUpdatePCP || shouldUpdateLCSW) {
        // Start with existing entries we want to preserve
        const existingEntries = updatedPatient.generalPractitioner || [];
        const newGeneralPractitioners = [];
        // Handle PCP
        if (shouldUpdatePCP) {
            if (formData["primary-care-provider"]) {
                newGeneralPractitioners.push({
                    reference: `PractitionerRole/${formData["primary-care-provider"]}`,
                    display: formData["primary-care-provider-display"] || "Practitioner",
                    extension: [
                        {
                            url: GP_ROLE_TYPE_EXTENSION,
                            valueCode: GP_ROLE_TYPES.REFERRING_PROVIDER
                        }
                    ]
                });
            }
        // If PCP is cleared (empty string), we don't add it - array will be empty or just LCSW
        } else {
            // Preserve existing PCP if not being updated
            const existingPCP = existingEntries.find((ref)=>ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION && ext.valueCode === GP_ROLE_TYPES.REFERRING_PROVIDER));
            if (existingPCP) {
                newGeneralPractitioners.push(existingPCP);
            }
        }
        // Handle Primary Advocate
        if (shouldUpdateLCSW) {
            if (formData["benchmark-lcsw"]) {
                newGeneralPractitioners.push({
                    reference: `PractitionerRole/${formData["benchmark-lcsw"]}`,
                    display: formData["benchmark-lcsw-display"] || "Primary Advocate",
                    extension: [
                        {
                            url: GP_ROLE_TYPE_EXTENSION,
                            valueCode: GP_ROLE_TYPES.BENCHMARK_PRIMARY_ADVOCATE
                        }
                    ]
                });
            }
        } else {
            // Preserve existing primary advocate if not being updated
            const existingLCSW = existingEntries.find((ref)=>ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION && isBenchmarkPrimaryAdvocateCode(ext.valueCode)));
            if (existingLCSW) {
                newGeneralPractitioners.push(existingLCSW);
            }
        }
        // Also preserve any entries without our extension (legacy data)
        const legacyEntries = existingEntries.filter((ref)=>!ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION));
        // Only preserve legacy if we're not explicitly setting PCP (legacy = PCP)
        if (!shouldUpdatePCP && legacyEntries.length > 0) {
            newGeneralPractitioners.push(...legacyEntries);
        }
        // Set generalPractitioner - use empty array if PCP was cleared and no LCSW
        if (shouldUpdatePCP && !formData["primary-care-provider"] && newGeneralPractitioners.length === 0) {
            // PCP was explicitly cleared - set to empty array
            updatedPatient.generalPractitioner = [];
        } else {
            updatedPatient.generalPractitioner = newGeneralPractitioners.length > 0 ? newGeneralPractitioners : undefined;
        }
        // Handle managingOrganization
        if (shouldUpdatePCP) {
            if (formData["primary-care-provider"] && formData["primary-care-provider-organization-ref"] && formData["primary-care-provider-organization-display"]) {
                // Set managingOrganization when PCP and org info are provided
                updatedPatient.managingOrganization = {
                    reference: formData["primary-care-provider-organization-ref"],
                    display: formData["primary-care-provider-organization-display"]
                };
            } else {
                // Only clear managingOrganization if there was an existing PCP being removed.
                // This prevents incorrectly clearing managingOrganization when:
                // - PCP was never set (form defaults to empty string)
                // - managingOrganization was set independently of PCP
                const hadExistingPCP = existingPatient.generalPractitioner?.some((ref)=>ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION && ext.valueCode === GP_ROLE_TYPES.REFERRING_PROVIDER)) || (existingPatient.generalPractitioner?.length ?? 0) > 0;
                if (hadExistingPCP) {
                    updatedPatient.managingOrganization = undefined;
                }
            // Otherwise preserve existing managingOrganization from initial spread
            }
        }
    }
    // Update extensions (race, ethnicity, sexual orientation)
    const extensions = updatedPatient.extension || [];
    // Race extension
    if (changed("race")) {
        const raceDisplayMapping = {
            "1002-5": "American Indian or Alaska Native",
            "2028-9": "Asian",
            "2054-5": "Black or African American",
            "2076-8": "Native Hawaiian or Other Pacific Islander",
            "2106-3": "White",
            UNK: "Unknown",
            ASKU: "Asked but no answer"
        };
        const raceCode = formData.race;
        const raceExtensionIndex = extensions.findIndex((ext)=>ext.url === "http://hl7.org/fhir/us/core/StructureDefinition/us-core-race");
        if (raceCode) {
            const raceExtension = {
                url: "http://hl7.org/fhir/us/core/StructureDefinition/us-core-race",
                extension: [
                    {
                        url: "ombCategory",
                        valueCoding: {
                            system: "urn:oid:2.16.840.1.113883.6.238",
                            code: raceCode,
                            display: raceDisplayMapping[raceCode] || "Unknown"
                        }
                    }
                ]
            };
            if (raceExtensionIndex === -1) {
                extensions.push(raceExtension);
            } else {
                extensions[raceExtensionIndex] = raceExtension;
            }
        } else if (raceExtensionIndex !== -1) {
            // Remove race extension if cleared
            extensions.splice(raceExtensionIndex, 1);
        }
    }
    // Ethnicity extension
    if (changed("ethnicity")) {
        const ethnicityDisplayMapping = {
            "2135-2": "Hispanic or Latino",
            "2186-5": "Not Hispanic or Latino",
            UNK: "Unknown",
            ASKU: "Asked but no answer"
        };
        const ethnicityCode = formData.ethnicity;
        const ethnicityExtensionIndex = extensions.findIndex((ext)=>ext.url === "http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity");
        if (ethnicityCode) {
            const ethnicityExtension = {
                url: "http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity",
                extension: [
                    {
                        url: "ombCategory",
                        valueCoding: {
                            system: "urn:oid:2.16.840.1.113883.6.238",
                            code: ethnicityCode,
                            display: ethnicityDisplayMapping[ethnicityCode] || "Unknown"
                        }
                    }
                ]
            };
            if (ethnicityExtensionIndex === -1) {
                extensions.push(ethnicityExtension);
            } else {
                extensions[ethnicityExtensionIndex] = ethnicityExtension;
            }
        } else if (ethnicityExtensionIndex !== -1) {
            // Remove ethnicity extension if cleared
            extensions.splice(ethnicityExtensionIndex, 1);
        }
    }
    // Sexual orientation extension
    if (changed("sexual-orientation")) {
        const sexualOrientationExtensionIndex = extensions.findIndex((ext)=>ext.url === "http://hl7.org/fhir/StructureDefinition/patient-sexualOrientation");
        if (formData["sexual-orientation"] && formData["sexual-orientation"].length > 0) {
            const sexualOrientationExtension = {
                url: "http://hl7.org/fhir/StructureDefinition/patient-sexualOrientation",
                valueCodeableConcept: {
                    coding: formData["sexual-orientation"].map((orientation)=>({
                            code: orientation,
                            display: orientation
                        }))
                }
            };
            if (sexualOrientationExtensionIndex === -1) {
                extensions.push(sexualOrientationExtension);
            } else {
                extensions[sexualOrientationExtensionIndex] = sexualOrientationExtension;
            }
        } else if (sexualOrientationExtensionIndex !== -1) {
            // Remove sexual orientation extension if cleared
            extensions.splice(sexualOrientationExtensionIndex, 1);
        }
    }
    if (changed("pronouns")) {
        const pronounsExtensionIndex = extensions.findIndex((ext)=>ext.url === INDIVIDUAL_PRONOUNS_EXTENSION_URL);
        const trimmed = formData.pronouns?.trim() ?? "";
        if (trimmed) {
            const pronounsExtension = buildIndividualPronounsExtension(trimmed);
            if (pronounsExtensionIndex === -1) {
                extensions.push(pronounsExtension);
            } else {
                extensions[pronounsExtensionIndex] = pronounsExtension;
            }
        } else if (pronounsExtensionIndex !== -1) {
            extensions.splice(pronounsExtensionIndex, 1);
        }
    }
    // CoCM consent extension
    if (formData["cocm-consent-verified"] !== undefined || formData["cocm-consent-date"] !== undefined) {
        const cocmExtensionIndex = extensions.findIndex((ext)=>ext.url === COCM_CONSENT_EXTENSION_URL);
        const cocmExtension = buildCoCMConsentExtension({
            verified: formData["cocm-consent-verified"],
            date: formData["cocm-consent-date"]
        });
        if (cocmExtension) {
            if (cocmExtensionIndex === -1) {
                extensions.push(cocmExtension);
            } else {
                extensions[cocmExtensionIndex] = cocmExtension;
            }
        } else if (cocmExtensionIndex !== -1) {
            extensions.splice(cocmExtensionIndex, 1);
        }
    }
    // Patient summary extension
    if (formData.summary !== undefined) {
        const trimmedSummary = formData.summary.trim();
        if (trimmedSummary && authorReference) {
            // Update or add summary extension when we have both content and author
            updatedPatient.extension = updatePatientSummaryExtension(extensions, {
                text: trimmedSummary,
                authorReference,
                generatedBy: "manual"
            });
        } else if (!trimmedSummary && authorReference) {
            // Remove summary extension if cleared (empty or whitespace only) AND we have authorReference
            // Only remove if we can safely update (have authorReference)
            updatedPatient.extension = removePatientSummaryExtension(extensions);
        } else {
            // authorReference is missing
            if (trimmedSummary) {
                // Summary provided but no authorReference - this should not happen in normal UI flow
                // Log warning to catch any code paths that don't pass authorReference
                console.warn("Patient summary update was provided but authorReference is missing. Summary will not be updated.", {
                    summaryLength: trimmedSummary.length
                });
            }
            // Preserve existing extension regardless of summary value
            // This prevents data loss when updatePatient.action.ts calls without authorReference
            // The form always initializes summary to "", so we can't distinguish "cleared" from "not touched"
            if (extensions.length > 0) {
                updatedPatient.extension = extensions;
            } else {
                updatedPatient.extension = undefined;
            }
        }
    } else {
        // Summary not in form data - preserve existing extensions
        if (extensions.length > 0) {
            updatedPatient.extension = extensions;
        } else {
            updatedPatient.extension = undefined;
        }
    }
    // Update veteran status in tags
    if (formData["veteran-status"] !== undefined) {
        const tags = updatedPatient.meta?.tag || [];
        const veteranTagIndex = tags.findIndex((tag)=>tag.system === "http://terminology.hl7.org/CodeSystem/v3-VeteranMilitaryStatus");
        if (formData["veteran-status"]) {
            const veteranTag = {
                system: "http://terminology.hl7.org/CodeSystem/v3-VeteranMilitaryStatus",
                code: "veteran",
                display: "Veteran"
            };
            if (veteranTagIndex === -1) {
                tags.push(veteranTag);
            } else {
                tags[veteranTagIndex] = veteranTag;
            }
        } else if (veteranTagIndex !== -1) {
            // Remove veteran tag if status is false
            tags.splice(veteranTagIndex, 1);
        }
        updatedPatient.meta = {
            ...updatedPatient.meta,
            tag: tags
        };
    }
    // Update engagement status tag
    if (formData["patient-engagement-status"] !== undefined) {
        const existingTags = updatedPatient.meta?.tag || [];
        // Filter out ALL engagement status tags before adding the new one.
        // The engagement modal writes V2-system tags while this form writes V1,
        // so filtering only the V1 system would leave a stale V2 tag behind and
        // accumulate multiple engagement tags. That breaks the member list filter,
        // which matches a patient if ANY tag matches the requested status.
        const filteredTags = existingTags.filter((tag)=>tag.system !== LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM && tag.system !== PATIENT_ENGAGEMENT_STATUS_SYSTEM);
        const code = formData["patient-engagement-status"] || "";
        const option = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === code);
        const newTags = option ? [
            ...filteredTags,
            {
                system: PATIENT_TAG_MAPPINGS.engagementStatus.system,
                code: option.code,
                display: option.display
            }
        ] : filteredTags;
        updatedPatient.meta = {
            ...updatedPatient.meta || {},
            tag: newTags
        };
    }
    // The edit form submits contact phones as E.164 while stored ones may use
    // any format, so compare on normalized phones.
    const comparableContacts = (data)=>data["patient-contacts"]?.map((contact)=>({
                ...contact,
                phone: toE164US$1(contact.phone ?? "")
            }));
    // ponytail: editing any contact rebuilds every contact from the form, so
    // the untouched ones still lose email/address/gender. Upgrade: per-row merge.
    if (formData["patient-contacts"] !== undefined && !deepEquals(comparableContacts(formData), comparableContacts(loaded))) {
        if (formData["patient-contacts"] && formData["patient-contacts"].length > 0) {
            updatedPatient.contact = patientContactsToFhir(formData["patient-contacts"]);
        } else {
            // Clear contacts if none provided
            updatedPatient.contact = [];
        }
    }
    return updatedPatient;
}

/**
 * Converts form data to Practitioner and PractitionerRole resources.
 * @param formData - Practitioner form data
 * @param existingPractitioner - Existing practitioner resource (for updates)
 * @param tempPractitionerFullUrl - Temporary full URL for transaction bundles
 * @returns Build result with practitioner and practitionerRole resources
 * @agentic @benchmark/core
 */ function practitionerFromForm(formData, existingPractitioner, tempPractitionerFullUrl) {
    // Find the selected specialty
    const selectedSpecialty = PRACTITIONER_SPECIALTY_OPTIONS.find((specialty)=>specialty.code === formData["practitioner-specialty"]);
    if (!selectedSpecialty) {
        throw new Error("Invalid specialty selected");
    }
    // Build telecom array
    const telecom = [];
    if (formData["practitioner-phone"]) {
        telecom.push({
            system: "phone",
            value: formData["practitioner-phone"],
            use: "work"
        });
    }
    if (formData["practitioner-email"]) {
        telecom.push({
            system: "email",
            value: formData["practitioner-email"],
            use: "work"
        });
    }
    // Build Practitioner resource
    const practitioner = existingPractitioner ? {
        ...existingPractitioner
    } : {
        resourceType: "Practitioner",
        active: true,
        name: [
            {
                given: [
                    formData["practitioner-first-name"]
                ],
                family: formData["practitioner-last-name"]
            }
        ]
    };
    // Update name if editing
    if (existingPractitioner) {
        practitioner.name = [
            {
                given: [
                    formData["practitioner-first-name"]
                ],
                family: formData["practitioner-last-name"]
            }
        ];
    }
    // Add telecom if provided
    if (telecom.length > 0) {
        practitioner.telecom = telecom;
    }
    // Build PractitionerRole resource
    // Use tempPractitionerFullUrl if provided (for transaction bundles),
    // otherwise use existing practitioner ID, or placeholder
    const displayName = getPractitionerDisplayName(practitioner);
    const practitionerReference = tempPractitionerFullUrl ? {
        reference: tempPractitionerFullUrl,
        display: displayName
    } : existingPractitioner?.id ? buildPractitionerReference(toPractitionerId(existingPractitioner.id), displayName) : {
        reference: "Practitioner/placeholder",
        display: displayName
    };
    const practitionerRole = {
        resourceType: "PractitionerRole",
        practitioner: practitionerReference,
        active: true,
        specialty: [
            {
                coding: [
                    {
                        system: selectedSpecialty.system,
                        code: selectedSpecialty.code,
                        display: selectedSpecialty.display
                    }
                ]
            }
        ]
    };
    // Add telecom to PractitionerRole if provided
    if (telecom.length > 0) {
        practitionerRole.telecom = telecom;
    }
    return {
        practitioner,
        practitionerRole
    };
}

/**
 * Maps Practitioner domain model to PractitionerFormData. Pure transformation, no IO.
 * @agentic @benchmark/core
 */ /**
 * Converts FHIR Practitioner resource to form data. Pure mapping, no IO.
 * @param practitioner - Practitioner resource
 * @param practitionerRole - Optional PractitionerRole resource for specialty
 * @returns Practitioner form data
 * @agentic @benchmark/core
 */ function practitionerToForm(practitioner, practitionerRole) {
    const formData = {
        "practitioner-first-name": "",
        "practitioner-last-name": "",
        "practitioner-specialty": ""
    };
    // Extract name information
    if (practitioner.name?.[0]) {
        const name = practitioner.name[0];
        if (name.given?.[0]) {
            formData["practitioner-first-name"] = name.given[0];
        }
        if (name.family) {
            formData["practitioner-last-name"] = name.family;
        }
    }
    // Extract telecom information
    const email = practitioner.telecom?.find((t)=>t.system === "email")?.value;
    const phone = practitioner.telecom?.find((t)=>t.system === "phone")?.value;
    if (email) {
        formData["practitioner-email"] = email;
    }
    if (phone) {
        formData["practitioner-phone"] = phone;
    }
    // Extract specialty from PractitionerRole
    if (practitionerRole?.specialty?.[0]?.coding?.[0]) {
        formData["practitioner-specialty"] = practitionerRole.specialty[0].coding[0].code || "";
    }
    return formData;
}

/**
 * Transforms QuestionnaireFormData to Questionnaire resource. Pure, no IO.
 * @agentic @benchmark/core
 */ /**
 * Converts form data to FHIR Questionnaire resource.
 * @param formData - Questionnaire form data
 * @param existingQuestionnaire - Optional existing questionnaire for updates
 * @returns Questionnaire resource ready for creation/update
 * @agentic @benchmark/core
 */ function questionnaireFromForm(formData, existingQuestionnaire) {
    // Start with existing questionnaire or create new one
    const questionnaire = existingQuestionnaire ? {
        ...existingQuestionnaire
    } : {
        resourceType: "Questionnaire",
        status: formData.status || "draft"
    };
    // Set id if updating
    if (existingQuestionnaire?.id) {
        questionnaire.id = existingQuestionnaire.id;
    }
    // Basic identification
    if (formData.url !== undefined) {
        questionnaire.url = formData.url;
    }
    if (formData.identifier !== undefined) {
        questionnaire.identifier = formData.identifier;
    }
    if (formData.version !== undefined) {
        questionnaire.version = formData.version;
    }
    if (formData.name !== undefined) {
        questionnaire.name = formData.name;
    }
    if (formData.title !== undefined) {
        questionnaire.title = formData.title;
    }
    if (formData.derivedFrom !== undefined) {
        questionnaire.derivedFrom = formData.derivedFrom;
    }
    if (formData.status !== undefined) {
        questionnaire.status = formData.status;
    }
    // Workflow status
    if (formData.experimental !== undefined) {
        questionnaire.experimental = formData.experimental;
    }
    if (formData.subjectType !== undefined) {
        questionnaire.subjectType = formData.subjectType;
    }
    // Publication info
    if (formData.date !== undefined) {
        questionnaire.date = formData.date;
    }
    if (formData.publisher !== undefined) {
        questionnaire.publisher = formData.publisher;
    }
    if (formData.contact !== undefined) {
        questionnaire.contact = formData.contact;
    }
    if (formData.description !== undefined) {
        questionnaire.description = formData.description;
    }
    if (formData.useContext !== undefined) {
        questionnaire.useContext = formData.useContext;
    }
    if (formData.jurisdiction !== undefined) {
        questionnaire.jurisdiction = formData.jurisdiction;
    }
    if (formData.purpose !== undefined) {
        questionnaire.purpose = formData.purpose;
    }
    if (formData.copyright !== undefined) {
        questionnaire.copyright = formData.copyright;
    }
    if (formData.approvalDate !== undefined) {
        questionnaire.approvalDate = formData.approvalDate;
    }
    if (formData.lastReviewDate !== undefined) {
        questionnaire.lastReviewDate = formData.lastReviewDate;
    }
    if (formData.effectivePeriod !== undefined) {
        questionnaire.effectivePeriod = formData.effectivePeriod;
    }
    // Content
    if (formData.code !== undefined) {
        questionnaire.code = formData.code;
    }
    if (formData.item !== undefined) {
        questionnaire.item = formData.item;
    }
    return questionnaire;
}

/**
 * Converts form data to FHIR QuestionnaireResponse resource.
 * @param formData - QuestionnaireResponse form data
 * @param patient - Patient resource (for building references)
 * @param practitioner - Practitioner resource (for building references)
 * @param practitionerRole - PractitionerRole resource (for building references)
 * @param encounter - Optional encounter resource (for building references)
 * @param existingResponse - Optional existing questionnaire response for updates
 * @param tempEncounterFullUrl - Optional temporary full URL for encounter in transactions
 * @returns QuestionnaireResponse resource ready for creation/update
 * @agentic @benchmark/core
 */ function questionnaireResponseFromForm(formData, patient, practitioner, practitionerRole, encounter, existingResponse, tempEncounterFullUrl) {
    let questionnaireResponse;
    if (existingResponse) {
        questionnaireResponse = {
            ...existingResponse
        };
    } else {
        questionnaireResponse = {
            resourceType: "QuestionnaireResponse",
            status: "in-progress",
            authored: new Date().toISOString()
        };
    }
    // Always (re)derive the deterministic actor references. On updates the
    // encounter bundle builder passes only a stub { resourceType, id } as
    // existingResponse, so a PUT built from it would otherwise drop
    // subject/author/source and wipe the patient/clinician linkage (which also
    // removes the resource from `patient`-scoped searches). `??=` preserves a
    // real existing value while populating a stub's missing one — mirroring how
    // the `encounter` reference below is always set.
    questionnaireResponse.subject ??= buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient));
    questionnaireResponse.author ??= buildPractitionerRoleReference(practitionerRole.id || "", getPractitionerDisplayName(practitioner));
    questionnaireResponse.source ??= buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient));
    // Update fields from form data
    // Note: Form data identifier type doesn't match FHIR exactly, but utils builder assigns directly
    // Match that behavior - the form data structure is compatible
    if (formData.identifier !== undefined) {
        questionnaireResponse.identifier = formData.identifier;
    }
    if (formData.basedOn !== undefined) {
        questionnaireResponse.basedOn = formData.basedOn;
    }
    if (formData.partOf !== undefined) {
        questionnaireResponse.partOf = formData.partOf;
    }
    if (formData.questionnaire !== undefined) {
        questionnaireResponse.questionnaire = formData.questionnaire;
    }
    if (formData.status !== undefined) {
        questionnaireResponse.status = formData.status;
    }
    if (formData.subject !== undefined) {
        questionnaireResponse.subject = formData.subject;
    }
    if (formData.encounter !== undefined) {
        questionnaireResponse.encounter = formData.encounter;
    }
    if (formData.authored !== undefined) {
        questionnaireResponse.authored = formData.authored;
    }
    if (formData.author !== undefined) {
        questionnaireResponse.author = formData.author;
    }
    if (formData.source !== undefined) {
        questionnaireResponse.source = formData.source;
    }
    // Encounter reference if provided (takes precedence over form data)
    if (encounter) {
        if (tempEncounterFullUrl) {
            // New encounter: the reference is a transaction-local placeholder that
            // Medplum rewrites to the real Encounter id on submit. Do NOT derive a
            // display from the placeholder uuid — Medplum rewrites the reference but
            // not the display, leaving a display whose id mismatches the resolved
            // reference. Omit the display; it carries no real id at build time.
            questionnaireResponse.encounter = {
                reference: tempEncounterFullUrl
            };
        } else if (encounter.id) {
            questionnaireResponse.encounter = buildReferenceWithDisplay("Encounter", encounter.id, `Encounter ${encounter.id}`);
        }
    }
    // Content
    if (formData.item !== undefined) {
        questionnaireResponse.item = formData.item;
    }
    return questionnaireResponse;
}

/**
 * Converts form-captured service needs into enriched `ServiceRequest`
 * inputs (display labels resolved against shared option/mode value-sets,
 * preferences forwarded as-is).
 *
 * Phase 5d moved this from
 * `apps/connections/src/orchestration/adapters/mapServiceNeedsToServiceRequestInputs.ts`
 * to `@benchmark/core/adapters/`. Renamed to follow the
 * `<entity>.toBundleInput` adapter naming convention used by sibling
 * adapters like `encounter.toBundleInput`.
 * @agentic @benchmark/core
 */ const mapServiceNeedsToServiceRequestInputs = (needs)=>needs.map((need)=>{
        const serviceTypeOption = SERVICE_TYPE_OPTIONS.find((option)=>option.value === need.serviceType);
        const modeDisplays = need.modes.map((modeCode)=>{
            const sharedLabel = getReferralModeLabel(modeCode);
            if (sharedLabel !== modeCode) {
                return sharedLabel;
            }
            const modeOption = SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS.find((option)=>option.value === modeCode);
            return modeOption?.label || modeCode;
        }).filter(Boolean);
        const conditionInputs = need.conditions.map((conditionCode)=>{
            const conditionOption = SERVICE_SPECIALTY_TREATMENT_CONDITION_OPTIONS.find((option)=>option.value === conditionCode);
            return conditionOption ? {
                code: conditionCode,
                display: conditionOption.label
            } : {
                code: conditionCode,
                display: conditionCode
            };
        }).filter((condition)=>condition.code);
        return {
            code: need.serviceType,
            display: serviceTypeOption?.label || need.serviceType,
            modalities: need.modes.length > 0 ? need.modes : undefined,
            modalityDisplays: modeDisplays.length > 0 ? modeDisplays : undefined,
            conditions: conditionInputs.length > 0 ? conditionInputs : undefined,
            preferences: need.preferences || undefined
        };
    });

/** Lookup table for canonical activity order by v2 activity code. */ new Map(getAllCarePlanActivityOptions().map((activity)=>[
        activity.code,
        activity.order
    ]));
/**
 * The exact top-level clause labels that completion flows emit (single source of
 * truth for both the clause split and keyed detection below). A clause is only
 * `keyed`, and the top-level split only fires, when a clause begins with one of
 * these labels followed by `": "`. This is what keeps free text intact: a note
 * such as `Notes: Discussed options. Recommendation: PHP` has no allow-listed
 * label after its internal `". "`, so it is not split, and a standalone
 * `Summary: …` note is not mistaken for a keyed segment.
 */ const STATUS_REASON_LABELS = [
    "Selected services",
    "Services",
    "Notes",
    "Approved",
    "Denied",
    "Needs more information",
    "Outcome",
    "Follow-up",
    "Member accepted",
    "Member declined",
    "Member wants changes",
    "Appointments scheduled",
    "CoCM care plan built with activities"
];
new Set(STATUS_REASON_LABELS);
/**
 * Splits a status-reason string into top-level clauses. Completion flows join
 * clauses with `". "` and every non-leading clause begins with one of
 * {@link STATUS_REASON_LABELS} + `": "`. Breaking only before an allow-listed
 * label keeps sentence-ending periods inside a clause value from fragmenting it
 * — e.g. the `Dr. Jane Doe` in a consult string, or a `". Recommendation: "`
 * inside a free-text note, stays within its clause.
 *
 * Built from the constant allow-list above (labels are escaped); a bounded
 * alternation of literals with a single lookahead — no nested quantifiers, so it
 * stays linear on adversarial input (CodeQL `js/polynomial-redos`).
 */ new RegExp(`\\. +(?=(?:${STATUS_REASON_LABELS.map((label)=>label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")}): )`);
/**
 * Extracts the canonical v2 activity code from a Task's code.coding, filtering
 * by the care-plan activity system and normalizing legacy codes.
 * @agentic @benchmark/core
 */ const resolveTaskActivityCode = (task)=>{
    const rawCode = task.code?.coding?.find((c)=>c.system === CARE_PLAN_ACTIVITY_SYSTEM)?.code;
    if (!rawCode) return undefined;
    return resolveActivityCode(rawCode);
};
/**
 * True when at least one Task in the list has activity code "offer-services"
 * and status "completed". Resource-agnostic — any caller passing Tasks for
 * any CarePlan can use this.
 * @agentic @benchmark/core
 */ const hasCompletedOfferServicesTask = (tasks)=>tasks.some((t)=>resolveTaskActivityCode(t) === "offer-services" && t.status === "completed");
/**
 * Returns the first ready/in-progress Task as `{ code, display, owner, dueDate }`.
 * Resource-agnostic (no triage assumption).
 * @agentic @benchmark/core
 */ const resolveNextReadyTaskActivity = (tasks)=>{
    const readyTask = tasks.find((t)=>t.status === "ready" || t.status === "in-progress");
    if (!readyTask) return undefined;
    const code = resolveTaskActivityCode(readyTask) ?? "unknown";
    return {
        code,
        display: getCarePlanActivityDisplay(code),
        owner: readyTask.owner?.display,
        dueDate: readyTask.restriction?.period?.end
    };
};

/**
 * Computes days since ServiceRequest creation or since offer-services was
 * completed. When services have been offered, counts days since the
 * offer-services Task completion. Otherwise counts days since the earliest
 * ServiceRequest authoredOn (falling back to CarePlan dates).
 * @agentic @benchmark/core
 */ const resolveServiceRequestDaysWaiting = (carePlan, tasks, serviceRequestDate, now = new Date())=>{
    const offered = hasCompletedOfferServicesTask(tasks);
    if (offered) {
        const offerTask = tasks.find((t)=>resolveTaskActivityCode(t) === "offer-services" && t.status === "completed");
        const completedDate = offerTask?.meta?.lastUpdated;
        if (completedDate) {
            return daysBetween(new Date(completedDate), now);
        }
    }
    const createdDate = serviceRequestDate ?? carePlan.period?.start ?? carePlan.meta?.lastUpdated;
    if (!createdDate) return 0;
    return daysBetween(new Date(createdDate), now);
};
/** Whole-day difference between two dates. */ const daysBetween = (from, to)=>{
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.max(0, Math.floor((to.getTime() - from.getTime()) / msPerDay));
};

/**
 * Transforms FHIR resources into `ServiceRequestFulfillmentRow[]`. Each
 * active triage `CarePlan` becomes one row per unique patient + carePlan
 * combination. Excludes patients with terminating engagement statuses and
 * plans with no actionable tasks.
 * @agentic @benchmark/core
 */ const serviceRequestsToFulfillmentRows = (input)=>{
    const { carePlans, tasks, patients, serviceRequests, lastContacted } = input;
    const tasksByCarePlan = groupTasksByCarePlan(tasks);
    const rows = [];
    for (const carePlan of carePlans){
        const carePlanId = carePlan.id ?? "";
        const patientRef = carePlan.subject?.reference ?? "";
        const patientId = patientRef.replace("Patient/", "");
        const patient = patients.get(patientId);
        if (patient && isTerminatedPatient(patient)) continue;
        const allPlanTasks = tasksByCarePlan.get(`CarePlan/${carePlanId}`) ?? [];
        const planTasks = allPlanTasks.filter((t)=>{
            const code = resolveTaskActivityCode(t);
            return code !== undefined && TRIAGE_ACTIVITY_CODES.has(code);
        });
        const hasActionableTasks = planTasks.some((t)=>t.status === "ready" || t.status === "draft" || t.status === "in-progress");
        if (!hasActionableTasks) continue;
        const srs = serviceRequests.get(carePlanId) ?? [];
        const earliestSrDate = srs.map((sr)=>sr.authoredOn ?? sr.meta?.lastUpdated).filter((d)=>Boolean(d)).sort()[0];
        const nextActivity = resolveNextReadyTaskActivity(planTasks);
        const taskOwnerRef = nextActivity ? planTasks.find((t)=>t.status === "ready" || t.status === "in-progress")?.owner : undefined;
        const serviceTypeStrings = srs.flatMap((sr)=>sr.code?.coding ?? []).filter((c)=>c.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_TYPE).map((c)=>c.display ?? c.code ?? "").filter(Boolean);
        rows.push({
            id: carePlanId,
            patientId,
            patientName: resolvePatientName(patient),
            advocateId: taskOwnerRef?.reference?.replace("PractitionerRole/", ""),
            advocateName: taskOwnerRef?.display,
            daysWaiting: resolveServiceRequestDaysWaiting(carePlan, planTasks, earliestSrDate),
            servicesOffered: hasCompletedOfferServicesTask(planTasks),
            nextActivity,
            lastContacted: lastContacted.get(patientId),
            serviceType: serviceTypeStrings.length > 0 ? serviceTypeStrings.join(", ") : undefined,
            carePlanId,
            tasks: planTasks
        });
    }
    return rows;
};
/**
 * Computes aggregate fulfillment statistics from row data (unique by
 * patient).
 * @agentic @benchmark/core
 */ const aggregateFulfillmentStats = (rows)=>{
    const byPatient = new Map();
    for (const row of rows){
        if (!byPatient.has(row.patientId)) {
            byPatient.set(row.patientId, row);
        }
    }
    let backlog = 0;
    let assigned = 0;
    let unassigned = 0;
    for (const row of byPatient.values()){
        if (!row.servicesOffered) backlog++;
        if (row.advocateId) assigned++;
        else unassigned++;
    }
    return {
        currentBacklog: backlog,
        totalBeingTriaged: byPatient.size,
        membersAssigned: assigned,
        membersUnassigned: unassigned
    };
};
/** Groups tasks by their `basedOn` `CarePlan` reference. */ const groupTasksByCarePlan = (tasks)=>{
    const map = new Map();
    for (const task of tasks){
        const ref = task.basedOn?.[0]?.reference;
        if (ref) {
            const existing = map.get(ref) ?? [];
            existing.push(task);
            map.set(ref, existing);
        }
    }
    return map;
};
/** Resolves a display name from a `Patient` resource. */ const resolvePatientName = (patient)=>{
    if (!patient?.name?.[0]) return "Unknown";
    return formatHumanName(patient.name[0]) || patient.id || "Unknown";
};
/** Returns true if the patient has a terminating engagement status. */ const isTerminatedPatient = (patient)=>{
    const status = getPatientEngagementStatus(patient);
    if (!status) return false;
    return TERMINATING_ENGAGEMENT_STATUSES.includes(status.code);
};

/**
 * Converts form data to FHIR Task resource. Pure mapping, no IO.
 * When existingTask is provided, preserves all fields not managed by the form
 * (basedOn, meta, input, output, focus, statusReason, etc.) to avoid
 * destructive updates via full PUT.
 * @agentic @benchmark/core
 */ function taskFromForm(formData, patient, existingTask) {
    const taskTypeConfig = formData["task-type"] ? getTaskTypeConfig(formData["task-type"]) : undefined;
    let ownerReference;
    if (formData["assigned-to"]) {
        ownerReference = formData["assigned-to-display"] ? buildReferenceWithDisplay("PractitionerRole", formData["assigned-to"], formData["assigned-to-display"]) : {
            reference: `PractitionerRole/${formData["assigned-to"]}`
        };
    }
    const requesterReference = formData.requester ? formData["requester-display"] ? buildReferenceWithDisplay("PractitionerRole", formData.requester, formData["requester-display"]) : {
        reference: `PractitionerRole/${formData.requester}`
    } : undefined;
    const isCustomTask = formData["task-type"] === CUSTOM_TASK_TYPE.code;
    const customCoding = isCustomTask && formData["custom-task-display"] ? [
        {
            system: CUSTOM_TASK_TYPE.system,
            code: "custom-task",
            display: formData["custom-task-display"]
        }
    ] : undefined;
    const v2ActivityCode = !isCustomTask && formData["task-type"] && isV2CarePlanActivityTaskCode(formData["task-type"]) ? formData["task-type"] : undefined;
    const existingV2Coding = existingTask?.code?.coding?.[0];
    const v2PlanActivityCoding = v2ActivityCode ? [
        {
            system: CARE_PLAN_ACTIVITY_SYSTEM,
            code: v2ActivityCode,
            display: existingV2Coding?.code === v2ActivityCode && existingV2Coding.display ? existingV2Coding.display : resolveActivityDisplay(v2ActivityCode)
        }
    ] : undefined;
    const formCode = taskTypeConfig || customCoding || v2PlanActivityCoding ? {
        coding: customCoding || v2PlanActivityCoding || [
            {
                system: taskTypeConfig.system,
                code: taskTypeConfig.code,
                display: taskTypeConfig.display
            }
        ],
        text: existingTask?.code?.text
    } : existingTask?.code;
    const extensions = [];
    const estimatedDuration = formData["estimated-duration"] || (taskTypeConfig ? getEstimatedDurationForTaskType(taskTypeConfig.code) : undefined);
    if (estimatedDuration) {
        extensions.push({
            url: "http://www.benchmarkhealth.com/fhir/StructureDefinition/estimated-duration",
            valueInteger: estimatedDuration
        });
    }
    if (formData.instructions) {
        extensions.push({
            url: "http://www.benchmarkhealth.com/fhir/StructureDefinition/task-instructions",
            valueString: formData.instructions
        });
    }
    const formNote = formData.notes && formData.requester && formData["requester-display"] ? [
        {
            text: formData.notes,
            time: new Date().toISOString(),
            authorReference: buildReferenceWithDisplay("PractitionerRole", formData.requester, formData["requester-display"])
        }
    ] : [];
    const formFields = {
        code: formCode,
        description: formData.description || taskTypeConfig?.description,
        status: formData.status || "requested",
        priority: formData.priority || taskTypeConfig?.defaultPriority || "routine",
        intent: formData.intent || "order",
        for: buildReferenceWithDisplay("Patient", patient.id, getPatientDisplayName(patient)),
        owner: ownerReference,
        ...requesterReference ? {
            requester: requesterReference
        } : {},
        authoredOn: formData["authored-on"] || new Date().toISOString(),
        restriction: formData["due-date"] ? {
            period: {
                end: formData["due-date"]
            }
        } : undefined,
        extension: extensions.length > 0 ? extensions : undefined,
        businessStatus: formData["business-status"] ? {
            text: formData["business-status"]
        } : undefined,
        note: formNote.length > 0 ? formNote : undefined
    };
    const status = formFields.status || existingTask?.status || "requested";
    const intent = formFields.intent || existingTask?.intent || "order";
    const executionPeriod = status === "completed" ? {
        ...existingTask?.executionPeriod,
        end: new Date().toISOString()
    } : existingTask?.executionPeriod;
    if (existingTask) {
        return {
            ...existingTask,
            ...formFields,
            resourceType: "Task",
            status,
            intent,
            lastModified: new Date().toISOString(),
            ...executionPeriod && {
                executionPeriod
            }
        };
    }
    return {
        resourceType: "Task",
        ...formFields,
        status,
        intent,
        ...executionPeriod && {
            executionPeriod
        }
    };
}

/**
 * Pre-fills form data for new task from task type config.
 * @param taskTypeCode - Task type code
 * @returns Partial TaskFormData with pre-filled values
 * @agentic @benchmark/core
 */ const getPrefilledTaskFormData = (taskTypeCode)=>{
    const taskTypeConfig = getTaskTypeConfig(taskTypeCode);
    if (!taskTypeConfig) return {};
    const formData = {
        "task-type": taskTypeCode,
        priority: taskTypeConfig.defaultPriority,
        "estimated-duration": taskTypeConfig.estimatedDuration,
        instructions: taskTypeConfig.instructions,
        intent: "order",
        status: "requested"
    };
    return formData;
};
/**
 * Converts FHIR Task resource to form data for editing.
 * @param task - Task resource to convert
 * @returns TaskFormData ready for form editing
 * @agentic @benchmark/core
 */ const taskToForm = (task)=>{
    const formData = {};
    // Extract task type from code
    const taskCoding = task.code?.coding?.[0];
    const configuredTaskType = taskCoding?.code ? getTaskTypeConfig(taskCoding.code) : undefined;
    if (configuredTaskType) {
        formData["task-type"] = configuredTaskType.code;
    } else if (taskCoding?.code && isV2CarePlanActivityTaskCode(taskCoding.code)) {
        // Persisted tasks may use CARE_PLAN_ACTIVITY_SYSTEM, ActivityDefinition type
        // system, or legacy/variant URLs on coding.system — the activity code is canonical.
        formData["task-type"] = taskCoding.code;
    } else if (taskCoding?.code) {
        formData["task-type"] = CUSTOM_TASK_TYPE.code;
        if (taskCoding.display) {
            formData["custom-task-display"] = taskCoding.display;
        }
    }
    // Patient reference (who the task is for)
    if (task.for?.reference) {
        formData["patient-id"] = task.for.reference.replace("Patient/", "");
        if (task.for.display) {
            formData["patient-id-display"] = task.for.display;
        }
    }
    // Basic task information
    formData.description = task.description || "";
    formData.status = task.status;
    formData.priority = task.priority;
    formData.intent = task.intent;
    // Assignment and ownership
    if (task.owner?.reference) {
        formData["assigned-to"] = task.owner.reference.replace("PractitionerRole/", "");
        if (task.owner.display) {
            formData["assigned-to-display"] = task.owner.display;
        }
    }
    if (task.requester?.reference) {
        formData["requester"] = task.requester.reference.replace("PractitionerRole/", "");
        if (task.requester.display) {
            formData["requester-display"] = task.requester.display;
        }
    }
    // Timing
    formData["authored-on"] = task.authoredOn || "";
    formData["due-date"] = task.restriction?.period?.end || "";
    // Extract estimated duration from extension
    const durationExtension = task.extension?.find((ext)=>ext.url === "http://www.benchmarkhealth.com/fhir/StructureDefinition/estimated-duration");
    if (durationExtension?.valueInteger) {
        formData["estimated-duration"] = durationExtension.valueInteger;
    }
    // Extract instructions from extension
    const instructionsExtension = task.extension?.find((ext)=>ext.url === "http://www.benchmarkhealth.com/fhir/StructureDefinition/task-instructions");
    if (instructionsExtension?.valueString) {
        formData.instructions = instructionsExtension.valueString;
    }
    // Additional information
    if (task.businessStatus) {
        formData["business-status"] = task.businessStatus.text || task.businessStatus.coding?.[0]?.display || "";
    }
    // Combine notes from task.note array
    if (task.note && task.note.length > 0) {
        formData.notes = task.note.map((note)=>note.text).join("\n\n");
    }
    return formData;
};

/** Creation-only referral evidence, retained with the patient. @agentic @benchmark/core */ const ORIGINAL_REFERRAL_EXTENSION_URL = `${EXTENSION_BASE}/original-pcp-referral`;
const REFERRER_DISPLAY_ERROR = "Select a primary care provider with a recorded name.";
function referrerDisplay(values) {
    const id = values["primary-care-provider"];
    const display = values["primary-care-provider-display"]?.trim();
    return display && display.toLowerCase() !== "unknown" && display !== id && !/^(?:Practitioner|PractitionerRole)\//.test(display) ? display : undefined;
}
/** Validate referral inputs and supplied referrer names before any write. @agentic @benchmark/core */ function validatePatientReferral(values, today = localCalendarDateTodayISO()) {
    const errors = {};
    if (values["primary-care-provider"] && !referrerDisplay(values)) {
        errors["primary-care-provider"] = REFERRER_DISPLAY_ERROR;
    }
    const date = values["cocm-consent-date"];
    if (values["cocm-consent-verified"] === true) {
        const parsed = date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(`${date}T00:00:00Z`) : undefined;
        if (!date || !parsed || !Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date || date > today) {
            errors["cocm-consent-date"] = "Enter a consent date of today or earlier.";
        }
    }
    const recommendation = values["pcp-recommendation"];
    if (recommendation && ![
        "refer-in",
        "refer-out",
        "defer-to-advocate"
    ].includes(recommendation)) {
        errors["pcp-recommendation"] = "Select a PCP recommendation from the list.";
    }
    if ((values["pcp-additional-recommendations"]?.length ?? 0) > 4000) {
        errors["pcp-additional-recommendations"] = "Enter no more than 4,000 characters.";
    }
    return errors;
}
/** Capture once; later patient adapters preserve this extension untouched. @agentic @benchmark/core */ function buildOriginalReferralExtension(values, author, recordedAt) {
    const display = referrerDisplay(values);
    if (values["primary-care-provider"] && !display) throw new Error(REFERRER_DISPLAY_ERROR);
    const extension = [
        {
            url: "recorded-at",
            valueInstant: recordedAt
        }
    ];
    const add = (entry)=>extension.push(entry);
    if (author) add({
        url: "author",
        valueReference: author
    });
    if (values["pcp-recommendation"]) add({
        url: "recommendation",
        valueCode: values["pcp-recommendation"]
    });
    if (values["pcp-high-risk"] !== undefined) add({
        url: "high-risk",
        valueBoolean: values["pcp-high-risk"]
    });
    if (values["cocm-consent-verified"] !== undefined) add({
        url: "consent",
        valueBoolean: values["cocm-consent-verified"]
    });
    if (values["cocm-consent-verified"] && values["cocm-consent-date"]) add({
        url: "consent-date",
        valueDate: values["cocm-consent-date"]
    });
    if (values["pcp-source-reference"]) add({
        url: "source",
        valueString: values["pcp-source-reference"]
    });
    if (values["pcp-additional-recommendations"]) add({
        url: "recommendations",
        valueString: values["pcp-additional-recommendations"]
    });
    if (values["referral-date"]) add({
        url: "referral-date",
        valueDate: values["referral-date"]
    });
    if (values["primary-care-provider"]) add({
        url: "referrer",
        valueReference: {
            reference: `PractitionerRole/${values["primary-care-provider"]}`,
            display
        }
    });
    return {
        url: ORIGINAL_REFERRAL_EXTENSION_URL,
        extension
    };
}
/** Read saved evidence without inventing an entry for a historical patient. @agentic @benchmark/core */ function getOriginalReferral(patient) {
    const fields = patient.extension?.find((e)=>e.url === ORIGINAL_REFERRAL_EXTENSION_URL)?.extension;
    const field = (url)=>fields?.find((e)=>e.url === url);
    const recordedAt = field("recorded-at")?.valueInstant;
    if (!recordedAt) return undefined;
    const recommendation = field("recommendation")?.valueCode;
    return {
        recordedAt,
        recommendation: recommendation === "refer-in" || recommendation === "refer-out" || recommendation === "defer-to-advocate" ? recommendation : undefined,
        highRisk: field("high-risk")?.valueBoolean,
        consent: field("consent")?.valueBoolean,
        consentDate: field("consent-date")?.valueDate,
        source: field("source")?.valueString,
        recommendations: field("recommendations")?.valueString,
        referralDate: field("referral-date")?.valueDate,
        referrer: field("referrer")?.valueReference,
        author: field("author")?.valueReference
    };
}

export { ORIGINAL_REFERRAL_EXTENSION_URL, aggregateFulfillmentStats, appointmentFromForm, appointmentToForm, buildCoCMConsentExtension, buildDraftFormStateRecord, buildHealthcareServiceReference, buildIndividualPronounsExtension, buildLocationReference, buildOrganizationReference, buildOriginalReferralExtension, buildPatientReference, buildPractitionerReference, buildPractitionerRoleReference, buildReferenceWithDisplay, careTimeFromForm, careTimeToForm, clinicalImpressionFromForm, clinicalImpressionToForm, communicationFromForm, communicationToForm, compositionToEncounterFormData, conditionToICD10Code, conditionsToICD10Codes, coverageFromForm, coverageToForm, createAddress, createCodeableConcept, createContactPoint, createHumanName, createIdentifier, createPeriod, encounterFromForm, encounterToForm, extractDraftFormState, extractIndividualPronounsFromPatient, extractPrefillDataFromClinicalIntake, extractReferralMetadata, extractedInfoToForm, extractedOrgToForm, findClinicalImpressionFieldKey, findConditionByICD10Code, flagFromForm, flagToForm, fromBundleInput, fuzzyMatchOption, getLocationDisplayFromForm, getMirahInstanceIdFromPatient, getMirahPatientId, getOrganizationDisplayFromForm, getOriginalReferral, getPrefilledTaskFormData, goalFromForm, goalProgressFromForm, goalProgressToForm, goalToForm, hasReferralMetadata, healthcareServiceFromForm, healthcareServiceToForm, legacyEncounterFromBundleInput, legacyEncounterToBundleInput, libraryFromForm, libraryToForm, locationFromForm, mapServiceNeedsToServiceRequestInputs, observationFromForm, observationToForm, organizationFromForm, partitionPatientContacts, patientContactFromFhir, patientContactsFromFhir, patientContactsToFhir, patientFromForm, patientToForm, patientUpdateFromForm, practitionerFromForm, practitionerToForm, questionnaireFromForm, questionnaireResponseFromForm, serviceRequestsToFulfillmentRows, stripReferralMetadata, taskFromForm, taskToForm, toBundleInput, toE164US, validatePatientReferral, withCoCMConsentExtension };
